import { TOUR_STEPS, type TourPlacement, type TourStep } from '@/lib/tour/steps';

/**
 * 화면별 안내 대본 — 헤더의 <b>?</b> 버튼이 지금 보고 있는 화면에 맞춰 꺼내 쓴다.
 *
 * ## 왜 화면마다 다른가
 *
 * 처음 로그인했을 때 뜨는 안내({@link TOUR_STEPS})는 "어디에 무엇이 있는가" 를 말한다. 그 안내를
 * 어느 화면에서 눌러도 똑같이 틀면, 정작 <b>지금 이 화면을 어떻게 쓰는가</b> 는 아무도 알려주지 않는다.
 * 개선요청으로 가장 많이 들어온 것이 그 물음이었다. 그래서 ? 는 경로를 보고 대본을 고른다.
 *
 * 홈(`/home`)에서 누르면 처음 로그인 때와 <b>똑같은</b> 안내가 나온다 — 홈은 특정 업무 화면이 아니라
 * 서비스의 입구라, 여기서 사람들이 기대하는 것은 전체 안내다.
 *
 * ## 버튼은 낱개가 아니라 묶음으로 밝힌다
 *
 * 한 줄에 선 버튼 넷을 하나씩 설명하면 안내가 네 걸음 늘고, 읽는 사람은 같은 자리에서 네 번 "다음" 을
 * 누른다. 그 줄에 있는 버튼은 대개 <b>한 가지 일의 갈래</b>다("여기서 문서를 올리거나 규정을 봅니다").
 * 그래서 {@link PageGuideSpec.spots} 는 줄 단위로 잡고, 문구도 "이 버튼들로 …" 로 쓴다.
 *
 * 정말 낱개로 짚어야 하는 것(같은 줄인데 뜻이 전혀 다른 버튼)은 `subTargets` 로 그 안에서 한 번 더
 * 반짝이게 한다 — 걸음을 늘리지 않고도 눈이 갈 곳을 정해 준다.
 *
 * ## 순서는 <b>걸음의 차례</b>가 말한다 — 번호를 적지 않는다
 *
 * 기안하기·문서 입력·소모품 불출처럼 <b>1 → 2 → 3</b> 을 밟아야 끝나는 화면이 있다. 그것을 한때는
 * 첫 카드 안에 번호 목록으로 쌓았고, 그다음에는 제목 앞에 ①②③ 을 붙였다. <b>둘 다 없앴다</b>
 * (사용자 지시 2026-08-26).
 *
 * <p>카드가 이미 「안내 3 / 9」 를 달고 있고 걸음은 적어 둔 차례대로 넘어간다. 그 위에 번호를 또
 * 적으면 <b>두 벌의 숫자</b>가 나란히 서고, 권한 때문에 걸음 하나가 빠지는 날 «② 다음이 ④» 가 된다 —
 * 없는 ③ 을 찾게 만드는 것은 안내가 할 일이 아니다. 순서는 {@code spots} 에 적는 <b>차례</b>로만 둔다.
 *
 * ## 대본은 여기 한 곳에만 둔다
 *
 * 화면 컴포넌트마다 안내 문구를 흩어 두면 고칠 때 45곳을 뒤져야 한다. 문구는 개발자가 아닌 사람도
 * 고치는 것이라 한 파일에 모아 둔다(`docs/30_2026-08-07_page-help-guide.md` 가 사람이 읽는 판이다).
 *
 * ## 없는 것은 설명하지 않는다
 *
 * `tabs`·`actions`·`spots` 는 화면에 그 요소가 <b>실제로 있을 때만</b> 나온다. 대상을 `data-tour`
 * 이름으로 찾고 없으면 조용히 건너뛰기 때문이다({@link TourOverlay}) — 권한이 없어 버튼이 안 보이는
 * 사람에게 "오른쪽 위 버튼을 누르세요" 라고 말하는 일이 없다. 반대로 `what`·`tip` 과 대상이 `null` 인
 * 걸음은 어떤 화면에서도 반드시 뜬다.
 */

export type PageGuide = {
  /** 대본을 가리키는 이름. 홈은 {@link HOME_GUIDE_KEY} 다. */
  key: string;
  label: string;
  steps: TourStep[];
};

/**
 * 화면 안의 한 자리를 짚는 걸음.
 *
 * 한 줄에 여러 버튼이 있으면 그 <b>줄</b>을 `target` 으로 잡는다. 낱개로 잡지 않는 이유는 위 주석에.
 */
type GuideSpot = {
  /**
   * 밝힐 요소의 `data-tour` 값. 없는 이름이면 이 걸음은 통째로 빠진다.
   *
   * <p>{@code null} 이면 <b>밝힐 자리 없이 카드만</b> 뜬다(화면 가운데). 순서를 설명하는 걸음 중에는
   * 짚을 요소가 없는 것이 있다 — 「결재가 끝나면 여기로 내려옵니다」 처럼 <b>다른 화면에서 일어나는
   * 일</b>이 그렇다. 그런 걸음까지 억지로 어딘가에 붙이면 엉뚱한 곳이 밝아진다.
   */
  target: string | null;
  title: string;
  body: string;
  /** 밝힌 영역 <b>안에서</b> 한 번 더 반짝일 곳. */
  subTargets?: string[];
  /**
   * 이 걸음 동안 <b>대신 열어 둘 판</b>(옆판·모달). 값은 그 판을 여는 버튼의 `data-tour` 이름이다
   * ({@link TourStep.openPanelKeys} · {@code lib/tour/panels}).
   *
   * <p>「누르면 목록이 나옵니다」 까지만 말하는 안내는 <b>그 목록이 무엇인지</b>를 끝내 설명하지 못한다.
   * 그렇다고 사람에게 열어 보라고 할 수도 없다 — 투어 중에는 클릭이 막히고, 반대로 판이 열려 있으면
   * <b>? 버튼이 그 판에 가려</b> 안내를 시작할 수조차 없다.
   */
  openPanels?: string[];
  /**
   * 이 걸음 동안 <b>대신 열어 둘 탭</b>. 값은 그 탭 버튼의 `data-tour` 이름이다
   * ({@link TourStep.openTabKey} · {@code lib/tour/tabs}).
   *
   * <p>탭 줄을 밝히고 「이것들이 목차입니다」 로 끝내는 안내는 <b>탭 안에서 무엇을 누르면 무슨 일이
   * 일어나는지</b> 를 끝내 말하지 못한다. 대신 열어 주고 그 안의 자리를 하나씩 짚는다.
   */
  openTab?: string;
  /**
   * 이 걸음 동안 대신 펼쳐 둘 사이드바 그룹({@link TourStep.expandMenuKeys}). 접힌 메뉴 한 줄을
   * 짚을 때 함께 적는다 — 접혀 있으면 그 줄이 화면에 없어 걸음이 조용히 빠진다.
   */
  expandMenuKeys?: string[];
  /**
   * 이 걸음을 볼 수 있는 권한({@link TourStep.requires}). {@code openPanels} 로 여는 판 <b>안</b>의
   * 요소는 시작할 때 아직 없어 「없으면 빠진다」 규칙이 통하지 않는다 — 그때만 여기로 거른다.
   */
  requires?: TourStep['requires'];
  /** 직접 해 보는 걸음 — 막을 걷고 실제로 누르고 적는다({@link TourStep.interactive}). */
  interactive?: boolean;
  /** 이 `data-tour` 가 나타나면 다음 걸음으로 넘어간다({@link TourStep.advanceWhen}). */
  advanceWhen?: string;
  /** 사람이 할 일 — 누르기(대신 눌러 줌)·고르기({@link TourStep.act}). */
  act?: TourStep['act'];
  placement?: TourPlacement;
};

type PageGuideSpec = {
  /** 라우트 경로. `screens.tsx` 의 `path` 와 같아야 한다. */
  path: string;
  /** 화면 이름. 첫 걸음의 제목이 된다. */
  label: string;
  /** 이 화면은 무엇을 하는 곳인가. 대상 없이 화면 가운데 카드로 뜬다. */
  what: string;
  /** 상단 탭 줄(`page-tabs`)에 대한 설명. */
  tabs?: string;
  /**
   * 그 걸음의 제목 — <b>목차가 상단 줄이 아닌 화면만</b> 적는다(현장 조회의 왼쪽 나무 · 2026-09-28).
   * 비워 두면 「상단 탭」이다. 짚는 자리가 왼쪽인데 제목이 「상단」이면 사람은 위를 쳐다본다.
   */
  tabsTitle?: string;
  /** 제목 오른쪽 버튼 묶음(`page-actions`)에 대한 설명. "이 버튼들로 …" 로 쓴다. */
  actions?: string;
  /** 그 밖에 짚을 자리들. 화면에 나타나는 순서대로 적는다. */
  spots?: GuideSpot[];
  /** 마지막 한마디 — 자주 묻는 것, 권한, 주의할 점. */
  tip?: string;
};

/** 홈에서 누르면 나오는 대본. 처음 로그인했을 때 자동으로 뜨는 것과 같은 것이다. */
export const HOME_GUIDE_KEY = 'app-intro';
const HOME_PATH = '/home';

/** 여러 화면이 같은 말을 하는 자리 — 문구가 화면마다 미묘하게 달라지지 않도록 한 번만 쓴다. */
const LIST_CLICK = '줄을 누르면 자세한 내용이 펼쳐집니다. 수정·삭제는 권한이 있을 때만 함께 보입니다.';

/**
 * 화면별 안내 대본.
 *
 * 경로는 `screens.tsx` 의 라우트와 맞춘다. 탭이 URL 로 갈리는 화면(주간업무·월간업무)은 탭마다
 * 한 줄씩 둔다 — 같은 화면이라도 `/meetings/hq` 로 들어온 사람에게는 본사 탭 이야기를 해야 한다.
 */
const PAGE_GUIDES: PageGuideSpec[] = [
  {
    path: '/sites/status/completed',
    label: '준공',
    what: '끝난 현장에서 하는 일이 전부 여기 있습니다 — 입주민 앱으로 들어온 <b>하자</b>와 <b>게시판</b>입니다.',
    spots: [
      { target: null, title: '건물을 먼저 고릅니다', body: '위에서 고른 건물이 아래 다섯 갈피 전부의 전제입니다. 옆의 여섯 글자가 <b>입장 코드</b>이고, 입주민이 앱 첫 화면에 넣는 값입니다.' },
      { target: null, title: '하자 접수', body: '배정 → 방문일 → 착수 → 처리 완료 순입니다. 건너뛴 것은 눌러도 서버가 막습니다. <b>처리 완료가 끝이 아닙니다</b> — 닫는 것은 입주민의 확인입니다.' },
      { target: null, title: '사전점검도 같은 자리', body: '입주 전 점검은 하자와 <b>같은 표</b>입니다. 흐름이 같아서 나누지 않았고, 갈피 하나로 가려 봅니다.' },
      { target: null, title: '게시판', body: '본사는 <b>가리기만</b> 합니다. 지우는 손이 없습니다 — 가리면 글과 사유가 함께 남고 되돌릴 수 있습니다. 집 관리 안내문(결로·동파)도 공지로 올립니다.' },
      { target: null, title: '이 달의 우리 집', body: '입주민이 우리 집 갈피에 올린 사진 글 중 하나를 게시판 갈피에서 <b>[이 달의 우리 집으로]</b> 뽑습니다. 한 달에 하나이고, 뽑는 순간 글쓴이에게 「선정되었습니다 · 소정의 선물을 드립니다」가 갑니다. 선물은 손으로 건넵니다.' },
      { target: null, title: '신고', body: '입주민이 글·댓글·쪽지·사람을 신고하면 여기 섭니다(전 현장이 기본). 줄마다 <b>가리기 · 정지 · 차단 · 닫기</b> 중 하나로 닫습니다. 정지는 기한까지 쓰기만 막고 하자 접수는 둡니다. 쪽지는 신고된 그 한 통만 보입니다.' },
      { target: 'resident-register', title: '신청 없이 세우려면', body: '입주민 갈피의 [직접 등록]에 이름·휴대폰·동·호·비밀번호를 적으면 <b>바로 쓸 수 있는 계정</b>이 섭니다. 스토어 심사용·관리사무소 시연용 계정은 여기서 만듭니다.' },
      { target: null, title: '설정에서 켭니다', body: '준공일과 세대를 채운 뒤 「입주민 앱에 이 건물을 연다」를 켭니다. 켠 순간부터 그 코드로 가입 신청이 들어옵니다.' },
    ],
    tip: '자동 승인은 없습니다. 우리가 입주자 명단을 갖고 있지 않아 「그 집 사람인지」를 사람이 봐야 합니다.',
  },

  // ── 게시판 ──────────────────────────────────────────────────────────────
  //
  // 익명과 <b>누가 읽는가</b>가 이 화면의 전부다(V218 의 표 · V259 의 규칙). 두 대본 모두 그
  // 이야기부터 한다 — 「내 이름이 보이나」·「누구에게 가나」 가 안 풀리면 아무도 쓰지 않고,
  // 그 물음은 화면의 설명 한 줄로는 안 가신다.
  {
    path: '/board/thanks',
    label: '윤리경영제보센터',
    what: '회사 안에서 본 일을 <b>익명으로</b> 제보하는 곳입니다. 이름이 어디에도 남지 않습니다.',
    actions: '이 버튼으로 제보합니다. 전 직원이 쓸 수 있습니다.',
    spots: [
      { target: 'page-filters', title: '건수와 정렬', body: '최신순·오래된순으로 바꿔 봅니다. 순서를 바꾸면 <b>다른 목록</b>이라 첫 쪽부터 다시 봅니다.' },
      { target: 'page-list', title: '목록', body: LIST_CLICK + ' 여기 서는 줄은 <b>내가 낸 제보</b>뿐입니다 — 들어온 제보 전부를 보는 것은 제보센터 담당입니다.' },
    ],
    tip: '제보하고 나면 목록에 내 줄이 남습니다. <b>답변은 그 자리에 달립니다</b> — 익명이라 따로 알려 드릴 길이 없습니다.',
  },
  {
    path: '/board/thanks/:postId',
    label: '제보 내용',
    what: '제보 하나와 그에 달린 답변을 보는 곳입니다. 제보도 답변도 익명입니다.',
    actions: '이 버튼으로 목록으로 돌아갑니다.',
    spots: [
      // 「답변」 은 세는 칸이자 내려가는 버튼이라, 숫자만 보고 어디 있는지 다시 찾는 일이 없게 적는다.
      { target: null, title: '답변은 제목 줄에서', body: '제목 오른쪽의 「답변 n」 을 누르면 답변 자리로 내려갑니다. 답변을 다는 것은 제보센터 담당입니다.' },
    ],
    tip: '[수정]·[삭제]는 <b>내가 낸 제보에만</b> 보입니다. 지운 뒤에는 되돌릴 수 없고, 달린 답변도 함께 사라집니다.',
  },

  // ── 보미소식 ────────────────────────────────────────────────────────────
  {
    path: '/news/notices',
    label: '공지사항',
    what: '회사 공지와 업무 안내를 게시판 형태로 나누는 곳입니다.',
    actions: '이 버튼으로 새 공지를 올립니다. 등록 권한이 없으면 버튼이 보이지 않고 읽기만 됩니다.',
    spots: [
      { target: 'page-filters', title: '이 버튼들로 분류를 고릅니다', body: '기획·인사·정보(공통 항목)와 계약·착공·진행·준공(현장 진행 순서)으로 나뉩니다. 여러 개를 겹쳐 고르지는 않고, 누른 하나만 남습니다.' },
      { target: 'page-list', title: '공지 목록', body: LIST_CLICK + ' 최신 글이 위에 옵니다.' },
    ],
    tip: '최근 올라온 공지는 홈 화면 위젯에서도 바로 볼 수 있습니다.',
  },
  {
    path: '/news/employees',
    label: '입/퇴사자 소식',
    what: '매월 입사자와 퇴사자를 구분해 등록하고 공유하는 곳입니다.',
    actions: '인사팀이 이 버튼으로 그 달의 입사자·퇴사자를 등록합니다.',
    spots: [
      { target: 'page-filters', title: '여기서 달을 바꿉니다', body: '월을 고르면 그 달의 입사자·퇴사자만 아래에 나옵니다. 지난 달 소식도 같은 방법으로 봅니다.' },
    ],
    tip: '입사자와 퇴사자는 좌우로 나뉘어 있습니다. 퇴사자 목록은 권한에 따라 보이지 않을 수 있습니다.',
  },
  {
    path: '/news/dining',
    label: '식당안내',
    what: '본사 구내식당 식단표와 현장별 장부식당 정보를 보는 곳입니다.',
    tabs: '이 버튼들로 본사와 현장을 오갑니다. 본사는 주차별 식단표, 현장은 장부식당 목록으로 내용이 아예 다릅니다.',
    tip: '현장 장부식당은 상호·연락처·위치가 함께 적혀 있어, 출장 갔을 때 그대로 쓸 수 있습니다.',
  },
  {
    path: '/news/polls',
    label: '투표',
    what: '사내 투표에 참여하고 지난 투표의 결과를 보는 곳입니다. 제목을 누르면 선택지가 펼쳐집니다.',
    actions: '이 버튼으로 새 투표를 만듭니다. 만들 권한이 없으면 버튼이 보이지 않고 참여만 됩니다.',
    // 「누가 무엇을 골랐는지」 를 화면이 아예 모른다는 사실이 이 화면의 첫 물음이다
    // (`PollBoardPage` 머리말 — 서버가 마감 전에는 표 수를 응답에서 빼고 보낸다).
    tip: '누가 무엇을 골랐는지는 아무도 볼 수 없습니다. 표 수는 마감된 뒤에만 나옵니다.',
  },
  {
    path: '/news/trends',
    label: '건설동향',
    what: '건설 관련 뉴스와 시장 동향을 모아 공유하는 곳입니다. 업무에 참고할 만한 기사를 직원이 직접 올립니다.',
    actions: '이 버튼들로 기사를 등록하거나, 자동 수집해 오는 소스를 관리합니다.',
    spots: [
      { target: 'page-filters', title: '이 줄에서 찾습니다', body: '위쪽 버튼들로 종류를 고르고, 아래 검색창에 제목·내용·출처를 넣어 좁힙니다. 둘은 함께 걸립니다.' },
    ],
  },
  {
    path: '/news/headlines',
    label: '뉴스',
    what: '섹션별 주요 뉴스를 모아 보여 주는 곳입니다. 제목을 누르면 언론사 원문으로 이동합니다.',
    actions: '이 버튼으로 지금 바로 다시 수집합니다. 몇 건이 새로 들어왔는지 숫자로 알려 줍니다.',
    tip: '기사는 평소 자동으로 수집됩니다. 따로 등록할 것은 없습니다.',
  },
  {
    path: '/news/guidelines',
    label: '사내지침&매뉴얼',
    what: '부서별 사내 지침과 업무 매뉴얼, 그 개정 이력을 보는 곳입니다. 지금 유효한 판이 무엇인지, 무엇이 바뀌었는지 함께 확인합니다.',
    actions: '이 버튼으로 지침·매뉴얼을 등록합니다. 시행일자·종결일자와 개정일·변경 내용을 함께 적어 두면 이력 표에 그대로 남습니다 — 종결일자를 비우면 「계속」으로 표기됩니다.',
    spots: [
      { target: 'page-filters', title: '이 줄에서 좁힙니다', body: '종류 버튼과 담당 부서를 골라 필요한 지침만 남깁니다. 검색어도 함께 걸 수 있습니다.' },
    ],
    tip: '「개정 이력 보기」 를 누르면 판별로 개정일·시행일·변경 내용이 한 표에 섭니다. 줄을 누르면 그 판의 본문으로 바로 갈아탑니다.',
  },

  // ── 총무 취합 대장(DOC-43 §14 2단계) ─────────────────────────────────────
  {
    path: '/hr/business-cards',
    label: '명함 취합',
    what: '결재가 끝난 명함 신청이 모이는 곳입니다. 여러 건을 묶어 한 번에 발주하고 입고·배부까지 여기서 진행합니다.',
    actions: '미발주 목록에서 여러 건을 고른 뒤 이 버튼으로 발주 묶음을 만듭니다.',
    spots: [
      { target: 'page-filters', title: '상태로 좁힙니다', body: '미발주 → 묶음 → 발주 → 입고 → 배부완료 순으로 일이 흘러갑니다. 「업체 제출용 CSV」 는 지금 보고 있는 목록(또는 고른 건)을 그대로 추출합니다.' },
      { target: 'page-list', title: '신청 목록', body: '이름·직급·연락처는 신청 시점 값입니다. 나중에 인사 정보가 바뀌어도 그때 찍은 명함은 그대로 남습니다.' },
    ],
    tip: '이미 묶은 신청은 다시 묶이지 않습니다 — 같은 사람 명함이 두 번 발주되는 것을 시스템이 막습니다.',
  },
  {
    path: '/expenses/standards',
    label: '경비 기준안',
    what: '부서·현장 경비를 산정하는 기준입니다. 여기 넣은 항목과 단가로 신청 화면이 금액을 만듭니다.',
    actions: '이 버튼으로 기준안을 등록합니다. 항목마다 산정 방식(인원당·정액·현장당)과 단가를 적습니다. 쓰던 기준안 표가 있으면 「엑셀로 가져오기」·「PDF로 가져오기」로 한 번에 올립니다 — 기준안 이름·시행일자가 같은 줄들을 한 기준안으로 묶어 읽습니다.',
    tip: '종결일자를 비우면 「계속」입니다. 항목을 저장하면(가져오기도 마찬가지) 기존 항목은 통째로 바뀌지만, 이미 산정된 신청의 금액은 그대로 남습니다.',
  },
  {
    path: '/expenses/requests',
    label: '경비 신청 대장',
    what: '결재가 끝난 부서·현장 경비가 월별로 쌓입니다. 자동 산정액과 조정 내역을 나란히 봅니다.',
    actions: '월과 구분을 골라 그 달 신청만 봅니다.',
    tip: '지급·정산은 상태로 넘깁니다. 정산 자체(원가 배부)는 별도 절차라 이 화면이 하지 않습니다.',
  },
  {
    path: '/expenses/courier',
    label: '퀵서비스 대장',
    what: '그 달의 퀵 요청이 모입니다. 업체 청구서를 받으면 여기서 금액을 채우고 대사합니다.',
    actions: '월을 골라 그 달 요청과 정산을 봅니다.',
    tip: '청구액은 사람이 청구서를 보고 칩니다 — 대장 합계를 그대로 옮기면 업체가 더 청구해도 알 수 없습니다. 차이가 나면 「차이 있음」 으로 남습니다.',
  },
  {
    path: '/assets/reservations/rooms',
    label: '회의실 예약',
    what: '회의실을 잡는 곳입니다. 누르면 바로 확정됩니다 — 승인을 기다리지 않습니다.',
    actions: '회의실 카드의 「예약하기」 로 날짜와 시간을 고릅니다.',
    tip: '겹치는 시간에 잡으면 언제 비는지 알려 드립니다. 끝나는 시각과 시작 시각이 같은 예약은 겹치지 않습니다 — 연달아 쓰는 회의를 막지 않습니다.',
  },
  {
    path: '/assets/supplies/items',
    label: '소모품 관리',
    what: '품목과 재고를 관리하는 곳입니다. 불출이 승인되면 여기 재고가 차감됩니다.',
    actions: '「품목 등록」 으로 새 소모품을 추가합니다.',
    tip: '직원이 신청하는 화면은 「소모품·비품 신청」 입니다 — 같은 자료를 보지만 하러 온 일이 다릅니다.',
  },
  {
    path: '/assets/software',
    label: '소프트웨어 자산',
    what: 'CAD·오피스 같은 업무 프로그램의 라이센스를 관리합니다. 현황 · 신청 · 내역 세 탭이고, 만료가 가까운 것이 위에 옵니다.',
    actions: '이 버튼으로 라이센스를 등록합니다. 구독이면 만료일을, 영구면 비워 둡니다.',
    spots: [
      { target: 'page-filters', title: '탭이 셋입니다', body: '현황은 갖고 있는 것과 나가 있는 것, 신청은 빈 라이센스 요청과 승인·불출, 내역은 누가 언제 받아 언제 돌려줬는지입니다.' },
      { target: 'page-list', title: '표의 주인공은 라이센스입니다', body: '한 줄이 라이센스 1개의 배포 한 건입니다. 앞쪽이 제품·유형·만료이고 사용자·현장·등록 PC 는 그 뒤, 배포일·회수일이 끝입니다. 뒤쪽이 빈 줄은 «지금 줄 수 있는» 라이센스입니다.' },
    ],
    tip: '회수한 줄은 지워지지 않고 회수일이 찍힙니다. 지난달에 누가 썼는지는 갱신 협상에서 실제로 묻는 값입니다.',
  },
  {
    path: '/assets/movements',
    label: '자산 이동',
    what: '창고와 현장 사이의 자산 이동을 기록합니다. 지금 이동 중인 물건이 기본으로 보입니다.',
    actions: '이 버튼으로 이동을 등록합니다. 출발지는 적지 않습니다 — 자산이 지금 있는 자리가 곧 출발지입니다.',
    spots: [
      { target: 'page-list', title: '도착 확인이 위치를 바꿉니다', body: '보내기만 하면 자산의 위치는 그대로입니다. 실제로 받은 뒤에 「도착 확인」 을 눌러야 배치가 바뀝니다.' },
    ],
    tip: '현물이 왔으면 「스캔 도착」 으로 태그를 찍어 처리하세요 — 누가 실제로 받았는지가 남습니다. 이동 이력은 지워지지 않습니다: 잘못 보낸 것은 취소로 남기고, 이미 도착한 것은 반대 방향 이동을 만듭니다.',
  },
  {
    path: '/assets/materials',
    label: '잡자재 대장',
    what: '결재로 올라온 현장 잡자재 신청이 모이는 대장입니다. 외주구매팀이 여기서 기성 반영월을 정하고 증빙을 붙입니다.',
    actions: '줄의 「처리」 를 누르면 상태·기성 반영월·거래처를 고치고 증빙을 붙일 수 있습니다.',
    spots: [
      {
        target: 'page-list',
        title: '미정산이 맨 위에 옵니다',
        body: '기성 반영월이 빈 건이 먼저 나옵니다. 쌓이면 그 현장의 원가가 뒤늦게 튀는 신호라 이 화면에 오는 이유가 대개 그것입니다.',
      },
    ],
    tip: '수량·단가·금액은 여기서 고칠 수 없습니다 — 결재가 전결 구간을 정한 근거입니다. 금액이 달라졌다면 새 결재로 올려 주세요.',
  },
  {
    path: '/assets/material-prices',
    label: '자재 단가 설정',
    what: '홈의 「환율 · 자재 단가」 위젯이 읽는 표입니다. 철근·레미콘처럼 자주 보는 자재의 단가를 적어 둡니다.',
    actions: '이 버튼으로 자재를 하나 등록합니다. 자재·단가·단위·기준일이 필요하고, 출처는 「물가정보 2026-08월호」처럼 적어 둡니다.',
    spots: [
      { target: 'page-list', title: '한 줄이 자재 하나입니다', body: '달마다 새 줄을 만들지 않습니다. 값이 바뀌면 그 줄의 단가와 기준일만 고치면 되고, 「직전 대비」 는 저장할 때 자동으로 계산됩니다.' },
    ],
    tip: '넣지 않아도 됩니다 — 한 줄도 없으면 홈 위젯에 자재 칸이 아예 나오지 않고 환율만 보입니다. 철근 시중 유통단가는 무료로 받을 수 있는 곳이 없어(물가정보·건설물가는 유료 구독) 자동으로 채워 드리지 못합니다. 그래서 숫자 옆에 기준일과 출처가 늘 함께 나갑니다.',
  },
  {
    path: '/assets/items',
    label: '비품 자산 현황',
    what: '회사 비품을 종류별 표로 봅니다. 들어오면 「요약」 이 먼저 서고, PC · 전열기기 · 무전기 · 퇴직공제부금 단말기 · 세단기 · 차량 탭이 이어집니다. 쓰던 엑셀의 머리글이 그대로 칸입니다.',
    actions: '「직접 입력」 과 「PDF로 가져오기」 는 보고 있는 탭의 칸으로 읽습니다. 차량 탭에서만 「엑셀로 열기」 와 「자산 등록」 이 섭니다.',
    spots: [
      { target: 'page-tabs', title: '탭 하나가 엑셀 한 장', body: '요약은 아래 탭에서 센 값이라 적는 칸이 없습니다. PC 는 직원 자산 현황의 PC 와 같은 자료입니다.' },
      { target: 'page-list', title: '표에서 바로 넣고 고칩니다', body: '[줄 추가] 로 한 줄씩, 또는 엑셀에서 여러 줄을 복사해 새 줄의 아무 칸에 붙여 넣으면 줄이 섭니다. 줄마다 [저장] · [삭제] 입니다. 무전기 · 세단기는 [+ 사용자 칸] 으로 사용자 묶음을 더 폅니다.' },
    ],
    tip: '연식은 적지 않습니다 — 구매연도를 적으면 올해에서 빼서 옆에 섭니다. 퇴직공제부금 단말기의 구매연도에는 「예정」 을 적어도 됩니다. 차량은 지우지 않고 「종료」 로 끕니다(예약 · 운행일지가 가리킵니다).',
  },
  {
    path: '/assets/meals',
    label: '식대 관리',
    what: '구내식당에서 점심을 먹은 사람을 날짜별로 봅니다. 직원이 식당에 붙은 QR 을 휴대폰 카메라로 찍으면 그날 명단에 한 줄이 섭니다.',
    actions: '「식당 QR」이 벽에 붙일 종이를 그려 줍니다 — 창에서 바로 인쇄하면 됩니다. 「설정」에서는 점심으로 인정하는 시간대(기본 11:00~14:00)를 고치고, 링크가 새 나갔을 때 QR 을 다시 뽑습니다. 다시 뽑으면 <b>붙어 있던 QR 은 그 자리에서 죽습니다</b>.',
    spots: [
      { target: 'page-list', title: '왼쪽이 달, 오른쪽이 하루입니다', body: '왼쪽 날짜를 누르면 오른쪽 명단이 그날로 바뀝니다. 맨 아래 「합계」가 그 달의 식수이고, 정산은 이 숫자로 합니다.' },
    ],
    tip: '휴대폰을 두고 왔거나 손님이 함께 먹었으면 이름을 적고 「명단에 추가」를 누릅니다 — 그 줄은 「수기」로 표시되어 찍은 줄과 갈립니다. 잘못 찍힌 줄은 줄 끝의 「삭제」로 뺍니다. 점심 시간대 밖에서 찍으면 체크되지 않습니다.',
  },
  {
    path: '/assets/staff',
    label: '직원 자산 현황',
    what: '전 직원에게 나간 개인 자산(PC · 모니터 · 휴대폰 · 태블릿)을 한 표로 봅니다. 인사기록 › 지급 자산 탭과 같은 자료인데, 사람을 하나씩 열지 않고 전체를 한 번에 봅니다.',
    actions: '「+ 등록」을 누르면 <b>빈 줄이 깔린 표</b>가 열립니다 — 한 대씩이 아니라 들어온 만큼 한 번에 적는 자리입니다. 위 칸에 엑셀을 <b>그대로 붙여 넣으면(Ctrl+V)</b> 줄이 채워지고, 눈으로 고친 뒤 저장합니다. 분류는 지금 보고 있는 탭이 채우고, 직원 칸을 비우면 윗줄과 같은 사람으로 봅니다. 고치거나 지우는 것은 줄을 눌러 연 창의 제목 줄에 있습니다. 표 오른쪽 위 「소속 색 설정」에서 부서·현장마다 색을 고르면, 직원 이름이 그 색 테두리에 감싸여 섭니다 — 태블릿을 여러 현장에 뿌린 표에서 우리 현장 것을 눈으로 찾을 때 씁니다.',
    spots: [
      { target: 'page-list', title: '오른쪽 두 칸은 기기가 말한 것입니다', body: '「기종(앱)」과 「마지막 접속」은 사람이 적은 값이 아니라 그 사람이 앱에 로그인할 때 기기가 밝힌 값입니다. 지급했는데 「접속 없음」이면 쓰지 않는 기기라는 뜻입니다.' },
    ],
    tip: 'S/N 칸은 사람이 적습니다 — 앱은 시리얼 번호를 읽지 못합니다(안드로이드 10부터 막혔습니다). 기기 관리 프로그램(MDM) 콘솔에 있는 번호를 옮겨 적거나, 물건 상자 겉면의 바코드를 보고 적습니다. 소프트웨어는 이 화면에서 만들지 않습니다 — 라이선스는 자산관리 › 소프트웨어 자산에서 좌석으로 불출합니다. 회수한 물건도 지우지 말고 <b>반납일</b>을 적으면 표 아래로 내려갑니다.',
  },
  {
    path: '/assets/site-inventory',
    label: '현장 자산 현황',
    /*
      대본이 <b>탭 안까지</b> 들어간다(사용자 지시 2026-09-07). 종전 대본은 걸음 둘이었고,
      정작 이 화면에서 사람이 막히는 자리 — 「확인 필요가 뭔가」·「수량은 어디서 고치나」·
      「보냈는데 왜 상대 현장에 안 늘었나」 — 는 한 줄도 없었다. 만든 사람조차 못 알아본 안내다.

      <p>이 화면은 <b>재고 프로그램</b>이다. 그 말이 익숙하지 않은 사람에게 필요한 것은 기능 목록이
      아니라 <b>「무엇을 하면 수량이 움직이나」</b> 하나라, 걸음의 뼈대를 그 순서로 세운다:
      들어오는 길(가져오기) → 확인 → 수량이 움직이는 다섯 갈래 → 부족 → 원장.
    */
    what: '현장에 있는 물건이 <b>지금 몇 개인지</b>를 대장으로 관리하는 곳입니다. 입고·출고·실사·폐기를 적으면 수량이 그만큼 오르내리고, 그 한 줄 한 줄이 「수불 이력」에 남습니다. 탭을 하나씩 열어 가며 짚어 드릴게요.',
    tabs: '재고 현황 · 부족·발주 · 수불 이력. <b>지금 몇 개인가 → 사야 하나 → 왜 그 수가 됐나</b> 순서입니다. 고른 탭은 주소에 남아, 링크로 그대로 건네줄 수 있습니다.',
    spots: [
      {
        target: 'page-filters',
        title: '현장부터 고릅니다',
        body: '처음에는 <b>전 현장</b>이 함께 나옵니다. 왼쪽에서 현장을 고르면 그 현장만 보이고, 탭을 옮겨도 그대로 걸려 있습니다. 「확인이 필요한 줄만」 은 아래에서 설명할 <b>확인 필요</b> 줄만 모아 보는 스위치입니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'inventory-import',
        title: '재고 현황 — 쓰던 표를 그대로 올립니다',
        body: '엑셀로 관리하고 계셨다면 <b>[엑셀로 가져오기]</b>, 현장에서 받은 스캔본·사진이면 <b>[PDF로 가져오기]</b> 입니다. 열 이름이 「품명」이든 「자재명」이든 알아서 맞춰 읽고, 올리기 전에 <b>「새로 등록 n · 기존 갱신 m」</b> 을 먼저 보여 줍니다. 같은 표를 두 번 올려도 줄이 두 벌 되지 않습니다 — 현장·자산명·규격·제조번호가 같으면 그 줄을 고칩니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'inventory-unverified',
        title: '재고 현황 — 「확인 필요」 는 이렇게 없앱니다',
        body: '스캔으로 읽은 줄은 <b>사람이 원본과 맞춰 보기 전까지</b> 확인 필요로 앉습니다. 「300」이 「800」으로 읽혀도 표에서는 이상해 보이지 않기 때문입니다. 없애는 방법은 <b>표 오른쪽 끝 「확인」 칸</b>의 네모를 켜고(맞는 줄만) 이 띠의 버튼을 누르는 것입니다 — 버튼에 적힌 숫자가 지금 고른 줄 수라, 아무것도 안 켰으면 <b>0줄</b>이고 눌리지 않습니다. 그 줄의 값을 직접 고쳐도 확인한 것으로 봅니다. 확인할 줄이 없으면 이 띠는 나오지 않습니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'page-list',
        title: '재고 현황 — 한 줄이 현장 하나의 품목 하나',
        body: '표는 <b>읽는 상태</b>로 열립니다. 값을 고치려면 먼저 표 제목 줄의 <b>[수정]</b> 을 누르세요 — 그때 칸이 하늘색으로 열리고, 고친 줄에만 <b>[저장]</b> 이 섭니다. 새 줄을 넣는 <b>[＋ 자산 추가]</b> 도 그때 함께 섭니다. 「안전재고」에 <b>있어야 할 최소 수량</b>을 적어 두면 그 아래로 떨어질 때 수량이 <b>빨갛게</b> 서고 ▼ 로 모자란 만큼이 붙습니다 — 이 칸이 비어 있으면 부족은 영영 잡히지 않습니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'page-list',
        title: '재고 현황 — 수량은 [수불] 로 움직입니다',
        body: '줄 끝의 <b>[수불]</b> 이 <b>입고 · 출고 · 실사 · 폐기 · 기초</b> 다섯을 엽니다. 「몇 개가 되었나」가 아니라 <b>「몇 개가 움직였나」</b> 를 적는 자리라, 500 중 20을 쓰셨으면 출고에 <b>20</b> 을 칩니다(실사만 «세어 보니 몇 개» 를 그대로 적습니다). 누르기 전에 「500 → 480」 이 미리 보이고, 재고보다 많이 나가도 막지는 않습니다 — 대신 음수로 빨갛게 남습니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'page-list',
        title: '재고 현황 — 수량 칸을 직접 고치면 사유를 묻습니다',
        body: '<b>[수정]</b> 을 켠 상태에서 수량을 바로 쳐도 됩니다. 다만 저장 대신 <b>「직접 수정」</b> 창이 열리고 <b>사유</b>를 받습니다 — 「300 → 180」 만 남으면 그것이 오타인지 도난인지 반출인지 나중에 아무도 모릅니다. 실제로 쓴 것이면 사유를 적기보다 <b>[수불] › 출고</b> 로 적어 주세요.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'page-list',
        title: '재고 현황 — [이동] 은 보내는 쪽만 찍습니다',
        body: '다른 현장이나 본사 창고로 넘길 때 씁니다. 보낸 쪽 재고는 <b>바로</b> 빠지지만 받는 현장 재고는 <b>그쪽이 [도착] 을 찍을 때</b> 오릅니다 — 그 사이 수량은 「이동중」이고 어느 현장의 재고도 아닙니다. 송장번호·운송사·차량번호는 비워 두어도 됩니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'inventory-shipments',
        title: '재고 현황 — 이 현장으로 오는 차',
        body: '다른 현장이 보낸 차가 있으면 표 <b>위</b>에 섭니다. <b>[도착]</b> 한 번이면 그 차에 실린 줄이 전부 앉습니다. 송장은 300인데 280만 왔다면 <b>실제로 온 수량</b>을 치세요 — 송장 수량으로 앉히면 표만 맞고 현장에는 물건이 없습니다. 올 차가 없으면 이 자리는 아예 나오지 않습니다.',
      },
      {
        openTab: 'inventory-tab-stock',
        target: 'page-list',
        title: '재고 현황 — QR·바코드 태그',
        body: '<b>[태그 발급]</b> 을 누르면 QR 그림이 뜨고, PNG 로 받아 라벨지에 얹어 물건에 붙입니다. 발전기처럼 <b>낱개로 세는 것</b>에만 붙습니다 — 「강관비계 300본」에 스티커 한 장을 붙이면 그것이 300본 중 무엇인지 알 수 없어 수량이 1을 넘는 줄에는 버튼이 서지 않습니다. 창고에서 <b>[태그 스캔]</b> 으로 찍으면 그 줄의 출고 창이 바로 열립니다.',
      },
      {
        openTab: 'inventory-tab-shortage',
        target: 'shortage-table',
        title: '부족·발주 — 사기 전에 남는 현장을 먼저 봅니다',
        body: '안전재고에 미달한 품목이 모입니다. 부족한 현장 오른쪽에 <b>같은 품목이 남는 현장</b>이 같은 줄에 서고, 그 자리에서 바로 <b>[이동]</b> 을 엽니다. 남는 데가 없으면 <b>[자재 신청]</b> — 현장·품목·규격·부족 수량이 채워진 채로 전자결재 서식이 열립니다. 안전재고를 안 적은 현장은 「미설정 n곳」으로만 세고 여유로 치지 않습니다.',
      },
      {
        openTab: 'inventory-tab-txns',
        target: 'txn-filters',
        title: '수불 이력 — 종류와 기간으로 거릅니다',
        body: '「지난달에 이 현장에서 나간 것만」 처럼 좁혀 봅니다. 표는 <b>다운로드</b>로 엑셀을 받을 수 있으니 월말 정리에 그대로 쓰시면 됩니다.',
      },
      {
        openTab: 'inventory-tab-txns',
        target: 'page-list',
        title: '수불 이력 — 「왜 그 수가 됐나」에 답하는 표',
        body: '한 줄이 수불 한 건입니다. <b>잔량</b> 칸이 그 줄을 적은 <b>직후의 재고</b>라, 위에서 아래로 읽으면 수량이 어떻게 그 수가 됐는지가 그대로 보입니다. 「상대」에 <b>이동중</b> 딱지가 붙은 줄은 보냈는데 아직 아무도 도착을 안 찍은 것입니다. <b>이 표는 고치거나 지울 수 없습니다</b> — 잘못 적었으면 반대 방향으로 한 줄 더 적습니다.',
      },
    ],
    tip: '이카운트 같은 재고 프로그램을 쓰셨다면 <b>「품목 → 입출고 전표 → 재고수불부」</b> 가 여기서는 <b>「재고 현황 → [수불] → 수불 이력」</b> 입니다. 다른 점은 둘입니다 — 적은 것을 지우지 않고 반대 줄로 바로잡는다는 것, 그리고 <b>스캔으로 들어온 줄은 사람이 확인하기 전까지 「확인 필요」로 남는다</b>는 것입니다. 표가 안 보이거나 버튼이 없으면 대개 권한이니 관리자에게 요청하세요.',
  },
  {
    path: '/assets/reservations/vehicles',
    label: '차량 예약',
    what: '회사 차량과 회의실을 잡는 곳입니다. 회의실은 누르면 바로 확정되고, 차량은 총무 배차 승인을 한 단계 거칩니다.',
    actions: '자산 카드의 「예약하기」 로 날짜와 시간을 고릅니다. 차량은 「반납일」 칸이 있어 1박·2박 운행도 여기서 적습니다.',
    spots: [
      { target: 'page-filters', title: '주 단위로 봅니다', body: '차량·회의실 탭을 고르고 주를 넘기며 빈 시간을 찾습니다.' },
      { target: 'page-list', title: '자산별 이번 주', body: '이미 잡힌 시간이 줄로 보입니다. 겹치는 시간에 잡으면 언제 비는지 알려 드립니다.' },
    ],
    tip: '1박 이상·주말 운행도 같은 신청으로 총무 배차 승인을 거칩니다. 다 쓰면 예약을 눌러 「사용 종료」 로 반납하고, 주행거리와 사진·영수증은 필요할 때만 붙입니다. 승인하는 자리는 총무 관리 › 배차 관리입니다.',
  },
  {
    path: '/general-affairs/dispatch',
    label: '배차 관리',
    what: '차량 예약을 날짜가 아니라 상태로 모아 봅니다. 승인대기 · 선사용 확인 · 사용중 · 반납완료를 칩으로 고르고, 줄마다 바로 승인·반려·사용 종료합니다.',
    actions: '「담당자」 에서 새 배차 요청의 앱 푸시와 홈 할일을 받을 사람을 고릅니다. 처리되면 그 할일은 사라집니다.',
    spots: [
      { target: 'page-filters', title: '상태 칩', body: '승인대기와 선사용 확인에는 기다리는 건수가 붙습니다. 「전체」 는 지난 것까지 다 보여 줍니다.' },
      { target: 'page-list', title: '줄 하나가 예약 하나', body: '줄을 누르면 예약 창이 열리고, 줄 끝의 단추는 그 창의 단추와 같은 일을 합니다. 반납 칸에는 주행거리 · 정산액 · 첨부가 함께 보입니다.' },
    ],
    tip: '승인 없이 쓴 차(선사용)는 「선사용 확인」 에 서고 [확인] 으로 처리합니다. 반납한 예약은 [정산 입력] 으로 유류비 · 통행료를 나중에 채웁니다.',
  },

  {
    path: '/remote-support',
    label: 'PC 원격 접속',
    what: '회사 PC 를 보고, [연결] 을 누르면 이 화면 안에서 그 PC 화면이 열립니다. 현장 사람이 그 PC 에서 [수락] 을 눌러야 보이고, 연결된 동안 그 PC 화면 위에는 띠가 남아 언제든 끊을 수 있습니다.',
    actions: '「설정」 에서 원격지원 서버 주소와 그 PC 로 바로 가는 주소 틀을 정합니다. PC 줄의 「기기 id」 를 누르면 그 자리에서 적습니다.',
    spots: [
      { target: 'page-filters', title: '찾기 · 설정', body: '이름 · PC 이름 · 소속으로 찾습니다. 「켜짐만」 은 지금 망에 붙어 있는 PC 만 보여 줍니다.' },
      { target: 'page-remote', title: '원격 화면', body: '[연결] 을 누르면 여기에 그 PC 화면이 열립니다. 처음 한 번은 원격지원 서버 로그인이 이 안에 뜹니다. [닫기] 로 접습니다.' },
      { target: 'page-list', title: '줄 하나가 PC 하나', body: '기기 id 가 비어 있으면 [연결] 이 원격지원 서버의 목록을 엽니다 — 거기서 PC 이름으로 고르면 됩니다.' },
    ],
    tip: '「접속 기록」 탭에 누가 언제 어느 PC 에 [연결] 을 눌렀는지 남습니다.',
  },

  {
    path: '/assets/certificates',
    label: '공인인증서 대장',
    what: '지금 나가 있는 인증서와 만료가 가까운 인증서를 봅니다. 불출·반납이 이력으로 쌓입니다.',
    actions: '반납을 받으면 그 줄에서 「반납 받음」 을 누릅니다.',
    tip: '비밀번호는 저장하지 않습니다. 이 대장이 답하는 것은 “누가 언제 들고 나갔고 언제 돌려줬는가” 입니다.',
  },

  // ── 보미기술집 ──────────────────────────────────────────────────────────
  {
    // `/tech` 로 들어오면 볼 수 있는 첫 탭에서 시작한다 — 그 자리의 안내도 이 항목이 맡는다.
    path: '/tech',
    label: '보미기술집',
    what: '공종별 기술집과 신기술 소개가 상단 탭으로 묶인 화면입니다.',
    actions: '탭을 눌러 오갑니다.',
    spots: [{ target: 'page-tabs', title: '두 자리가 탭입니다', body: '공종별 기술집·신기술 소개가 한 화면의 탭이 되었습니다. 옛 주소도 그대로 열립니다.' }],
  },
  {
    path: '/tech/cases',
    label: '공종별 기술집',
    what: '공종별로 시공 사례와 기술 자료를 쌓아 두는 곳입니다. 비슷한 공사를 시작할 때 먼저 찾아보는 자리입니다.',
    actions: '이 버튼으로 자료를 등록합니다.',
    spots: [
      { target: 'page-filters', title: '키워드로 찾습니다', body: '공종·현장·구분·적용 내용이 한 번에 걸립니다. 분류를 몰라도 기억나는 단어를 넣으면 됩니다.' },
      { target: 'page-list', title: '자료 목록', body: LIST_CLICK + ' 관련 자료는 파일이 아니라 링크로 등록되어 있어, 「자료 확인」 으로 바로 열립니다.' },
    ],
  },
  {
    path: '/tech/new',
    label: '신기술 소개',
    what: '새로 도입했거나 검토 중인 공법·자재·장비를 소개하는 곳입니다.',
    actions: '이 버튼으로 소개 글을 등록합니다.',
    spots: [{ target: 'page-filters', title: '키워드로 찾습니다', body: '제목과 내용이 함께 걸립니다.' }],
  },
  // 보미 실적(`/tech/performance`)은 2026-09-22 에 이 목록에서 내려갔다 — 화면이 현장 조회의
  // 준공 탭 안으로 들어가(`PerformancePanel`) 자기 주소를 갖지 않는다. 그 탭의 안내는
  // 현장 조회(`/sites/pmis`) 항목이 맡는다.

  // ── 현장 ────────────────────────────────────────────────────────────────
  {
    path: '/sites/status',
    label: '현장 현황',
    what: '현장별 현황과 공정 흐름을 한눈에 보는 화면입니다. 현장 관리에 등록된 정보와 주간회의에 올라온 내용을 모아 그립니다.',
    actions: '이 버튼들로 현장을 검색하고, 대화방으로 건너가고, 화면을 인쇄하거나 그래프로 봅니다.',
    spots: [
      { target: 'page-filters', title: '이 버튼들로 목록을 좁힙니다', body: '국내 진행 · 해외 진행 · 예정 · 평가 · 준공 중 하나를 고릅니다. 홈 현장 리스트와 같은 묶음입니다. 괄호 안 숫자는 그것을 골랐을 때 남는 현장 수입니다.' },
    ],
    tip: '현장 카드를 누르면 그 현장의 상세(현장 조회)로 넘어갑니다. 상세는 권한이 따로 있어, 열리지 않으면 관리자에게 요청하세요.',
  },
  // 하위 「진행」은 바로 위와 <b>같은 화면</b>이다(주소만 다르다 · V303). 대본을 통째로 베끼지 않고
  // 짧게 둔다 — 같은 말을 두 곳에 적으면 한쪽만 고쳐지는 날이 온다.
  {
    path: '/sites/status/active',
    label: '현장 현황 · 진행',
    what: '지금 짓고 있는 현장들입니다. 「현장 현황」을 눌렀을 때와 같은 화면입니다.',
    tip: '아직 수주 전이거나 착공 전인 프로젝트는 옆의 「입찰」에 있습니다. 착공신고필증 교부일을 적으면 그때 이 목록으로 올라옵니다.',
  },
  {
    path: '/sites/status/bids',
    label: '현장 현황 · 입찰',
    what: '발주처 공고를 보고 <b>우리가 투찰</b>한 뒤 계약서를 돌려 보고, 날인하고 착공 서류를 모으는 <b>수주 전 구간</b>을 한 화면에서 봅니다. 착공신고필증을 받으면 그 프로젝트는 여기서 빠지고 「진행」으로 넘어갑니다. 반대 방향 — 우리가 협력사에게 물어서 받는 일 — 은 옆의 「견적」입니다.',
    actions: '[프로젝트 등록]으로 새 입찰 건을 세웁니다. 현장 등록과 같은 일이라 여기서 세운 이름이 그대로 현장명이 됩니다.',
    spots: [
      {
        target: null,
        title: '맨 위 세 칸이 단계입니다',
        body: '<b>입찰 · 계약 · 착공</b> 순서로 서고, 칸 안의 이름표가 지금 그 단계에 있는 프로젝트입니다. 숫자는 그 단계의 건수입니다. 비어 있는 단계도 칸은 그대로 서 있습니다 — 「계약 건이 없다」와 「계약 단계라는 게 없다」는 다른 말입니다.',
      },
      {
        target: null,
        title: '단계는 날짜가 옮깁니다',
        body: '단계를 바꾸는 버튼은 없습니다. 상세 창의 <b>착공 일정</b>에 <b>계약 날인일</b>을 적으면 계약으로, <b>착공신고필증 교부일</b>을 적으면 진행으로 올라갑니다. 국내 현장은 분류도 함께 바뀝니다(국내계약현장 → 국내진행현장). 해외는 옮겨 앉힐 분류가 없어 사람이 현장 관리에서 고릅니다.',
      },
      {
        target: null,
        title: '줄을 누르면 그 프로젝트의 상세가 열립니다',
        body: '<b>프로젝트 · 공고 · 금액 · 결과 · 착공 일정 · 개요/비고</b> 묶음으로 서 있습니다. 공고번호·공고일·현장설명회·투찰 마감은 <b>현장설명서와 입찰안내서에 적힌 값을 그대로</b> 옮겨 적는 칸이고, 금액 넷(기초금액·추정가격·입찰보증금·투찰금액)도 마찬가지입니다 — 이 화면은 어떤 숫자도 스스로 계산하지 않습니다.',
      },
      {
        target: null,
        title: '계약서 검토는 부서별로 받습니다',
        body: '상세 창 안에서 부서를 고르고 <b>[의견 요청]</b>을 누르면 그 부서 줄이 생깁니다. 줄에 의견을 적고 [저장]하면 누가 언제 적었는지가 함께 남습니다. 결재로 남겨야 하는 의견은 <b>[전자결재로 올리기]</b>를 누르세요 — 협조전 서식에 수신 부서와 적어 둔 의견이 채워진 채로 기안 화면이 열립니다.',
      },
      {
        target: null,
        title: '착공 단계에서는 서류 목록이 함께 뜹니다',
        body: '계약 날인 뒤(착공 단계)에는 상세 창 안에 <b>착공서류 체크리스트</b>가 붙습니다. 현장 조회 › 건축허가&착공 탭에서 쓰던 그 목록이고, 같은 자리에 쌓입니다 — 두 화면이 서로 다른 목록을 들고 있지 않습니다.',
      },
    ],
    tip: '삭제는 <b>입찰 단계에서만</b> 됩니다. 계약·착공으로 넘어간 프로젝트를 지우면 그 현장에 쌓인 문서와 착공 서류까지 함께 사라지기 때문입니다.',
  },
  // 하위 「입찰」은 바로 위와 <b>같은 화면</b>이다(주소만 다르다 · 2026-09-30). 대본을 통째로
  // 베끼지 않고 짧게 둔다 — 진행(`/sites/status/active`)이 부모와 같은 자리에서 한 것과 같다.
  {
    path: '/sites/status/bids/tender',
    label: '현장 현황 · 입찰',
    what: '발주처 공고에 우리가 투찰하는 쪽입니다. 「입찰」을 눌렀을 때와 같은 화면입니다.',
    tip: '협력사에게 견적·입찰을 물어서 받는 일은 옆의 「견적」에 있습니다 — 같은 「입찰」이라는 말이지만 방향이 반대입니다.',
  },
  {
    path: '/sites/status/rfqs',
    label: '현장 현황 · 견적',
    what: '<b>협력사에게 견적·입찰을 물어서 받는</b> 자리입니다. 옆의 「입찰」과 방향이 반대입니다 — 저쪽은 발주처 공고에 우리가 투찰하는 쪽이고, 여기는 우리가 묻고 협력사가 냅니다. 전 현장의 공고가 한 표에 서고, 줄을 누르면 그 공고의 내역·초청업체·응찰이 아래에 펼쳐집니다.',
    actions: '[공고 만들기]로 공고를 세웁니다. <b>현장을 먼저 고르는 칸</b>이 맨 위에 있습니다 — 여러 현장을 한 화면에서 돌리는 자리라 어느 현장의 공고인지가 여기서 정해집니다.',
    spots: [
      {
        target: 'rfq-list',
        title: '공고 한 건이 도는 순서',
        body: '[공고 만들기] → <b>내역</b>·<b>초청업체</b>를 채우고 → <b>[공고]</b>를 누릅니다. 공고한 뒤에는 방식·마감을 못 고칩니다 — 고쳐야 하면 [취소]하고 다시 냅니다.',
      },
      {
        target: 'rfq-list',
        title: '금액은 마감까지 봉인됩니다',
        body: '방식이 <b>입찰</b>이면 마감 전에는 <b>우리도 금액을 못 봅니다</b>. 「누가 냈나」까지만 보이고, <b>[개찰]</b>을 누른 뒤에 열립니다. 개찰은 마감 시각이 지나야 눌리고, 누가 언제 열었는지가 남습니다. 방식이 <b>견적</b>이면 봉인이 없습니다.',
      },
      {
        target: 'rfq-detail',
        title: '낙찰하면 하도급 계약이 함께 섭니다',
        body: '응찰 표에서 <b>[낙찰]</b>을 누르면 그 업체로 하도급 계약 행이 하나 생기고, 그 현장의 하도급관리로 가는 버튼이 함께 뜹니다. 이미 손으로 넣어 둔 계약이 있으면 창 안에서 <b>「계약 행을 함께 만들기」</b>를 끄세요 — 같은 공사가 두 줄이 됩니다.',
      },
    ],
    tip: '표의 <b>「마감」 딱지</b>는 상태가 아니라 시각입니다. 「공고중」인데 마감 딱지가 붙어 있으면 시각이 지난 것이고, 그때부터 개찰을 누를 수 있습니다.',
  },
  {
    path: '/office/dashboard',
    label: '현장 전체 대시보드',
    /*
      대본이 <b>탭 안까지 들어간다</b> — 현장 조회(`/sites/pmis`)와 같은 방식이다(사용자 지시
      2026-08-28). 탭 줄만 밝히고 「넷이 있습니다」로 끝내면, 정작 이 화면에서 가장 자주 묻는
      <b>「이 숫자는 어디서 오는가 · 틀렸으면 어디서 고치는가」</b> 에 끝내 답하지 못한다.

      <p>이 화면은 <b>아무것도 입력하지 않는</b> 화면이다. 그래서 걸음마다 「여기서 고치세요」가
      아니라 <b>「이 값은 어느 화면에서 온다」</b>를 말한다 — 대시보드에서 숫자가 비어 있을 때
      사람이 실제로 해야 하는 일은 그 원천 화면으로 가는 것뿐이다.

      <p>{@code openTab} 이 걸음마다 그 탭을 대신 연다(`lib/tour/tabs`). 주소(`?tab=`)는 건드리지
      않아 안내가 끝나면 보던 탭으로 돌아온다.
    */
    what: '전 현장을 한 화면에 모아 봅니다. 여기서는 <b>보기만</b> 하고 아무것도 입력하지 않습니다 — 숫자는 각 현장이 자기 화면에 넣은 것을 그대로 더한 것입니다. 탭을 하나씩 열어 가며 짚어 드릴게요.',
    tabs: '개요 · 매출·원가·수금 · 주요자재 · 협력업체 평가. 처음 열리는 「개요」가 전체를 한눈에 보여 주고, 나머지가 그중 하나를 파고듭니다. 연락처는 이 화면에서 나가 제 화면이 되었습니다 — 현장 현황 머리말의 [연락처]입니다.',
    actions: '[현장 현황]으로 현장 목록에 돌아가고, 매출·원가·수금 탭에서는 [Excel]·[PDF]가 함께 나옵니다 — 보고 있던 기간·상태 그대로 나갑니다.',
    spots: [
      {
        target: 'page-filters',
        title: '기간·상태·단위는 탭을 옮겨도 그대로',
        body: '<b>‹ ›</b> 로 달을 한 칸씩 옮기고, 가운데 「2026년 8월」을 누르면 연·월을 골라 한 번에 건너뜁니다. 진행 상태와 금액 단위(억원·백만원)도 이 줄에서 바꿉니다. 협력업체 평가 탭에는 기간도 금액도 없어 이 줄이 사라지고, 주요자재 탭에서는 <b>상태</b>만 빠집니다 — 그 탭의 현장 상태는 표 옆 칩에 있습니다.',
      },
      {
        openTab: 'office-tab-overview',
        target: 'page-summary',
        title: '개요 — 맨 위 요약 줄',
        body: '현장 수 → 평균 공정률 → 돈 → 주요 현장 순입니다. 예정보다 뒤처진 공정률과 적자는 빨갛게 섭니다. 맨 오른쪽 「주요 현장」은 공정 지연·원가율 초과·마감 지연·준공 임박에 걸린 현장 수이고, 누르면 아래 표가 그 현장들만 보입니다.',
      },
      {
        openTab: 'office-tab-overview',
        target: 'overview-table',
        title: '개요 — 현장 리스트',
        body: '한 줄이 현장 하나입니다. 예정보다 뒤처진 공정률과 목표를 넘긴 원가율은 빨갛게 섭니다. 머리칸을 누르면 그 항목으로 정렬되고, 다시 누르면 방향이 뒤집힙니다. 줄을 누르면 그 현장 조회로 갑니다.',
      },
      {
        openTab: 'office-tab-overview',
        target: null,
        title: '개요 — 값이 비어 있다면',
        body: '공정률은 <b>주간회의</b>, 매출·원가·수금은 그 현장 <b>원가관리</b> 탭, 자재는 <b>주요자재관리</b> 탭에서 옵니다. 여기가 비었다면 그 화면에 아직 안 들어온 것입니다.',
      },
      {
        openTab: 'office-tab-office',
        target: 'finance-summary',
        title: '매출·원가·수금 — 기간 요약',
        body: '고른 달(또는 해)에 일어난 매출·원가·이익과 착공 이후 누계를 함께 봅니다. 매출은 <b>공정률 × 계약금액</b>입니다.',
      },
      {
        openTab: 'office-tab-office',
        target: 'finance-table',
        title: '매출·원가·수금 — 현장별',
        body: '머리칸을 누르면 그 기준으로 정렬됩니다. 원가율이 목표를 넘긴 현장은 빨갛고, 마감이 밀린 현장은 「N개월 지연」으로 섭니다(기한은 익월 10일).',
      },
      {
        openTab: 'office-tab-material',
        target: 'material-usage',
        title: '주요자재 — 소진율',
        body: '기준수량(수량산출서) 대비 반입·사용 누계입니다. 레미콘은 ㎥, 철근은 ton 이라 <b>현장 × 자재</b>로 한 줄입니다 — 현장 하나로 더하지 않습니다. 위 카드는 <b>자재</b>를 센 것이라 전 현장이 들어가고, 아래 표만 옆의 <b>현장 상태</b> 칩으로 갈라 봅니다(기본 진행).',
      },
      /*
        잡자재 블록(`material-requests`)은 <b>걸음으로 세우지 않는다</b>(사용자 지적 2026-08-28).

        화면에서 그것을 접어 둔 이유가 「비중이 낮아서」인데, 안내가 거기에 한 걸음을 통째로 쓰면
        <b>화면이 낮춘 무게를 안내가 도로 올린다</b>. 게다가 탭 이름이 「주요자재」라, 그 탭을 설명하며
        잡자재를 짚는 것은 앞뒤가 맞지 않는다 — 접힌 줄에 집행액·미정산이 이미 적혀 있어 눈에 걸릴
        사람은 걸린다.
      */
    ],
    /*
      권한 <b>키 이름을 적지 않는다</b>(사용자 지적 2026-08-28). `office_dashboard`·`site_pmis_material`
      같은 것은 우리끼리 쓰는 이름이고, 안내를 읽는 사람은 그 글자를 봐도 <b>자기가 무엇을 해야
      하는지</b>를 알 수 없다 — 오히려 화면이 고장 난 것처럼 읽힌다. 사람이 할 수 있는 일은 하나뿐이라
      (관리자에게 요청) 그 한 줄만 남긴다. 키가 필요한 사람은 역할·권한 관리 화면에서 본다.
    */
    tip: '탭이나 표가 통째로 안 보이면 대개 권한입니다 — 탭마다 볼 수 있는 사람이 다르게 지정되어 있습니다. 관리자에게 요청하세요.',
  },
  {
    path: '/sites/pmis',
    label: '현장 조회',
    /*
      대본이 <b>탭 안까지 들어간다</b>(사용자 지시 2026-08-27).

      1차로 고쳤을 때는 걸음이 탭 <b>버튼</b>을 하나씩 짚으며 「이 탭은 무엇을 하는 곳입니다」를
      말했다. 그것으로는 모자랐다 — 사람이 알고 싶은 것은 <b>어디를 누르면 무엇이 되는가</b> 이고,
      탭 줄만 밝히는 안내는 그 물음에 끝내 답하지 못한다. 투어 중에는 눌러 볼 수도 없다.

      그래서 걸음마다 {@link GuideSpot.openTab} 으로 <b>그 탭을 대신 열고</b>, 탭 안의 자리
      (`docslot-ai`·`progress-rollup`·`office-sheet` …)를 짚는다. 화면이 실제로 그 탭으로 바뀌므로
      「이 버튼을 누르면 …」 이 눈앞의 버튼을 가리킨다. 주소(`?tab=`)는 건드리지 않아 안내가 끝나면
      보던 탭으로 돌아온다.

      <p><b>문구는 한두 문장으로 끊는다</b>(사용자 지시 2026-08-27). 자리를 짚으면 화면이 이미
      절반을 말하므로 카드가 길 필요가 없다 — 길면 읽다 지쳐 안내를 닫아 버리고, 그러면 짚어 준
      자리도 함께 사라진다. 배경·설계 이유는 여기 주석에 남기고 카드에는 <b>지금 할 일</b>만 적는다.

      <p>덤으로 권한 판정이 저절로 된다 — 탭 버튼이 없는 사람에게는 그 탭의 걸음이 통째로 빠진다
      ({@link TourOverlay} 의 걸러내기).

      <p>작업일보 이야기는 걷어냈다. 전에는 이 안내의 절반이 「저장만으로는 공정율이 안 움직인다,
      [제출]까지 눌러라」 였는데, 작업일보·출역일보·검측은 목차에서 내려 탭조차 없다
      (`SitePmisPage` TABS 의 `hidden`). 없는 화면의 주의사항을 읽은 사람은 그 버튼을 찾아 헤맨다.

      <p><b>수행관리 넷은 직접 해 보는 걸음이다</b>(사용자 지시 2026-11-02). 공사일보·현장관리·공정관리·
      주요자재관리는 빈 현장에서 「어디부터 넣나」 가 막히는 자리라, 설명만 하지 않고 막을 걷어
      오늘 것을 실제로 넣게 한다(`interactive`). 저장은 진짜다 — 연습판을 따로 두지 않는다.
      수행관리 탭에 처음 들어가면 「작성 안내가 필요하신가요?」 를 묻고, [안내 보기] 면 그 탭의 걸음만
      편다(`SitePmisPage` 의 `GUIDED_TABS` · AppLayout 의 `guideTab`).
    */
    what: '현장 하나를 골라 계약·인허가·공정·원가·수금·자재·준공을 보는 곳입니다. 탭을 하나씩 열어 가며 짚어 드릴게요. 수행관리의 공사일보·현장관리·공정관리·주요자재관리에서는 <b>「직접 해 보기」</b> 안내가 뜨는 동안 화면을 그대로 누르고 적을 수 있고, 저장한 것은 실제 자료로 남습니다. 끝나면 보시던 탭으로 돌아옵니다.',
    // 「왼쪽에서 오른쪽이 공사 순서」는 만든 사람의 말이었다 — 읽는 사람이 알고 싶은 것은
    // «이 줄이 무엇이냐» 하나다(사용자 지적 2026-08-27).
    // 2026-09-28 에 <b>왼쪽 나무</b>가 됐다. 갈래 이름 셋만 말한다 — 그 아래 줄은 눌러 보면 알고,
    // 세어 주면 권한으로 빠진 사람이 없는 탭을 찾는다.
    tabsTitle: '왼쪽 목차',
    tabs: '공통 · 수행관리 · 매출·원가·수금 세 갈래입니다. 갈래 이름을 누르면 접히고, 옅게 보이는 줄은 아직 열지 않은 자리입니다.',
    actions: '[목록]으로 돌아가고, [수정]으로 현장 정보를 고치고, [자료 입력]으로 자료를 넣습니다. 현장·분류가 맞춰져 열리니 파일만 넣으면 됩니다.',
    spots: [
      {
        target: 'page-summary',
        title: '현장 머리 — 탭을 옮겨도 그대로',
        body: '사진은 눌러 바꾸고(권한 필요), 오른쪽 [대화방]·[NAS]는 어느 탭에서나 열립니다.',
      },
      {
        openTab: 'pmis-tab-overview',
        // 개요는 목차에 버튼이 없다(들어오면 처음 보는 화면 · `SiteTabTree`) — 권한은 여기서 적는다.
        requires: { menuKey: 'site_status', action: 'view' },
        target: 'overview-summary',
        title: '개요 — 매출·원가·수금',
        body: '원가관리 탭에 넣은 값을 그대로 접은 줄입니다. 원가 권한이 없으면 이 줄은 아예 없습니다.',
      },
      {
        openTab: 'pmis-tab-overview',
        // 개요는 목차에 버튼이 없다(들어오면 처음 보는 화면 · `SiteTabTree`) — 권한은 여기서 적는다.
        requires: { menuKey: 'site_status', action: 'view' },
        target: 'overview-monthly',
        title: '개요 — 월간 주요일정',
        // 「이 현장으로 잠긴다」는 만든 사람의 말이었다 — 무엇이 잠기는지 아무도 모른다.
        body: '이 현장의 일정만 모아 보는 달력입니다. 날짜를 누르면 그날 일정을 바로 적습니다.',
      },
      {
        openTab: 'pmis-tab-overview',
        // 개요는 목차에 버튼이 없다(들어오면 처음 보는 화면 · `SiteTabTree`) — 권한은 여기서 적는다.
        requires: { menuKey: 'site_status', action: 'view' },
        target: 'overview-details',
        title: '개요 — 현장 제원',
        body: '여기서는 못 고칩니다 — 오른쪽 위 [수정]에서 바꾸고, 담당자는 인력배치표에서 옵니다.',
      },
      {
        openTab: 'pmis-tab-overview',
        // 개요는 목차에 버튼이 없다(들어오면 처음 보는 화면 · `SiteTabTree`) — 권한은 여기서 적는다.
        requires: { menuKey: 'site_status', action: 'view' },
        target: 'overview-documents',
        title: '개요 — 현장 문서함',
        body: '이 현장에 올라온 최근 자료입니다. [문서함 열기]로 이 현장 폴더 전체를, [NAS 열기]로 현장 NAS 를 엽니다.',
      },
      {
        openTab: 'pmis-tab-contract',
        // 계약서 탭은 요약·물어보기가 목록 위에 붙박이로 다시 섰다(2026-10-15). 버튼이 없으니 카드를 짚는다.
        target: 'docslot-ai',
        title: '계약서 — AI 요약',
        body: '탭을 열면 계약금액·공사기간·지체상금율·하자담보 조건을 뽑아 보여 줍니다. 바로 아래 칸에서 그 서류에 직접 물어볼 수도 있습니다.',
      },
      {
        openTab: 'pmis-tab-contract',
        target: 'docslot-upload',
        title: '계약서 — 넣는 길은 둘',
        body: '새로 넣으면 [계약서 올리기], 자료실·NAS 에 <b>이미 있으면</b> [이미 올린 파일 고르기]. 두 번 올리지 않으려고 나란히 뒀습니다.',
      },
      {
        openTab: 'pmis-tab-contract',
        target: 'docslot-list',
        title: '계약서 — 목록',
        body: '파일 이름을 누르면 앱 안에서 열립니다. <b>[제거]는 자료실 원본까지 지웁니다</b> — 분류만 틀렸다면 자료실에서 고치세요.',
      },
      {
        openTab: 'pmis-tab-contacts',
        target: 'contacts-toolbar',
        title: '연락처 — 검색과 추가',
        body: '이름·회사·업무·번호가 한꺼번에 걸립니다. 협력업체 담당자는 [업체 연락처 추가]로 여기에 적습니다.',
      },
      {
        openTab: 'pmis-tab-contacts',
        target: 'contacts-table',
        title: '연락처 — 표',
        body: '관계사·업체 수첩·우리 인원이 한 표에 모입니다. 번호를 누르면 바로 걸리고, [수정]·[삭제]는 <b>수첩 줄에만</b> 붙습니다.',
      },
      /*
        ── 수행관리 작성 안내 (사용자 지시 2026-11-02) ─────────────────────────────────
        「작성 안내가 필요하신가요?」 의 [안내 보기] 와 수행관리 탭의 ? 가 이 걸음들을 그 탭 것만 추려 편다
        (AppLayout `guideTab`). 화면 위의 <b>진짜 버튼과 칸</b>을 차례로 짚고(추가 → 입력 → 저장 → 수정 →
        삭제), `interactive` 라 막이 없어 사람이 그 자리에서 직접 누르고 적는다. 저장은 진짜다.
        `advanceWhen` 이 있는 걸음은 누른 결과(새 줄·창)가 나타나면 저절로 넘어간다.
        짚을 버튼이 없는 사람(권한 없음·아직 줄이 없음)에게는 카드만 가운데 뜬다 — 이유는 눌렀을 때 서버가 답한다.
      */
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-start',
        act: 'click',
        advanceWhen: 'launch-editor',
        title: '건축허가&착공 — 착공 준비 시작',
        body: '처음 쓰는 현장은 여기서 시작합니다. <b>[착공 준비 시작]</b>을 누르면 이 자리에 착공 준비 칸이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-edit',
        act: 'click',
        advanceWhen: 'launch-editor',
        title: '건축허가&착공 — 착공 준비 편집',
        body: '이미 시작한 현장은 카드의 <b>[편집]</b>에서 같은 칸을 엽니다. 날인일 · 착공 예정일 · 세움터 · 소방 · 인계 · 담당을 여기서 고칩니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-editor',
        title: '착공 준비 — 입력',
        body: '<b>계약 날인일</b>(필수) 하나면 시작됩니다 — 이 날부터 착공까지가 준비 기간입니다. <b>착공 예정일</b>을 적으면 착공 D-day 와 서류 기한을 그 날에서 거꾸로 셉니다. 세움터 개설일 · 접수번호, 소방착공 신고 기한 · 신고일, 현장 인계일, 착공 준비 담당자는 아는 만큼 적습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-editor-save',
        act: 'click',
        title: '착공 준비 — 저장',
        body: '<b>[저장]</b>을 누르면 착공 준비 카드(서류 완료율 · 미비 서류 · 소방착공 신고 · 인원 선임)가 섭니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-add',
        act: 'click',
        advanceWhen: 'launch-add-form',
        title: '착공서류 — 추가',
        body: '챙길 서류를 한 줄씩 넣습니다. 지금 <b>[+ 서류 추가]</b>를 눌러 보세요. 회사 기준표에 있는 서류를 한꺼번에 깔려면 옆의 [기준표에서 가져오기]를 씁니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-add-form',
        title: '착공서류 — 입력',
        body: '<b>서류명</b>(예: 착공신고서)을 적고, 이 서류를 낼 <b>주체</b>(발주자 · 설계사 · 감리자 · 시공사), 우리 쪽 <b>담당자</b>, 언제까지인지 <b>기한</b>을 적습니다. 서류명만 있어도 넣을 수 있습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-add-save',
        act: 'click',
        title: '착공서류 — 저장',
        body: '<b>[추가]</b>를 누르면 아래 목록과 일정표에 한 줄이 생기고, 위 주체별 진행률이 다시 그려집니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-row-status',
        act: 'choose',
        title: '착공서류 — 수정(상태 바꾸기)',
        body: '서류가 움직일 때마다 이 칸에서 상태를 바꿉니다. <b>대기 → 작업중 → 제출 → 완료</b>. 고르는 즉시 저장되고, 완료로 바꾸면 실적일이 오늘로 찹니다. 주체 · 담당도 같은 줄에서 바로 바꿉니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-row-delete',
        title: '착공서류 — 삭제',
        body: '잘못 넣은 줄은 줄 끝 <b>[삭제]</b>로 지웁니다. 지우기 전에 한 번 묻습니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-documents>>기준표에서 가져오기',
        title: '착공서류 — 기준표에서 가져오기',
        body: '착공 때 늘 챙기는 회사 기준표에서 <b>이 현장에 필요한 것만 골라</b> 한 번에 깝니다. 이미 넣은 서류는 표시되어 두 번 들어가지 않습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-documents>>주체별',
        title: '착공서류 — 주체별 · 상태별',
        body: '목록을 <b>주체별</b>(누가 낼 서류인가)로 묶거나 <b>상태별</b>(어디까지 왔나)로 묶어 봅니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-documents>>서류 · 주체 · 담당 찾기',
        title: '착공서류 — 찾기',
        body: '서류명 · 주체 · 담당 이름으로 줄을 찾습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-documents>>붙이기',
        title: '착공서류 — 파일 붙이기',
        body: '줄마다 <b>[붙이기]</b>로 그 서류 파일을 올립니다. 올린 파일은 자료실 같은 분류에 쌓이고, 표에서 바로 열립니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'launch-staffing',
        title: '건축허가&착공 — 법정인원·선임',
        body: '공석과 미선임 · 미신고를 세어 줍니다. 인력배치표에서 오는 숫자라 <b>배치를 바꾸면</b> 사라집니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-permit',
        interactive: true,
        target: 'docslot-upload',
        title: '건축허가&착공 — 허가 서류함',
        body: '건축허가서 · 착공신고필증을 <b>[허가 서류 올리기]</b>로 올려 두면 허가 조건과 착공 조건을 이 화면에서 바로 봅니다. 자료실에 이미 있으면 [이미 올린 파일 고르기].',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-permit',
        target: 'launch-chips',
        title: '건축허가&착공 — 세움터·소방·인계·담당',
        body: '기한을 넘겼거나 담당이 없으면 빨갛게 섭니다. 고칠 수 있는 사람에게는 [세움터 · 인계 · 담당 편집]이 보입니다.',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-vendors',
        title: '공사일보 — 협력사 작업일보',
        body: '협력사가 포털에 쓴 그날 작업일보 현황입니다. 새 일보를 열면 그날 협력사가 적은 <b>출역 · 작업</b>을 바탕으로 칸이 채워집니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        requires: { menuKey: 'site_pmis_daily', action: 'create' },
        target: 'daily-report-new',
        act: 'click',
        advanceWhen: 'daily-report-editor',
        title: '공사일보 — 추가',
        body: '달력이 이 현장의 일보 목록입니다. 하루에 한 장이고, 날짜를 누르면 그날 일보가 열립니다. 지금 <b>[오늘 일보]</b>를 눌러 보세요 — 오늘 양식이 아래에 열리면 다음 안내로 넘어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        requires: { menuKey: 'site_pmis_daily', action: 'create' },
        target: 'daily-block-labor',
        title: '공사일보 — 입력: 인력 투입',
        body: '칸을 눌러 바로 적습니다. 한 줄에 <b>공정 · 업체 · 금일 인원</b>을 적고, 공정이 여럿이면 줄을 더 적습니다. 엑셀에서 여러 칸을 복사해 붙여 넣어도 됩니다. 어제 일보가 있으면 위 [어제 일보 가져오기]로 채운 뒤 고치는 것이 빠릅니다.',
        placement: 'right',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        requires: { menuKey: 'site_pmis_daily', action: 'create' },
        target: 'daily-block-work',
        title: '공사일보 — 입력: 작업 내역',
        body: '인력에 적은 공정마다 줄이 생깁니다. 그 줄의 <b>금일</b>에 오늘 한 일, <b>명일</b>에 내일 할 일을 적습니다. 위 [AI 초안]에 말하듯 적으면 빈 칸을 채워 줍니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>어제 일보 가져오기',
        title: '공사일보 — 어제 일보 가져오기',
        body: '바로 앞 일보에서 공정 · 업체 이름, 직원 배치, 결재선을 가져오고, 그날의 <b>명일 작업이 오늘의 금일 작업</b>으로 옮겨 옵니다. 인원 · 수량은 비워 두니 오늘 숫자만 적으면 됩니다. 이미 적은 칸은 덮지 않습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>AI 초안',
        title: '공사일보 — AI 초안',
        body: '누르면 아래에 글 상자가 열립니다. 「오늘 지하 2층 슬래브 배근 했고 철근공 8명 들어왔어요」 처럼 <b>말하듯 적고 [빈 칸 채우기]</b>를 누르면 인력 · 작업 칸을 채워 줍니다. 이미 적은 칸은 그대로 둡니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>샘플로 채우기',
        title: '공사일보 — 샘플로 채우기',
        body: '빈 칸을 시험용 값으로 채워 양식이 어떻게 찍히는지 봅니다. 저장하지 않으니 [미리보기]로 본 뒤 [닫기]로 버려도 됩니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        requires: { menuKey: 'site_pmis_daily', action: 'create' },
        target: 'daily-report-save',
        act: 'click',
        title: '공사일보 — 저장',
        body: '<b>[저장]</b>을 누르면 달력의 오늘 칸에 작업 내역 첫 줄이 뜹니다. 저장하지 않고 다른 날을 누르면 버릴지 먼저 묻습니다. [미리보기]로 쪽을 보고 [PDF] · [엑셀]로 내려받습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>미리보기',
        title: '공사일보 — 미리보기',
        body: '종이에 찍힐 모양 그대로 쪽을 보여 줍니다. 칸이 넘치면 여기서 먼저 보입니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>PDF',
        title: '공사일보 — PDF · 엑셀',
        body: '저장한 일보를 회사 양식 그대로 <b>[PDF]</b> · <b>[엑셀]</b>로 내려받습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-editor>>양식 편집',
        title: '공사일보 — 양식 편집',
        body: '인력 · 작업 · 자재 · 장비 · 사진 같은 덩이를 <b>켜고 끄고 순서를 바꿉니다</b>. 덩이 아래 손잡이를 끌면 높이가 정해집니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        requires: { menuKey: 'site_pmis_daily', action: 'create' },
        target: 'daily-report-submit',
        title: '공사일보 — 결재 올리기',
        body: '다 적었으면 <b>[결재 올리기]</b>로 결재선에 넘깁니다. 반려되면 사유가 위에 뜨고, 고쳐서 다시 올립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-calendar',
        title: '공사일보 — 수정',
        body: '고칠 날은 <b>달력에서 그날</b>을 누르면 그 일보가 다시 열립니다. 칸을 고치고 [저장]합니다. 결재에 올린 일보를 고치면 작성중으로 돌아간다고 먼저 묻습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-daily',
        interactive: true,
        target: 'daily-report-delete',
        title: '공사일보 — 삭제',
        body: '열어 둔 일보를 지우려면 위 줄의 <b>[삭제]</b>를 누릅니다. 그날 사진도 함께 지워지고, 자재 전표는 남습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'site-permit-grid>>만료',
        title: '현장관리 — 상태 칩',
        body: '위 칩으로 <b>만료 · 30일 이내 · 날짜 확인 · 공사 종료 전 만료 · 유효</b>만 골라 봅니다. 숫자는 그 상태의 줄 수입니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'site-permit-grid>>표 전체에서 찾기',
        title: '현장관리 — 찾기',
        body: '옆의 열 고르기에서 찾을 열을 고르고 이 칸에 적으면 그 열에서만 찾습니다. 「전체 열」 이면 표 전체에서 찾습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'site-permit-grid>>다운로드',
        title: '현장관리 — 다운로드 · 인쇄',
        body: '<b>[다운로드]</b>는 지금 보이는 차례 그대로 엑셀로 내려받고, <b>[인쇄]</b>는 표를 그대로 인쇄하거나 PDF 로 저장합니다. 머리칸을 누르면 그 열로 정렬됩니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-edit',
        act: 'click',
        advanceWhen: 'field-permit-add',
        title: '현장관리 — 수정 열기',
        // 이 표는 읽는 표로 열린다(editToggle). [수정] 을 눌러야 칸과 [＋ 항목 추가] 가 선다 — 그래서 첫 걸음이다.
        body: '인허가 하나가 한 줄입니다. 이 표는 읽는 표로 열려 있어서, 넣을 때도 고칠 때도 머리 줄 오른쪽 끝 <b>[수정]</b>부터 누릅니다. 눌러 보세요 — [＋ 항목 추가]가 나타나면 다음 안내로 넘어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-add',
        act: 'click',
        advanceWhen: 'field-permit-draft',
        title: '현장관리 — 추가',
        body: '<b>[＋ 항목 추가]</b>를 눌러 보세요. 표 맨 위에 빈 줄이 열리면 다음 안내로 넘어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-draft',
        title: '현장관리 — 입력',
        body: '빈 줄에 <b>구분</b>(도로점용 · 가설건축물 …) · <b>내용</b> · <b>관할 기관</b> · <b>신고일(허가일)</b> · <b>유효기간</b>을 적습니다. 날짜는 2026.08.14 처럼 적어도 됩니다. 엑셀에서 여러 줄을 복사해 이 줄에 붙여 넣으면 한꺼번에 들어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-draft-save',
        act: 'click',
        title: '현장관리 — 저장',
        body: '줄 끝 <b>[저장]</b>을 누르면 표에 들어갑니다. 남은 날과 상태(만료 · 30일 이내 · 유효)는 서버가 유효기간으로 계산합니다. 빈 줄은 그대로 남아 이어서 더 넣을 수 있습니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-row',
        title: '현장관리 — 수정',
        body: '이미 있는 줄은 [수정]이 켜진 동안 <b>칸을 눌러 바로 고칩니다</b>. 고친 줄에는 줄 끝에 [저장]이 섭니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-save',
        act: 'click',
        title: '현장관리 — 고쳐 저장',
        body: '여러 줄을 고쳤으면 머리 줄의 <b>[고친 N줄 저장]</b>으로 한 번에 저장합니다. 그만두려면 옆의 [취소] — 읽는 표로 돌아갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-field',
        interactive: true,
        target: 'field-permit-open',
        title: '현장관리 — 첨부와 삭제',
        body: '줄 끝 <b>[첨부]</b>를 누르면 그 줄의 창이 열립니다. 허가증 파일을 올리고 이력을 보며, 줄을 지울 때는 그 창 위의 <b>[삭제]</b>를 누릅니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-views>>계약 공정표',
        title: '공정관리 — 공정표 세 가지',
        body: '공정표는 <b>계약 · 목표 · 단기</b> 세 가지로 나눠 짭니다. 여기서 고른 공정표가 아래에 섭니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet-open',
        act: 'click',
        advanceWhen: 'progress-sheet',
        title: '공정관리 — 공정표 열기',
        body: '위 줄의 현장 전체 공정율은 주간회의에 보고한 값입니다. 공정은 공정표에서 넣습니다 — <b>[공정표 만들기]</b>를 눌러 보세요(이미 있으면 [공정표 조회]).',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet-add',
        act: 'click',
        title: '공정관리 — 추가',
        body: '<b>[＋ 공정 추가]</b>를 누를 때마다 표 아래에 빈 줄이 하나씩 붙습니다. 엑셀 공정표가 있으면 같은 줄의 가져오기로 한 번에 넣습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet',
        title: '공정관리 — 입력',
        body: '빈 줄에 <b>공정 이름</b>과 <b>시작일 · 종료일</b>을 적습니다. 물량으로 진척을 잴 공정은 <b>단위 · 계획수량</b>까지 적으면 나중에 적는 실적수량으로 진행(%)이 계산됩니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet-save',
        act: 'click',
        title: '공정관리 — 저장',
        body: '줄을 다 적었으면 <b>[N줄 저장]</b>을 한 번 누릅니다. 고친 칸도 같은 버튼으로 저장됩니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet>>파일에서 가져오기',
        title: '공정관리 — 파일에서 가져오기',
        body: '가진 엑셀 공정표를 고르면 시트 구조를 읽어 <b>미리보기</b>를 보여 주고, 확인한 뒤에 들어갑니다. 바로 저장되지 않습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet>>양식 추출',
        title: '공정관리 — 다운로드 · 양식 추출',
        body: '<b>[다운로드]</b>는 지금 공정표를 내려받고, <b>[양식 추출]</b>은 빈 양식을 내려받습니다 — 양식에 적어 [파일에서 가져오기]로 넣으면 됩니다. [엑셀로 열기]는 자료실의 이 현장 공정표 엑셀을 엽니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet>>전체 보기',
        title: '공정관리 — 전체 보기 · 모두 접기',
        body: '<b>[전체 보기]</b>로 표를 화면 가득 펼치고, <b>[모두 접기]</b>로 상위 공정만 남겨 큰 줄기를 봅니다. 확대 · 축소는 옆의 － ＋ 입니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet>>최종 저장',
        title: '공정관리 — 최종 저장',
        body: '공정표를 다 짰으면 <b>[최종 저장]</b>으로 굳힙니다. 굳힌 뒤에는 계획 칸을 고칠 수 없고 설계변경을 거쳐야 합니다. 실적은 계속 적습니다. 대신 누르지 않습니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet-delete',
        title: '공정관리 — 삭제',
        body: '줄 맨 앞의 <b>×</b>로 공정을 지웁니다. 그 공정의 진척 기록도 함께 지워지므로 먼저 묻습니다. 최종 저장으로 굳은 공정표에서는 지울 수 없습니다.',
        placement: 'right',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-sheet-open',
        act: 'click',
        advanceWhen: 'progress-list-edit',
        title: '공정관리 — 목록으로',
        body: '이제 오늘 실적을 적습니다. <b>[목록 보기]</b>를 눌러 목록으로 돌아가세요.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-list-edit',
        act: 'click',
        advanceWhen: 'progress-list-save',
        title: '공정관리 — 수정(오늘 실적)',
        body: '목록 머리의 <b>[수정]</b>을 누르면 실적 칸이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-table',
        title: '공정관리 — 실적 입력',
        body: '오늘 시작한 공정은 <b>실적시작일</b>에 오늘 날짜를, 오늘까지 한 양은 <b>실적수량</b>(누계)에 적습니다. 진행(%)과 예정대비가 계획수량 기준으로 다시 계산됩니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-list-save',
        act: 'click',
        title: '공정관리 — 실적 저장',
        body: '<b>[N줄 저장]</b>을 누르면 곡선에 실적이 얹힙니다. 줄을 누르면 그 공정의 창이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-progress',
        target: 'progress-curve',
        title: '공정관리 — 곡선',
        body: '계획 위에 실적을 얹어 앞섰는지 밀렸는지 봅니다.',
      },
      {
        openTab: 'pmis-tab-progress',
        interactive: true,
        target: 'progress-curve>>바 차트',
        title: '공정관리 — 바 차트',
        body: '곡선과 바 차트를 바꿔 봅니다. 바 차트에서는 <b>전체 · ±3개월 · CP</b> 로 줄을 거르고, 막대를 누르면 그 공정의 창이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-office',
        target: 'office-setup',
        title: '원가관리 — [설정 확정]부터',
        body: '공사기간·도급금액·목표원가율을 넣고 [설정 확정]을 눌러야 아래 표가 열립니다. 다시 여는 길은 설계변경뿐입니다.',
      },
      {
        openTab: 'pmis-tab-office',
        target: 'office-summary',
        title: '원가관리 — 요약',
        body: '누계로 본 매출·원가·수금입니다. 실적원가율이 목표를 넘기면 붉게 섭니다.',
      },
      {
        openTab: 'pmis-tab-office',
        target: 'office-toolbar',
        title: '원가관리 — 도구 줄',
        body: '연도와 금액 단위를 바꾸고, [Excel]·[PDF]로 <b>전 기간</b>을 한 장으로 추출합니다.',
      },
      {
        openTab: 'pmis-tab-office',
        target: 'office-sheet',
        title: '원가관리 — 표와 [마감]',
        body: '하늘색 칸이 입력 자리이고 치는 대로 임시저장됩니다. 달 머리의 <b>[마감]</b>을 눌러야 확정되며, 세금계산서가 없으면 마감이 막힙니다.',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-add',
        act: 'click',
        advanceWhen: 'sub-edit-trade',
        title: '하도급관리 — 추가',
        body: '공종 하나를 어느 업체에 얼마에 맡겼는지 한 줄로 둡니다. 지금 <b>[공종 넣기]</b>를 눌러 보세요. 넣는 창이 열리면 다음 안내로 넘어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-edit-trade',
        title: '하도급관리 — 입력: 공종',
        body: '공정표에 있는 공정이면 바로 위 <b>공종(공정관리)</b>에서 고르고, 없으면 여기 <b>공종 이름</b>에 적습니다. 공정을 고르면 발주 계획일이 그 공정 일정을 따라갑니다.',
        placement: 'right',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-edit-amount',
        title: '하도급관리 — 입력: 금액·기간',
        body: '<b>공사금액</b>(VAT 제외)과 옆의 <b>공사기간</b>을 적습니다. 업체는 견적 · 입찰에서 낙찰하면 채워집니다. 계약서 파일은 아래 첨부파일에 올립니다.',
        placement: 'right',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-edit-save',
        act: 'click',
        title: '하도급관리 — 저장',
        body: '창 위 <b>[저장]</b>을 누르면 하도급 표에 한 줄이 생깁니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-table',
        title: '하도급관리 — 수정',
        body: '[저장]하면 창이 닫히고 표에 줄이 섭니다(저장하지 않을 거면 창의 × 로 닫습니다). 고칠 줄은 표에서 <b>그 줄을 누르면</b> 같은 창이 열립니다. 고친 뒤 [저장]하고, 금액이 바뀌는 변경 · 정산은 창 위 [차수 넣기]로 그 줄 아래에 붙입니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-edit-delete',
        title: '하도급관리 — 삭제',
        body: '줄을 눌러 연 창 위의 <b>[삭제]</b>로 지웁니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-table>>다운로드',
        title: '하도급관리 — 다운로드 · 엑셀로 열기',
        body: '<b>[다운로드]</b>는 하도급 표를 내려받고, <b>[엑셀로 열기]</b>는 자료실의 이 현장 하도급 엑셀을 엽니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-subcontract',
        interactive: true,
        target: 'sub-vendor-eval>>펼치기',
        title: '하도급관리 — 협력업체 평가',
        body: '이 현장에서 일한 업체를 평가해 둡니다. 펼쳐서 <b>[평가 넣기]</b>로 적으면 전체 대시보드의 「협력업체 평가」 에 모입니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-subcontract',
        target: 'docslot-list',
        title: '하도급관리 — 계약서',
        body: '표 아래는 공종별 하도급 계약서 자리입니다. 계약서 탭과 분류가 갈라져 있어 같은 문서가 두 탭에 뜨지 않습니다.',
      },
      {
        openTab: 'pmis-tab-payment',
        target: 'payment-contract',
        title: '수금관리 — 도급기성(받는 것)',
        body: '[추가]로 회차·금액을 넣으면 누계와 기성율이 따라 계산되고, 상태가 청구 → 승인 → 완료로 넘어갑니다.',
      },
      {
        openTab: 'pmis-tab-payment',
        target: 'payment-subcontract',
        title: '수금관리 — 하도급기성(내는 것)',
        body: '협력업체가 낸 기성입니다. 표 모양은 같고 업체 열이 하나 더 붙습니다.',
      },
      {
        openTab: 'pmis-tab-payment',
        target: 'docslot-upload',
        title: '수금관리 — 표 아래는 근거 서류',
        body: '기성 청구서·세금계산서·입금증을 여기 두면 표의 숫자가 어디서 나왔는지 같은 화면에서 꺼냅니다.',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-setup',
        title: '주요자재관리 — 처음 한 번',
        // 「준비 중」은 만드는 사람의 말이다 — 쓰는 사람에게는 «지금 무엇을 할 수 있는가» 만 적는다.
        body: '표는 「층 × 자재」 라 자재와 층이 하나씩은 있어야 수량을 적습니다. 그 둘은 이 서랍에서 세웁니다. 자재나 층이 없는 현장은 서랍이 펼쳐진 채 열립니다. 수량산출서가 있으면 [수량산출서 등록]으로 한 번에 넣습니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-setup>>수량산출서 등록',
        title: '주요자재관리 — 수량산출서 등록',
        body: '「층별총집계표」 한 장을 올리거나 붙여 넣으면 <b>자재 × 층 기준수량</b>으로 읽어 표로 보여 줍니다. 확인하고 [확정]을 눌러야 들어갑니다. [엑셀로 열기]는 자료실의 이 현장 주요자재 엑셀을 엽니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-setup>>기준수량 · 층구간 펼치기',
        act: 'click',
        advanceWhen: 'mat-items-add',
        title: '주요자재관리 — 서랍 열기',
        // 자재·층이 이미 있는 현장은 서랍이 접혀 열린다 — 접혀 있으면 아래 추가 걸음들이 짚을 것이 없다(2026-11-02).
        body: '자재 · 층을 더 넣거나 고치려면 <b>「기준수량 · 동 · 층구간」</b> 서랍을 엽니다. 눌러 보세요 — 열리면 다음 안내로 넘어갑니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-items-add',
        act: 'click',
        advanceWhen: 'mat-items-draft',
        title: '주요자재관리 — 추가: 자재',
        body: '<b>[＋ 자재 추가]</b>를 눌러 보세요. 주요자재 표 맨 위에 빈 줄이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-items-draft',
        title: '주요자재관리 — 입력: 자재',
        body: '<b>구분</b>(레미콘 · 철근 …) · <b>자재명</b> · <b>규격</b>(25-24-150 · HD13) · <b>단위</b>(㎥ · ton)를 적고 줄 끝 [저장]을 누릅니다. 규격이 다르면 다른 자재입니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-setup>>＋ 동 추가',
        title: '주요자재관리 — 동',
        body: '동을 나누는 현장은 <b>[＋ 동 추가]</b>로 동(1동 · 지하주차장 …)을 먼저 만들고, 층을 그 동에 붙입니다. 동을 안 나누면 건너뜁니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-zones-add',
        act: 'click',
        advanceWhen: 'mat-zones-draft',
        title: '주요자재관리 — 추가: 층',
        body: '아래 층·구간 표의 <b>[＋ 층·구간 추가]</b>를 눌러 보세요.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-zones-draft',
        title: '주요자재관리 — 입력: 층',
        body: '<b>구분</b>(층 · 구간)과 <b>이름</b>(3F · B2F)을 적고 줄 끝 [저장]을 누릅니다. 동을 나누는 현장은 동을 먼저 고릅니다. 층별 기준수량은 서랍 맨 위 격자에 정미량으로 적고 제목 줄 [저장]을 누릅니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-grid',
        title: '주요자재관리 — 리비전과 표',
        body: '자재와 층이 생기면 맨 위에 「층 × 자재」 표가 섭니다. 왼쪽 세로 줄은 수량의 <b>단계(리비전)</b>입니다 — 단계를 고르면 그 기준수량으로 표가 바뀌고, 둘 이상이면 「전체」 로 견줘 봅니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-grid-edit',
        act: 'click',
        advanceWhen: 'mat-grid-save',
        title: '주요자재관리 — 수정(이번 달 반입)',
        body: '자재와 층이 생기면 맨 위에 「층 × 자재」 표가 섭니다. 그 표의 <b>[수정]</b>을 누르면 이번 달 칸이 열립니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'material-grid',
        title: '주요자재관리 — 반입 입력',
        body: '층 줄과 자재 열이 만나는 <b>반입</b> 칸에 이번 달 들어온 양을 적습니다. 반입은 달마다 쌓이므로 오늘 들어온 양은 더해서 적습니다. 쓴 양은 같은 줄의 <b>사용</b> 칸에 적습니다.',
        placement: 'top',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-grid-save',
        act: 'click',
        title: '주요자재관리 — 저장',
        body: '<b>[고친 N칸 저장]</b>을 누르면 반입 누계와 소진율이 다시 계산됩니다.',
        placement: 'bottom',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'mat-item-delete',
        title: '주요자재관리 — 삭제',
        body: '서랍의 자재 줄 끝 <b>[삭제]</b>로 지웁니다. 반입 · 사용 기록이나 기준수량이 붙은 자재는 서버가 막습니다 — 그 칸을 먼저 비우고 지웁니다. 층도 같은 방법입니다.',
        placement: 'left',
      },
      {
        openTab: 'pmis-tab-material',
        interactive: true,
        target: 'site-stock',
        title: '주요자재관리 — 보유 자산',
        body: '이 현장이 지금 갖고 있는 자산(재고)입니다. 자산관리 › 현장 자산 현황과 <b>같은 표</b>라 어디서 고쳐도 같습니다. 눌러서 펼칩니다.',
        placement: 'top',
      },
      // 준공 탭의 서류함 걸음은 서류함과 함께 지웠다(사용자 결정 2026-10-15).
      /*
        [쉬운 모드] 버튼을 짚던 걸음은 <b>버튼과 함께 지웠다</b>(사용자 지시 2026-09-23).

        기능은 그대로 있고 켜고 끄는 일만 AI Agent 로 옮겼다(「글씨 크게 해줘」). 없는 버튼을
        가리키는 걸음은 대상이 없어 어차피 조용히 빠지지만, 대본에 남겨 두면 다음 사람이
        「왜 안 뜨지」 를 버튼이 아니라 투어에서 찾는다.
      */
      /*
        <b>탭별 권한</b> — 마지막 걸음이자 이 대본에서 유일하게 권한을 <b>가진 사람</b>에게만 뜨는
        걸음이다(사용자 지시 2026-09-04).

        탭마다 볼 사람이 달라진 뒤(요청표 · V237) 「잘 됐는지」를 확인하려면 역할·권한 관리와
        부서 관리와 오픈 관리를 따로 열어 머릿속에서 이어 붙여야 했다. 세 화면 어디에도
        <b>「공정관리 탭」</b> 이라는 말은 없다 — 표를 이 화면으로 가져온 이유다.

        ★ `requires` 가 있어야 한다. 판을 여는 걸음은 대상이 <b>지금 없는 것이 정상</b>이라
          「없으면 빠진다」 규칙이 통하지 않는다({@link TourOverlay} 의 걸러내기) — 안 적으면
          권한 없는 사람에게 <b>짚을 자리 없는 카드</b>가 뜬다. 키는 점검표 자신이 보는 것과
          같은 것이라야 한다(`TabPermissionAudit` 의 `canAudit`).
      */
      {
        openPanels: ['pmis-permission-audit'],
        target: 'pmis-permission-audit',
        title: '탭별 권한 — 지금 누구에게 열려 있나',
        body: '탭마다 오픈 상태와, 소속만으로 열리는지, 어느 역할이 무슨 칸을 쥐고 있는지 한 줄로 폅니다. 관리자는 전부 지나가므로 여기 적힌 것은 <b>다른 사람</b>의 화면입니다.',
        requires: { menuKey: 'settings_roles', action: 'view' },
      },
    ],
    tip: '보이지 않는 탭은 권한 때문입니다 — 관리자에게 요청하세요. 자료는 어느 탭에서 올리든 자료실 한 곳에 쌓입니다. '
  },
  {
    /*
      공사일보 한 장으로 가는 짧은 주소(DOC-117 §5, {@code DailyReportRedirect}). 일보를 읽어
      현장 조회의 공사일보 탭으로 바로 넘기므로 이 대본은 화면에 뜨지 않는다. 라우트가 있는 한
      목록을 맞춰 보는 테스트가 이 줄을 요구한다.
    */
    path: '/sites/pmis/daily-report/:id',
    label: '공사일보',
    what: '이 주소로 들어오면 그 일보의 현장·날짜로 현장 조회의 <b>공사일보 탭</b>이 열립니다.',
  },
  {
    /*
      내 작업 일정(DOC-100). 메뉴 골격에 없고 홈 TO DO 위젯의 「전체보기」로 들어온다 —
      현장 대화방과 같은 방식이라 그 옆에 둔다.

      ★ 대본이 <b>빠져 있어 CI 가 섰다</b>(2026-09-30 · `pageGuides.test` 의 「모든 화면에 대본이
        있다」). 화면을 더할 때 이 파일을 함께 고치는 것이 규칙이고, 그 규칙을 테스트가 지킨다.
    */
    path: '/todos',
    label: '내 작업 일정',
    what: '홈의 <b>TO DO 위젯에서 「전체보기」</b>로 들어오는 화면입니다. 두 축이 <b>서로 다른 것</b>을 보여 줍니다 — <b>간트</b>는 「지금 무엇이 남았나」(내 할일 목록)이고, <b>일자별</b>은 「무엇을 했나」(기안·결재·일보·검측까지 포함한 기록)입니다.',
    actions: '위쪽에서 축(간트·일자별)과 연도를 고릅니다. 고른 축·사람·연도는 <b>주소에 실립니다</b> — 「이 사람 9월」을 링크로 넘길 수 있습니다.',
    tip: '<b>남의 간트는 없습니다.</b> 간트가 그리는 할일 목록은 본인만 읽는 자료라, 다른 사람은 일자별 기록만 볼 수 있습니다.',
  },
  {
    path: '/sites/chats',
    label: '현장 대화방',
    what: '현장마다 운영 중인 카카오톡 오픈채팅방 입구를 한곳에 모아 둔 곳입니다. 누르면 카카오톡이 열립니다.',
    actions: '이 버튼들로 대화방을 등록하고, 현장·방 이름으로 찾고, 지난 방까지 함께 볼지 정합니다.',
    tip: '대화 내용은 이 서비스에 저장되지 않습니다. 기록으로 남겨야 하는 내용은 주간업무나 자료실에 올리세요.',
  },
  /*
    연락처(2026-09-22). 전에는 현장 전체 대시보드의 둘째 탭이었고 대본도 그 화면의 걸음 둘이었다 —
    화면이 밖으로 나오면서 대본도 함께 옮겼다.
  */
  {
    path: '/sites/contacts',
    label: '연락처',
    what: '본사와 전 현장의 전화번호를 한 표에서 찾습니다. 한 줄이 사람 하나이고 「소속」이 그중 한 칸이라, 어느 현장인지 모르는 채로 「○○레미콘 배차」를 쳐도 걸립니다.',
    actions: '[현장 현황]으로 돌아가고, [본사 연락처 추가]로 현장에 매이지 않는 번호를 적습니다 — 잠재 발주처, 보증보험 담당자, 구청 인허가 창구 같은 것들입니다.',
    spots: [
      {
        target: 'all-contacts-filters',
        title: '소속·회사·이름·업무·번호가 한꺼번에 걸립니다',
        body: '소속 선택 상자는 본사와 현장마다 몇 줄이 있는지 괄호로 보여 주고, 옆의 칩은 구분으로 좁힙니다.',
      },
      {
        target: 'all-contacts-table',
        title: '고치는 자리는 줄마다 다릅니다',
        body: '본사 줄은 <b>여기서</b> 고칩니다. 현장 줄은 현장 이름을 누르면 그 현장 연락처 탭이 열리고, 거기가 정본입니다 — 발주처·감리·설계사는 현장 수정, 당사 인원은 인력배치표에서 옵니다.',
      },
    ],
    tip: '번호를 누르면 바로 걸립니다. 우리 직원만 빠르게 찾을 때는 화면 위 「직원찾기」가 더 빠릅니다.',
  },

  // ── 업무 일정 ───────────────────────────────────────────────────────────
  {
    path: '/dashboard',
    label: '대시보드',
    what: '본사와 현장의 주간업무 현황을 모아 보는 곳입니다. 어느 부서·현장이 무엇을 올렸는지 주차 단위로 훑습니다.',
    tabs: '이 버튼들로 본사 업무와 현장 업무를 갈아 봅니다.',
    actions: '여기서 주차를 고릅니다. 화면 전체가 고른 주차 기준으로 다시 그려집니다.',
    tip: '내용을 고치는 곳은 아닙니다. 수정은 「주간업무」 의 본사·현장 탭에서 합니다.',
  },
  {
    path: '/meetings/weekly',
    label: '주간업무',
    what: 'v1 의 주간회의 세 화면(본사·현장·준공현장)을 한 화면으로 묶은 곳입니다. 화면이 사라진 것이 아니라 상단 탭이 되었습니다.',
    tabs: '탭마다 주소가 따로 있어, 쓰던 즐겨찾기 링크가 그대로 열립니다. 왼쪽 사이드바의 하위 항목도 같은 곳을 가리킵니다.',
    tip: '보이는 탭은 권한에 따라 다릅니다. 있어야 할 탭이 없으면 관리자에게 조회 권한을 요청하세요.',
  },
  {
    path: '/meetings/hq',
    label: '주간업무 · 본사',
    what: '부서별 중점업무를 주차 단위로 작성하고 공유하는 곳입니다. 예전 「본사 주간회의」 화면입니다.',
    tabs: '본사·현장·준공현장이 한 화면의 탭으로 묶였습니다. 다른 탭도 여기서 바로 오갑니다.',
    actions: '여기서 주차를 고릅니다. 아래 내용 전체가 그 주차 기준으로 바뀝니다.',
    spots: [
      { target: 'page-filters', title: '이 버튼들이 부서입니다', body: '부서를 고르면 그 부서의 중점업무만 아래에 나옵니다. 옆의 작은 숫자가 그 부서에 올라온 건수입니다.' },
      { target: null, title: '업무를 적습니다', body: '내 부서 자리에서 업무를 추가하거나, 지난 주 내용을 복사해 이어 씁니다.' },
    ],
    tip: '주차 목록은 서버가 달력에서 만듭니다 — 따로 등록할 것이 없습니다.',
  },
  {
    path: '/meetings/site',
    label: '주간업무 · 현장',
    what: '현장별 주간업무를 작성하고 공유하는 곳입니다. 예전 「현장 주간회의」 화면입니다.',
    tabs: '본사·현장·준공현장이 한 화면의 탭으로 묶였습니다.',
    actions: '여기서 주차를 고릅니다.',
    spots: [
      { target: 'page-filters', title: '이 버튼들로 분류를 고릅니다', body: '건축·토목 같은 현장 분류입니다. 고르면 아래 현장 목록이 그 분류만 남습니다.' },
      { target: null, title: '현장을 골라 작성합니다', body: '분류 아래 현장 목록에서 대상을 고르면 그 현장의 주간업무 자리가 열립니다.' },
    ],
    tip: '현장이 준공 처리되면 이 목록에서 빠지고 「준공현장」 탭으로 옮겨 갑니다 — 지워지는 것이 아닙니다.',
  },
  {
    path: '/meetings/completed',
    label: '주간업무 · 준공현장',
    what: '준공 처리된 현장의 주간업무 이력을 보는 곳입니다. 지난 현장이 어떻게 진행됐는지 되짚을 때 씁니다.',
    tabs: '본사·현장·준공현장이 한 화면의 탭으로 묶였습니다.',
    actions: '여기서 주차를 고릅니다.',
  },
  {
    path: '/meetings/monthly',
    label: '월간업무',
    what: '한 달 일정을 달력으로 보는 곳입니다. 지금 보고 있는 「전체」 탭은 본사·현장 일정에 주간회의 주차와 연차까지 겹쳐 한 번에 보여 줍니다.',
    tabs: '일정을 만들고 고치는 것은 본사·현장 주요일정·현장 회의일정 탭에서 합니다. 전체 탭은 겹쳐 보는 자리입니다.',
    actions: '이 버튼들로 달을 옮깁니다 — 오늘·이전 달·다음 달.',
    spots: [
      { target: 'page-filters', title: '이 버튼들이 일정의 출처입니다', body: '눌러서 켜고 끕니다. 꺼진 출처의 일정은 달력에서 사라집니다 — 연차만 보고 싶을 때 나머지를 끄면 됩니다.' },
    ],
    tip: '예전 `/calendar` 주소도 이 화면으로 들어옵니다.',
  },
  {
    // 「전체」 탭의 제 주소. 부모 주소(`/meetings/monthly`)와 같은 화면이라 대본도 같다.
    path: '/meetings/monthly/all',
    label: '월간업무 · 전체',
    what: '한 달 일정을 달력으로 보는 곳입니다. 본사·현장 일정에 주간회의 주차와 연차까지 겹쳐 한 번에 보여 줍니다.',
    tabs: '일정을 만들고 고치는 것은 본사·현장 주요일정·현장 회의일정 탭에서 합니다. 전체 탭은 겹쳐 보는 자리입니다.',
    actions: '이 버튼들로 달을 옮깁니다 — 오늘·이전 달·다음 달.',
    spots: [
      { target: 'page-filters', title: '이 버튼들이 일정의 출처입니다', body: '눌러서 켜고 끕니다. 꺼진 출처의 일정은 달력에서 사라집니다 — 연차만 보고 싶을 때 나머지를 끄면 됩니다.' },
    ],
  },
  {
    path: '/meetings/monthly/hq',
    label: '월간업무 · 본사',
    what: '본사 월간 일정과 관계 부서를 달력 형태로 관리하는 곳입니다. 예전 「본사 월간회의」 화면입니다.',
    tabs: '여러 출처를 겹쳐 보려면 「전체」 탭으로 가세요. 일정 등록은 이 탭에서 합니다.',
    actions: '이 버튼들로 달을 옮기고, 카테고리를 관리하고, 「일정 추가」 로 날짜·내용·관계 부서를 넣습니다.',
    spots: [
      { target: 'page-filters', title: '색이 곧 카테고리입니다', body: '달력의 일정 색과 같습니다. 카테고리는 오른쪽 위 「카테고리 관리」 에서 늘리고 줄입니다.' },
      { target: null, title: '고치는 것은 달력에서', body: '달력에 놓인 일정을 누르면 그 자리에서 고치거나 지울 수 있습니다.' },
    ],
  },
  {
    path: '/meetings/monthly/field',
    label: '월간업무 · 현장 주요일정',
    what: '현장의 월간 주요일정을 달력 형태로 관리하는 곳입니다.',
    tabs: '여러 출처를 겹쳐 보려면 「전체」 탭으로 가세요.',
    actions: '이 버튼들로 달을 옮기고, 현장을 관리하고, 「일정 추가」 로 현장·날짜·내용을 넣습니다.',
    spots: [
      { target: null, title: '고치는 것은 달력에서', body: '달력에 놓인 일정을 누르면 그 자리에서 고치거나 지울 수 있습니다.' },
    ],
  },
  {
    path: '/meetings/monthly/site',
    label: '월간업무 · 현장 회의일정',
    what: '현장별 회의 일정을 달력 형태로 관리하는 곳입니다.',
    tabs: '여러 출처를 겹쳐 보려면 「전체」 탭으로 가세요.',
    actions: '이 버튼들로 달을 옮기고, 현장을 관리하고, 일정을 추가합니다.',
  },

  // ── 인사회의 ────────────────────────────────────────────────────────────
  {
    path: '/hr/placement',
    label: '인력배치표',
    what: '오늘 기준으로 누가 어느 현장·부서에 배치돼 있는지 한 장으로 보는 곳입니다. 「전술판」 에서는 부서·현장을 나란히 놓고 사람을 끌어 옮겨 인사발령을 냅니다.',
    tabs: '「명부」 는 인쇄하는 표, 「전술판」 은 배치를 바꾸는 판, 「자리배치도」 는 본사 도면 위에 사람을 끌어다 앉히는 판입니다. 명부에서는 본사·현장을 갈아 봅니다.',
    actions: '이 버튼으로 지금 보고 있는 배치표를 그대로 인쇄합니다 — 검색·탭 버튼은 인쇄물에 나오지 않습니다.',
    spots: [
      { target: 'page-filters', title: '사람을 찾습니다', body: '이름·직급·역할·분야·부서/현장이 한 번에 걸립니다. 걸린 사람이 있는 묶음만 아래에 남습니다.' },
    ],
    tip: '전술판에서 카드를 옮겨도 바로 저장되지 않습니다 — 아래 「변경사항」 에 쌓였다가 「발령 확정」 을 눌러야 반영됩니다. 겸직·파견을 여러 곳에 걸 때는 카드를 눌러 「자리 관리」 창에서 한 번에 고르세요. 옮길 수 있는 사람은 대표·본부장급과 인사배치 담당뿐이고, 보는 것은 배치표 권한이 있으면 누구나 됩니다. 자리배치도는 놓는 순간 저장되고, 앉히는 것은 최고관리자와 인사총무팀입니다.',
  },
  {
    path: '/hr/attendance',
    label: '근태 관리',
    what: '직원이 <b>앱에서 찍은</b> 출퇴근을 기간·이름으로 훑어보고, 「연차관리」 탭에서 전 직원의 한 해 연차를 한 장으로 보는 곳입니다. 연차 일수를 세는 공휴일 달력은 「공휴일」 탭에 있습니다.',
    // 등록 버튼이 없다는 것이 이 화면의 성격이다(`AttendancePage` 머리말 · `MenuActions` 에도
    // CREATE 가 없다) — 없는 버튼을 찾게 두지 않는다.
    tip: '출퇴근은 여기서 새로 만들 수 없습니다. 본인이 앱에서 찍는 것이고, 잘못 찍힌 것만 바로잡습니다. 연차관리 표에서는 파란 칸(결재)은 못 지우고, 노란 칸(직접 넣은 연차)만 지웁니다.',
  },
  {
    path: '/hr/search',
    label: '인력검색',
    what: '등록된 인력을 이름·직급·자격·현장·경력으로 찾는 곳입니다. 배치 계획을 짤 때 사람을 고르는 자리입니다.',
    spots: [
      { target: 'page-filters', title: '여기에 넣고 찾습니다', body: '위의 예시 단어를 눌러 보면 어떤 식으로 걸리는지 바로 확인할 수 있습니다. 프로젝트 경력까지 함께 검색됩니다.' },
    ],
    tip: '연락처만 빠르게 찾을 때는 화면 위 「직원찾기」 가 더 빠릅니다 — 어느 화면에서든 열립니다.',
  },
  {
    /*
      옛 경력관리 주소. 2026-09-23 부터 <b>인사자료 › 직원 자료의 경력 탭</b>으로 넘어간다
      ({@code screens.tsx} 의 `CareerTabRedirect`) — 여기 머무는 사람이 없으므로 이 대본은
      화면에 뜨지 않는다. 그래도 지우지 않는 이유는 <b>라우트가 남아 있기 때문</b>이다:
      대본 없는 화면이 조용히 생기지 않도록 목록을 맞춰 보는 테스트가 이 줄을 요구한다.
    */
    path: '/hr/management',
    label: '인력관리',
    what: '경력관리는 「인사자료」의 <b>경력 탭</b>이 되었습니다. 이 주소로 들어오면 그 탭으로 넘어갑니다.',
    tip: '퇴사 처리는 삭제가 아니라 퇴사일 입력으로 합니다 — 지난 기록이 함께 사라지지 않게 하기 위해서입니다.',
  },
  {
    path: '/hr/technicians',
    label: '기술인 관리',
    what: '건설기술인 등급·역량지수·교육 이수를 전 직원 한 장으로 보는 곳입니다. 협회에서 받은 명부 엑셀을 올리면 사람마다 자동으로 붙습니다.',
    actions: '「협회 명부 올리기」 로 엑셀을 올립니다 — 파일은 서버에 저장되지 않습니다. 확인필요·추출도 이 줄에 있습니다.',
    spots: [
      { target: null, title: '미리보기에서 확인하고 반영합니다', body: '누가 붙었고 무엇이 확인필요인지 먼저 보여 드립니다. 반영을 눌러야 사람마다 값이 붙습니다.' },
      { target: 'page-filters', title: '분야 탭이 엑셀의 시트입니다', body: '설계시공·품질처럼 직무분야로 나눠 봅니다. 「모자란 것만」 을 켜면 승급 부족분·미이수·미제출이 있는 줄만 남아, 챙길 사람을 골라 할일·메일을 보냅니다.' },
    ],
    tip: '이름이 마스킹된 회원(김○윤)은 생년월일과 성·끝자로 찾습니다. 못 찾은 줄은 「확인필요」 에 남고, 한 번 이어 주면 다음 달부터 자동으로 붙습니다.',
  },
  {
    path: '/hr/records',
    label: '인사자료 관리',
    what: '직원별 연차·부재·인사고과·자격증·지급 자산을 입력하는 곳입니다. 인사 담당자용 화면입니다.',
    spots: [
      { target: 'page-list', title: '왼쪽이 직원 명부입니다', body: '이름으로 검색해 고릅니다. 고른 사람의 자료가 오른쪽에 열립니다 — 먼저 고르지 않으면 오른쪽은 비어 있습니다.' },
      { target: null, title: '오른쪽에서 넣습니다', body: '연차·부재·인사고과·자격증 중 넣을 것을 고르고 저장하면, 그 직원의 마이페이지에 바로 나타납니다.' },
    ],
    tip: '본인이 자기 자료를 보는 곳은 「마이페이지」 입니다. 인사고과는 본인이 비밀번호를 다시 넣어야 열립니다.',
  },
  /*
    인사자료 관리·휴무 관리의 화면들이 각각 주소를 갖게 되면서(2026-08-28 · DOC-12 §메뉴 기준)
    대본도 갈라졌다.

    ★ `resolveGuide` 가 접두어로 물러서 주기는 한다. 그래도 갈라 쓰는 이유는, 그러면 「숙소·귀향」 을
      열고 <b>직원 명부 설명</b>을 읽게 되기 때문이다 — 틀린 안내는 안내가 없는 것보다 나쁘다.
  */
  {
    path: '/hr/records/staff',
    label: '인사자료',
    what: '직원 하나를 여는 곳입니다. 경력·현장 배치·기술등급과 연차·부재·인사고과·자격증이 한 화면의 탭으로 서 있습니다.',
    spots: [
      { target: 'page-list', title: '왼쪽이 직원 명부입니다', body: '이름·직급·소속·연락처로 좁혀 고릅니다. 먼저 고르지 않으면 오른쪽은 비어 있습니다. 계정만 있고 인력 정보가 없는 사람도 명부에 함께 서서, 그 줄을 누르면 계정이 이어집니다.' },
      { target: null, title: '오른쪽은 탭으로 갈립니다', body: '「경력」 은 옛 경력관리 화면이고, 나머지 탭이 인사자료입니다. 받은 권한에 따라 한쪽만 보일 수 있습니다.' },
      { target: null, title: '개인정보는 탭 위에서 고칩니다', body: '이름·연락처·소속은 어느 탭에서 보든 같은 값이라, 탭 안이 아니라 사람 이름 옆의 「개인정보 수정」 에 있습니다.' },
    ],
    tip: '본인이 자기 자료를 보는 곳은 「마이페이지」 입니다. 인사고과는 본인이 비밀번호를 다시 넣어야 열립니다.',
  },
  {
    // 옛 주소(2026-10-26) — 근태 관리 › 연차관리 탭으로 넘어간다(`screens.tsx`). 라우트가 남아 대본도 남긴다.
    path: '/hr/records/promotion',
    label: '연차관리',
    what: '연차관리는 「근태 관리」의 <b>연차관리 탭</b>이 되었습니다. 이 주소로 들어오면 그 탭으로 넘어갑니다.',
    tip: '연차 일수를 세는 값은 같은 화면의 「공휴일」 탭이 정합니다 — 그쪽이 비어 있으면 일수가 맞지 않습니다.',
  },
  {
    path: '/hr/records/evaluation',
    label: '인사고과',
    what: '한 해의 인사고과를 <b>전 직원 한 장</b>에서 매깁니다. 이 값이 승진 요건의 고과 점수로 쌓입니다.',
    actions: '연도를 고르고 등급을 넣은 뒤 [저장] 을 누르면 고친 줄만 한 번에 저장됩니다.',
    spots: [
      {
        target: null,
        title: '빈 칸은 「안 매김」입니다',
        body: '등급을 비워 둔 줄은 저장에서 건너뜁니다 — 지워지지 않습니다. 이미 매긴 고과를 지우려면 「인사자료」 의 인사고과 탭에서 그 줄을 삭제하세요.',
      },
    ],
    tip: '등급 옆 괄호는 승진 고과 점수입니다. 그 환산표와 직급별 기준점은 「복지·수당 기준」 에서 정합니다 — 비어 있으면 승진 판정의 고과 축이 서지 않습니다.',
  },
  {
    path: '/hr/records/promotions',
    label: '승진 관리',
    what: '승진 요건을 채운 직원을 골라 <b>결재로 올리는</b> 곳입니다. 여기서 직급이 바로 바뀌지는 않습니다.',
    // 「여기서 바뀌지 않는다」가 이 화면의 핵심이다 — 올려 놓고 반영된 줄 알면 그 사람의 직급이
    // 결재함에 멈춰 있는 동안 아무도 모른다(`HrRecordsPage` 의 promotions 설명).
    tip: '결재가 완료되어야 직급과 직급 이력이 함께 올라갑니다. 올린 뒤에는 결재함에서 진행을 봅니다.',
  },
  {
    path: '/hr/records/holiday',
    label: '공휴일',
    what: '공휴일은 「근태 관리」의 <b>공휴일 탭</b>이 되었습니다. 이 주소로 들어오면 그 탭으로 넘어갑니다. 대체공휴일·임시공휴일도 그 탭에서 넣습니다.',
    spots: [
      { target: null, title: '숫자의 근거입니다', body: '여기 빠진 날은 근무일로 세어져 전 직원의 연차·휴무 집계가 함께 틀어집니다. 해가 바뀌면 먼저 챙기는 화면입니다.' },
    ],
  },
  {
    path: '/hr/records/standards',
    label: '복지·수당 기준',
    what: '건강검진·경조·장학·법정의무교육·수당·법정 선임·숙소·귀향여비의 <b>기준을 넣는 곳</b>입니다. 승진 요건은 인사 기준표에서 고칩니다.',
    actions: '「문서 업로드」 로 사규 엑셀·PDF·한글·캡처를 읽어 미리보기에서 고친 뒤 반영합니다.',
    spots: [
      { target: null, title: '여기가 비면 나머지가 「기준 미등록」 입니다', body: '판정 로직은 이미 서 있고 기준만 비어 있습니다 — 한 줄을 넣는 순간 전 직원의 마이페이지에 대상 여부가 뜹니다.' },
      { target: null, title: '숙소·귀향 탭은 둘만 담습니다', body: '자격 문턱(몇 km부터 대상인가)과 귀향여비 구간표입니다. 숙소비 금액은 기준표가 아니라 <b>승인하는 사람이 그 자리에서</b> 정합니다.' },
    ],
    tip: '바로 저장하지 않습니다 — 읽은 줄을 미리보기에서 고치고 고른 줄만 반영합니다. 근거 칸은 입력이 아니라 원문 인용이고, 비어 있는 줄은 값을 특히 눈여겨봐야 합니다.',
  },
  {
    path: '/hr/records/relocation',
    label: '숙소·귀향',
    what: '직원이 올린 숙소·귀향여비 요청을 <b>결재 없이 여기서 바로</b> 승인·배정·지급 처리하는 곳입니다.',
    actions: '줄을 누르면 지도와 방 목록이 있는 창이 열립니다.',
    spots: [
      { target: null, title: '두 탭입니다', body: '「요청 처리」 가 손이 가는 자리이고, 「숙소 현황」 은 숙소 계약 표입니다. 줄을 누르면 계약·방·사용자·납부·첨부를 한 창에서 고치고, 엑셀로도 내려받습니다.' },
      { target: null, title: '방이 없으면 고를 것도 없습니다', body: '「숙소 현황」 에 숙소를 추가해 두어야 승인 창에서 고를 수 있습니다. 그 전에는 숙소비 지급이나 「나중에 정함」 으로 승인합니다.' },
      { target: null, title: '정원 초과는 막지 않습니다', body: '임시 인원이 끼는 일이 실제로 있어 경고만 하고 통과시킵니다 — 막으면 화면 밖에서 처리되어 목록이 현실과 갈라집니다.' },
    ],
    tip: '기준 금액(귀향여비 구간표·자격 문턱)을 고치는 곳은 「복지·수당 기준 › 숙소·귀향」 입니다.',
  },
  {
    path: '/hr/records/dashboard',
    label: '대시보드',
    what: '인사 명부에서 센 재직·입사·퇴사 인원과 최근 10년 이직률입니다. 입사일이 빈 사람은 재직 인원에 들지 않습니다.',
    actions: '그래프마다 [표] 를 누르면 숫자로 봅니다. 아래 탭에서 재직자·입사자·퇴사자 명단과 사원별 경력카드를 엽니다.',
    spots: [
      { target: null, title: '본사/임원은 합계 하나입니다', body: '소속이 본사 부서이거나 직급이 임원인 재직자를 한 번씩 셉니다.' },
      { target: null, title: '이직률은 퇴사자 ÷ 연평균 재직자입니다', body: '연평균 재직자는 그해 월말 재직자의 평균입니다. 올해는 이번 달까지(이번 달은 오늘 기준)만 셉니다.' },
      { target: null, title: '경력카드는 PDF 로 내려받습니다', body: '사람을 고르면 인적사항·직급 이력·근무처이력·경력·자격증이 한 장에 서고, [PDF] 로 내려받습니다.' },
    ],
    tip: '명단의 이름을 누르면 그 사람의 경력카드가 열립니다.',
  },
  {
    path: '/hr/records/duties',
    label: '업무분장',
    what: '회사 업무분장 엑셀 한 장을 그대로 옮긴 표입니다. 담당자 칸의 이름은 <b>저장할 때 직원과 자동으로 이어집니다</b>.',
    actions: '[수정] 을 누르고 엑셀에서 머리 줄째 복사해 붙여 넣은 뒤 [저장] 을 누릅니다.',
    spots: [
      { target: null, title: '왼쪽은 부서 트리입니다', body: '부서를 누르면 그 부서와 아래 부서의 줄만 보입니다. 부서를 고른 채 붙여 넣으면 부서명이 비어 있는 줄은 그 부서로 채워집니다.' },
      { target: null, title: '담당자는 여럿을 적습니다', body: '「홍길동 대리 / 김철수 사원, 이영희 기사(파견)」 처럼 / · 쉼표 · 줄바꿈으로 나눠 적으면 이름마다 재직자와 잇습니다. 직급·(파견) 같은 덧붙임은 떼고 찾습니다.' },
      { target: null, title: '빨간 이름은 못 이은 사람입니다', body: '재직자 중에 그 이름이 없거나 오탈자입니다. 동명이인이면 그 자리에 고르는 칸이 서고, 고르면 바로 이어집니다.' },
      { target: null, title: '이은 업무는 직원찾기와 마이페이지에 보입니다', body: '직원찾기 명함의 「업무분장」 과 본인 마이페이지의 「내 업무분장」 이 이 표에서 읽습니다.' },
    ],
    tip: '머리 줄 없이 열아홉 칸을 붙여 넣으면 차례대로 들어갑니다.',
  },
  {
    path: '/hr/records/incentive',
    label: '성과급',
    what: '준공이 다가온 현장의 성과급 건이 <b>저절로 열리는</b> 곳입니다. 지급금 한도·기준 인원을 넣으면 그 조직의 현장대리인·팀장 마이페이지에 평가할 자리가 생깁니다.',
    actions: '「한도 미입력」 줄을 눌러 한도와 기준 인원을 넣고, 올라온 명단을 조정한 뒤 「확정 상신」 을 누릅니다.',
    spots: [
      { target: null, title: '건은 저절로 열립니다', body: '계약 준공일이 한 달 안으로 들어오면 서버가 기간만 채워 건을 세웁니다. 목록 맨 위의 「한도 미입력」 이 그것입니다 — 한도를 넣기 전까지는 <b>평가자에게 보이지 않습니다</b>. 안 열린 곳은 「성과급 건 열기」 로 손수 엽니다.' },
      { target: null, title: '현장만이 아닙니다', body: '건은 <b>현장이거나 부서</b>입니다. 본사 부서는 끝나는 날이 없어 저절로 열리지 않으니 「성과급 건 열기」 에서 구분을 「부서」 로 고릅니다 — 부서는 기간이 다르면 해마다 여러 건일 수 있습니다.' },
      { target: null, title: '평가는 그 조직이 합니다', body: '기여도는 현장대리인(부서는 팀장)이 마이페이지 › 복지관리 › 성과급에서 매깁니다. 여기서 손대는 것은 <b>기준값과 조정액</b>입니다.' },
      { target: null, title: '기준 금액은 적는 칸이 아닙니다', body: '지급금 한도 ÷ 기준 인원으로 나옵니다. 현장대리인은 그 금액의 100%가 권장이고, 나머지는 기여도(%)를 곱합니다.' },
      { target: null, title: '한도를 넘으면 제출이 막힙니다', body: '현장이 본사로 넘기는 순간과 결재를 올리는 순간에 한 번씩 셉니다. 잔여가 빨간 음수면 그만큼 어느 줄을 깎아야 합니다.' },
      { target: null, title: '확정 버튼은 없습니다', body: '확정은 전자결재가 완료되는 순간 찍힙니다. 그때부터 직원 본인의 마이페이지에 금액이 보입니다.' },
    ],
    tip: '평가를 다시 받아야 하면 「반려」 입니다 — 확인 창에서 사유를 적어야 눌리고, 그 글이 평가자 화면의 「본사 의견」 으로 보입니다.',
  },
  {
    path: '/hr/records/payroll',
    label: '급여명세서',
    what: '한 달 치 급여 엑셀을 올려 직원마다 <b>회사 양식 그대로의 명세서 PDF</b>를 만드는 곳입니다.',
    actions: '「양식 내려받기」 로 받은 엑셀에 금액을 채워 귀속월을 고르고 올립니다. 줄 끝의 PDF 를 누르면 그 사람의 명세서가 내려받힙니다.',
    spots: [
      { target: null, title: '지급·공제 칸은 늘려도 됩니다', body: '엑셀 1행에 「지급」 이나 「공제」 라고 적힌 열은 2행의 이름 그대로 명세서 항목이 됩니다. 합계와 차인지급액은 서버가 더합니다.' },
      { target: null, title: '안 되는 줄만 돌려줍니다', body: '사원코드(없으면 이름)로 사람을 찾고, 못 찾거나 금액이 아닌 칸이 있는 줄은 사유와 함께 아래 표에 남깁니다. 나머지는 저장됩니다.' },
    ],
    tip: '같은 달을 다시 올리면 파일에 있는 사람의 명세서만 바뀝니다. 계산방법은 엑셀의 「계산방법」 시트에서 옵니다.',
  },
  {
    path: '/hr/leave/dashboard',
    label: '휴무 대시보드',
    what: '이 달 휴무를 달력 한 장으로 봅니다 — 「그날 누가 없나」 에 가장 먼저 답하는 화면입니다.',
    spots: [
      { target: null, title: '날짜 칸이 둘로 갈라져 있습니다', body: '본사(보라)와 현장(청록)이 위아래로 나뉩니다. 점선은 아직 쓰지 않은 <b>계획</b>이고 위 스위치로 끕니다. 날짜를 누르면 그날 쉬는 사람 전부가 열립니다.' },
    ],
    tip: '보이는 줄의 범위는 서버가 정합니다 — 필터를 풀어도 남의 휴무가 열리지 않습니다.',
  },
  {
    path: '/hr/leave/plan',
    label: '휴무 계획서',
    what: '「언제 쉬겠다고 적었나」 와 그 이행률을 보는 곳입니다.',
    spots: [
      { target: 'page-filters', title: '챙길 사람만 추려 봅니다', body: '「계획 미제출만」·「지난 계획 미이행만」 으로 좁힙니다.' },
      { target: null, title: '내는 곳은 마이페이지입니다', body: '본인이 <b>마이페이지 › 연차관리</b>에서 달력에 날짜를 골라 저장합니다 — 결재가 없습니다. 이 화면은 그것을 <b>보는</b> 자리라 남의 계획을 대신 넣지 않습니다.' },
    ],
    tip: '계획을 저장해도 <b>잔여 연차는 줄지 않습니다</b> — 실제로 쉬는 날은 「연차 신청」 결재입니다.',
  },
  {
    path: '/hr/leave/board',
    label: '휴무 현황',
    what: '실제로 얼마나 썼는지 — 사람마다 일자별 사용을 한 표로 봅니다.',
    spots: [
      { target: null, title: '색이 휴가 종류입니다', body: '점선 칸은 아직 쓰지 않은 「계획」 이고, 채운 칸이 실제 사용입니다.' },
    ],
    tip: '대시보드에서 달을 넘기면 이 표도 같은 달을 봅니다 — 한 화면에서 보고 있는 시점은 하나입니다.',
  },
  {
    path: '/hr/leave',
    label: '휴무 관리',
    what: '「대시보드」 달력으로 이 달 휴무를 먼저 보고, 「계획서」 로 진척을, 「현황」 으로 실제 사용을 봅니다.',
    spots: [
      { target: null, title: '먼저 달력입니다', body: '대시보드 탭은 이 달 달력입니다 — 날짜 칸 안에서 <b>본사(보라)와 현장(청록)</b>이 위아래로 갈라져 「그날 누가 없나」 가 바로 읽힙니다. 점선은 아직 쓰지 않은 <b>계획</b>이고, 위 스위치로 끌 수 있습니다. 날짜를 누르면 그날 쉬는 사람 전부가 열립니다.' },
      { target: 'page-filters', title: '소속과 이름으로 좁힙니다', body: '계획서 탭에서는 「계획 미제출만」·「지난 계획 미이행만」 으로 추려 봅니다. 현황 탭의 색은 휴가 종류이고, 점선 칸은 아직 쓰지 않은 「계획」 입니다.' },
      /*
        한때 「내 계획 줄의 [휴무 계획서 작성]」 을 가리켰다. 그 카드를 화면에서 걷어냈다
        (사용자 지시 2026-08-26) — 이 화면은 남의 진척을 <b>보는</b> 자리이고 일반 사용자는 들어오지
        못한다. 넣는 문은 마이페이지 하나다.
      */
      { target: null, title: '계획을 내는 곳은 마이페이지입니다', body: '본인이 <b>마이페이지 › 연차관리</b>에서 달력에 날짜를 골라 저장합니다 — 결재가 없습니다. 이 화면은 그렇게 들어온 계획을 <b>보는</b> 자리라, 남의 계획을 대신 넣지 않습니다.' },
    ],
    tip:
      '처음 보이는 축도 맡은 자리가 정합니다 — 팀장·현장대리인은 맡은 곳의 일자별로, 임원과 ' +
      '인사총무팀은 현장·부서별로 열립니다. 버튼으로 언제든 바꿉니다. 계획은 결재 없이 저장되지만 ' +
      '<b>잔여 연차는 줄지 않습니다</b> — 실제로 쉬는 날은 「연차 신청」 결재이고, ' +
      '사용 내역을 고치는 곳은 「인사자료 관리」 입니다.',
  },
  {
    path: '/hr/pc-off',
    label: 'PC Off',
    what:
      '정해 둔 시각에 사내 PC 를 끄는 곳입니다. <b>여기가 유일한 스위치</b>입니다 — ' +
      '정책에 시각을 칠하는 것 말고 PC 가 꺼지는 길이 없습니다.',
    spots: [
      {
        target: 'page-tabs',
        title: '정책이 「몇 시」, PC 가 「누구」 입니다',
        body:
          '정책은 대상별로 한 줄입니다 — 전사 · 부서 · PC 한 대. 겹치면 <b>좁은 것이 이깁니다</b>. ' +
          '기록 탭에는 그 PC 의 하루가 시간 순으로 남습니다: 켜짐 → 정책 실행 → 긴급 작업 → 꺼짐. ' +
          '<b>다운로드</b> 탭에서 프로그램을 받고, 거기 <b>[설치 방법]</b> 에 깔는 법이 처음부터 ' +
          '끝까지 적혀 있습니다 — PowerShell 창 그림과 그대로 붙여 넣을 명령어까지 있습니다.',
      },
      {
        target: null,
        title: '요일마다 쓸 수 있는 시간을 칠합니다',
        body:
          '정책을 열면 7일 × 24시간 표가 나옵니다. 끌 시각을 적는 것이 아니라 ' +
          '<b>쓸 수 있는 구간을 칠하고</b>, 그 구간의 끝이 곧 종료 시각입니다. ' +
          '칠하지 않은 요일은 그날 이 정책이 돌지 않습니다 — 「그날은 아예 못 쓴다」가 아닙니다.',
      },
      {
        target: null,
        title: '새로 깔린 PC 는 승인해야 돕니다',
        body:
          '에이전트를 깔면 <b>승인 대기</b>로 뜹니다. 승인 전에는 정책이 걸리지 않습니다 — ' +
          '누구 PC 인지는 사람이 보고 정합니다. 사흘 넘게 소식이 없는 PC 는 <b>연결 끊김</b>으로 섭니다.',
      },
      {
        target: 'page-list',
        title: '줄 끝의 버튼들',
        body:
          '<b>테스트 실행</b> 은 그 PC 에 지금 경고 화면을 띄웁니다(안 끕니다) — 어느 PC 인지 찾는 자리이고, ' +
          '걸려 있는 동안은 <b>테스트 취소</b>가 됩니다. <b>수정</b> 은 사용자 정보를 고치고, ' +
          '<b>강제 종료</b> 는 실제로 끕니다.',
      },
      {
        target: null,
        title: '그 PC 만 빼려면 — PC Off 제외',
        body:
          '수정 안의 체크박스입니다. 켜 두면 그 PC 는 정책을 받지 않습니다. ' +
          '<b>인사총무팀 · 공사전략팀 팀장만</b> 바꿀 수 있고, 누가 언제 걸었는지가 남습니다.',
      },
      {
        target: null,
        title: '직원이 미룰 수 있는 두 가지',
        body:
          '<b>긴급 작업</b> 은 경고 화면에서 직원이 직접 누릅니다 — 10분씩 하루 세 번, 승인이 없습니다. ' +
          '그날을 통째로 열어야 하면 전자결재의 <b>PC Off 해제</b> 를 올리고, <b>본인 팀 팀장</b>이 전결합니다.',
      },
    ],
    tip:
      '「오늘」 칸은 <b>서버가 계산한 값</b>입니다 — PC 의 에이전트가 받는 것과 같은 값이라, ' +
      '여기 적힌 시각이 곧 그 PC 에서 일어날 일입니다. 아무 일도 없는 PC 는 그 이유가 함께 적힙니다 ' +
      '(승인 대기 · PC Off 제외 · 정책 없음 · 오늘 없음).',
  },
  {
    path: '/hr/tech-education',
    label: '기술인 교육',
    what:
      '기술인 법정교육이 <b>신청 → 수강 → 증빙 → 정산 결재 → 환급</b>까지 가는 길을 <b>표 하나</b>로 봅니다. ' +
      '위의 탭이 그 표를 단계별로 자르고, 줄마다 <b>그 단계에서 할 수 있는 버튼만</b> 나옵니다.',
    tabs:
      '진행 중(끝나지 않은 것 전부) · 증빙 미제출(직원이 올릴 차례) · 증빙 확인(기술인 담당 차례) · ' +
      '정산 결재(결재선이 도는 중) · 환급 진행(재무팀 차례) · 완료. ' +
      '<b>환급까지 끝난 건은 「완료」 탭에만 있습니다</b> — 더 할 일이 없는 줄이 목록에 섞이면 손댈 것과 아닌 것을 매번 가려야 합니다. ' +
      '지우지는 않으니 「그때 얼마 줬더라」는 완료 탭에서 봅니다. 탭 옆 숫자는 그 단계의 건수이고, 0건이어도 탭은 사라지지 않습니다.',
    spots: [
      {
        target: 'page-filters',
        title: '탭이 곧 「누구 차례인가」입니다',
        body:
          '한 건은 위 순서대로 한 칸씩 내려옵니다. <b>노란 배지는 우리가 쥐고 있는 것</b>, 파란색은 남을 기다리는 것, ' +
          '빨간색은 기한을 넘겼거나 되돌아온 것입니다. 상태 배지에 마우스를 올리면 접기 전의 세 축(대장·증빙·환급)이 그대로 보입니다. ' +
          '머리글을 누르면 정렬되고, 기본은 <b>신청일 최신순</b>입니다.',
      },
      {
        target: null,
        title: '「증빙 확인」 탭 — 여기서 결재가 시작됩니다',
        body:
          '직원이 올린 <b>[수료증] [영수증]</b> 버튼을 눌러 창에서 열어 보고 판단합니다(붙어 있지 않으면 회색 「수료증 없음」). ' +
          '<b>[확인]</b>을 누르면 교육비 정산 결재가 <b>그 직원 명의로 자동 상신</b>됩니다 — 직원은 결재를 따로 올리지 않습니다. ' +
          '틀렸으면 <b>[반려]</b>에 사유를 적으세요. 사유는 그 직원 화면에 뜨고 홈 할일이 다시 살아납니다. ' +
          '이 두 버튼은 <b>기술인 담당에게만</b> 보입니다.',
      },
      {
        target: null,
        title: '「환급 진행」 탭 — 재무팀이 보는 자리',
        body:
          '정산 결재가 끝난 건만 여기 섭니다. 승인일 + 2주가 기한이고, 넘기면 빨간 「n일 초과」가 붙습니다. ' +
          '왼쪽 체크칸으로 여럿 고른 뒤 오른쪽 위 <b>[재무팀 전달]</b>을 누르면, 건마다 ' +
          '<b>수강 신청서 → 수료증 → 출금결의서 → 영수증</b>이 한 PDF로 묶여 <b>미리보기</b>로 뜹니다. ' +
          '눈으로 확인하고 <b>[결재 상신]</b>을 누르면 지출 결의 결재가 올라가고 그 문서로 건너갑니다 — ' +
          '파일로 다운로드하지 않는 이유는 메일로 보내면 받았는지·처리했는지가 남지 않기 때문입니다. ' +
          '이체를 마쳤으면 줄의 <b>[지급]</b>을 눌러 완료로 넘깁니다.',
      },
      {
        target: null,
        title: '체크는 환급 진행 줄에만 열립니다',
        body:
          '정산 결재가 끝나야 지급 근거가 갖춰지므로, 그 앞 단계의 줄은 <b>체크 자체가 잠깁니다</b>. ' +
          '또 수료증·영수증·금액 중 하나라도 빠지면 묶음을 <b>아예 만들지 않고</b> ' +
          '「김도현 수료증·영수증 없음」처럼 누가 무엇이 없는지 적어 돌려줍니다 — 되돌아올 서류를 만들어 보내지 않기 위해서입니다.',
      },
      {
        target: 'page-list',
        title: '줄을 누르면 그 건이 걸어온 길이 펴집니다',
        body:
          '수강 신청 결재 → 증빙 제출 → 담당 확인 → 교육비 정산 결재 → 환급까지 <b>날짜와 함께</b> 폅니다. ' +
          '문서번호를 누르면 그 결재 문서로 가고, <b>아직 지나지 않은 걸음은 회색</b>으로 남으니 회색 자리가 곧 다음 할 일입니다. ' +
          '「2주째 담당 확인에서 멈춘 건」이 이 줄에서 보입니다.',
      },
      {
        target: null,
        title: '묶음의 「출금결의서」에 무엇이 채워지나',
        body:
          '<b>자동으로 들어가는 것</b> — 발의부서 · 현장 · 발의일자 · 담당자 · 거래처(교육기관) · ' +
          '금액(숫자와 「일금 …원整」 둘 다) · 적요(「홍길동 대리 승급교육 환급 서류」). ' +
          '<b>손으로 적는 칸</b> — 대표자 · 계좌 · 공급가액 · 부가가치세 · 차변/대변 분개. ' +
          '공급가액과 부가세를 비워 두는 이유는 우리가 아는 값이 <b>총액 하나</b>뿐이기 때문입니다 — ' +
          '면세 교육이 흔한데 임의로 떼어 적으면 재무팀이 그 값을 믿고 분개합니다.',
      },
      {
        target: null,
        title: '누르면 바로 되는 버튼은 없습니다',
        body:
          '[확인] · [반려] · [지급] · [되돌리기] · [재무팀 전달]은 모두 <b>한 번 더 묻습니다</b>. ' +
          '창에는 <b>누구의 어느 건인지</b>와 금액·첨부 유무가 함께 적힙니다 — 줄이 촘촘하고 바로 옆 줄은 다른 사람이라, ' +
          '「정말 하시겠습니까?」만 뜨는 창은 잘못 누른 사람을 그대로 통과시킵니다.',
      },
    ],
    tip:
      '<b>직원은 이 화면을 쓰지 않습니다.</b> 직원 쪽은 홈 할일의 「수료증·영수증을 첨부해주세요」를 눌러 ' +
      '들어가는 증빙 제출 화면이고, 그 줄은 <b>치울 수 없습니다</b> — 회사가 돌려줄 돈이 걸린 줄이라 제출해야 사라집니다. ' +
      '수료증·영수증은 <b>본인 · 기술인 담당 · 재무팀 · 관리자</b>만 열 수 있고 자료실에는 뜨지 않습니다. ' +
      '파일을 열면 창 안에서 보이고, 회계 시스템에 붙일 파일이 필요하면 그 창의 [다운로드]를 씁니다.',
  },
  {
    path: '/hr/tech-education/evidence',
    label: '교육 증빙 제출',
    what:
      '수강 결재가 끝난 교육의 <b>수료증과 영수증</b>을 올리는 곳입니다. 홈 할일의 ' +
      '「수료 후 수료증·영수증을 첨부해주세요」 를 누르면 여기로 옵니다.',
    spots: [
      {
        target: null,
        title: '올리는 것은 파일 둘과 금액입니다',
        body:
          '교육비를 적고 수료증 · 영수증을 고르면 [제출] 이 열립니다. <b>이수일은 묻지 않습니다</b> — ' +
          '수료증이 곧 「받았다」는 증거라, 낸 날이 이수일로 적힙니다. 이수시간은 수료증에 적힌 값을 옮겨 적습니다(선택). ' +
          '결재를 따로 올릴 필요가 없습니다 — 기술인 담당이 확인하면 <b>교육비 정산 결재가 내 이름으로</b> 올라갑니다.',
      },
      {
        target: null,
        title: '낸 뒤에는 잠깁니다',
        body:
          '「확인 대기」 인 동안에는 고칠 수 없습니다. 잘못 냈으면 기술인 담당에게 반려를 요청하세요 — ' +
          '되돌아오면 사유가 붉게 적히고 그때 다시 올릴 수 있습니다.',
      },
    ],
    tip:
      '올린 파일은 <b>본인 · 기술인 담당 · 재무팀</b>만 열 수 있습니다. 자료실에는 뜨지 않습니다. ' +
      '할일 목록의 이 줄은 <b>치울 수 없습니다</b> — 회사가 돌려줄 돈이 걸린 줄이라, 내면 사라집니다.',
  },
  {
    path: '/hr/standards',
    label: '인사 기준표',
    what:
      '승진 요건 · 현장 정원 · 역량지수 환산식을 넣어 두는 곳입니다. 회사가 정하는 값이라 코드가 아니라 ' +
      '여기에 둡니다 — 배포 없이 고칠 수 있어야 실제로 고쳐집니다.',
    tabs: '승진 기준 · 현장 기준인원 · 역량지수 산정 세 표를 오갑니다.',
    spots: [
      {
        target: null,
        title: '표에서 바로 고칩니다',
        body:
          '줄을 고쳐 「저장」, 맨 아래 빈 줄에 적으면 새 줄이 생깁니다. ' +
          '값이 들어간 순간부터 진급 패널·조직도·기술등급 점수가 판정을 시작합니다.',
      },
    ],
    tip:
      '비운 칸은 0 이 아니라 「보지 않는다」 는 뜻입니다. 표가 비어 있으면 각 화면은 판정하지 않고 ' +
      '「기준 미등록」 이라고 적습니다 — 어림값을 채워 두면 그것이 회사의 기준인 줄 알게 되기 때문입니다.',
  },
  {
    path: '/hr/doc-templates',
    label: '증명서 서식 관리',
    what:
      '직원이 마이페이지에서 뽑는 증명서의 <b>양식</b>을 고치는 곳입니다. ' +
      '여기서 고친 것은 다음 발급부터 나갑니다. 이미 나간 문서는 그대로입니다.',
    tabs: '「서식 관리」에서 양식을 고치고, 「직인 관리」에서 회사 직인을 등록합니다.',
    actions: '새 서식을 만듭니다. 이름만 적으면 되고, 내용은 만든 뒤 편집기에서 씁니다.',
    spots: [
      {
        target: 'template-list',
        title: '고칠 서식을 고릅니다',
        body: '옆에 그 서식의 상태·발급 건수·찍히는 직인이 함께 보입니다.',
      },
      {
        target: 'template-meta',
        title: '이름과 상태',
        body: '고치는 즉시 저장됩니다. 「준비 중」이면 직원에게 안 보입니다 — 다 고친 뒤 「사용 중」으로 바꾸세요.',
        placement: 'bottom',
      },
      {
        target: 'template-editor',
        title: '여기에 문구를 씁니다',
        body:
          '워드처럼 씁니다. 글자를 드래그해 고른 뒤 위 버튼(굵게·가운데·크기)을 누르세요. ' +
          '아래 「변수 팔레트」의 이름·소속은 발급할 때 그 직원 값으로 채워집니다.',
        subTargets: ['template-toolbar', 'template-fields'],
      },
      {
        target: 'template-preview',
        title: '오른쪽은 나갈 모습입니다',
        body: '치는 즉시 다시 그려집니다. 이름·기간은 예시 값입니다.',
        placement: 'left',
      },
      {
        target: 'template-seal-anchor',
        title: '직인과 이미지 놓기',
        body:
          '이 버튼으로 직인을 불러오고 그림을 올립니다. 불러오면 바로 종이에 놓입니다. ' +
          '종이에서 끌어 옮기고 네 귀퉁이로 크기를 맞추거나, 숫자로 직접 적어도 됩니다.',
      },
      {
        target: 'template-assets',
        title: '올린 그림 관리',
        body: '올린 그림이 여기 모입니다. 「제거」하면 종이에서도 사라집니다.',
      },
      {
        target: 'template-save',
        title: '확인하고 저장합니다',
        body:
          '「PDF로 확인」은 실제로 나갈 문서를 그대로 만들어 봅니다(직인은 자리만 점선). ' +
          '「저장하기」를 눌러야 반영됩니다.',
      },
      {
        target: 'template-versions',
        title: '잘못 고쳤으면 되돌립니다',
        body: '저장한 판은 지워지지 않습니다. 여기서 예전 판을 골라 되돌리세요. 버튼의 v3 이 지금 쓰이는 판입니다.',
      },
    ],
    tip:
      '저장이 거절되면 대개 태그를 닫지 않았거나 없는 변수를 적은 것입니다 — 메시지에 그대로 나옵니다. ' +
      '「직인 없음」이 뜨면 등록만 하고 이 서식에 걸지 않은 것입니다.',
  },

  // ── 자료실 ──────────────────────────────────────────────────────────────
  {
    path: '/docs',
    label: '자료 조회',
    what: '사내 S3 저장소와 현장 NAS 를 한자리에서 조회하는 입구입니다.',
    actions: '이 버튼으로 문서를 넣는 화면(문서 입력)으로 들어갑니다.',
    spots: [
      { target: null, title: '아래 카드에서 저장소를 고릅니다', body: 'S3 저장소 · 현장/본사 NAS · 자료 뷰어로 갈라집니다. 맨 위의 파일 이름 규칙은 넣기 전에 한 번 보고 가는 자리입니다.' },
    ],
    tip: '파일 이름 규칙을 지켜야 나중에 검색됩니다. 표준 분류는 「문서 분류」 메뉴에서 관리합니다.',
  },
  {
    path: '/docs/categories',
    label: '문서 분류',
    what: '자료를 정리하는 대·중·소 표준 분류 체계를 관리하는 곳입니다. 회사 공통으로 쓰이는 기준입니다.',
    spots: [
      {
        target: 'page-form',
        title: '이 줄에서 분류를 더합니다',
        body: '왼쪽에서 상위 분류를 고르고(비우면 대분류), 분류 코드와 이름을 넣은 뒤 「추가」 를 누릅니다.',
      },
      { target: 'page-list', title: '아래가 지금의 분류 체계', body: '대 › 중 › 소 순으로 접혀 있습니다. 줄에서 바로 이름과 코드를 고치거나 지울 수 있습니다.' },
    ],
    tip: '분류를 바꾸면 이미 올라간 자료의 정리 기준도 함께 바뀝니다. 새로 만들기 전에 비슷한 분류가 있는지 먼저 보세요.',
  },
  {
    path: '/docs/upload',
    label: '문서 입력',
    what: '자료실에 문서를 넣는 곳입니다. 여러 파일을 한 번에 올릴 수 있고, 파일마다 공유 문서인지 개인 자료인지 정합니다.',
    spots: [
      { target: 'page-list', title: '왼쪽에 파일을 담습니다', body: '끌어다 놓거나 골라서 담습니다. 파일이 들어오면 AI 가 읽어 분류를 제안하고, 파일마다 다르게 할 것은 그 파일 줄에서 고칩니다.' },
      { target: 'page-form', title: '오른쪽은 단계별 공통 설정', body: '번호 순서대로 접혀 있습니다. 한 단계를 채우면 다음이 열립니다 — 여기서 정한 값이 담은 파일 전부의 기본값이 됩니다.' },
      { target: 'page-actions', title: '올리기', body: '모든 파일에 분류가 있어야 눌립니다. 못 누를 때는 그 이유가 파일 목록 아래에 적힙니다.' },
    ],
    tip: '개인 자료로 넣으면 지정한 직원의 마이페이지에서만 보입니다. 공유 문서는 지정한 메뉴 권한을 가진 사람이 봅니다.',
  },
  {
    path: '/docs/s3',
    label: '자료 조회 · S3 저장소',
    what: '사내 저장소(S3)의 파일을 조회하는 곳입니다. 서비스가 만들어 보관하는 계약요약·급여 같은 파일이 여기 있습니다.',
    actions: '이 버튼으로 자료실 입구로 돌아갑니다.',
    spots: [
      { target: 'page-summary', title: '맨 위는 연결 상태', body: '저장소에 붙었는지 여기서 확인합니다. "연결 안 됨" 이면 파일이 안 보이는 것이 권한 문제가 아니라 설정 문제입니다.' },
      { target: 'page-list', title: '이 안에서 파일을 찾습니다', body: '위쪽 경로(빵부스러기)로 상위 폴더로 올라가고, 폴더를 눌러 내려갑니다. 파일은 눌러서 다운로드합니다.' },
    ],
    tip: '다운로드 링크는 잠시 뒤 만료됩니다. 링크를 남에게 전달하지 말고, 필요한 사람이 직접 여기서 받게 하세요.',
  },
  {
    /*
      <b>짧게, 설명만</b>(사용자 지시 2026-08-26). 시켜 보는 문장(「눌러 보세요」)도, 줄줄이 이어지는
      단서도 뺐다 — ? 를 누른 사람이 원하는 것은 «이 화면이 무엇이고 이 버튼이 무엇인가» 한 마디다.

      <p>대신 <b>짚는 자리는 정확히</b> 한다. 종전에는 줄 전체(`page-tabs`)를 밝혀서, 한 줄에 선
      갈래·NAS 고르기·연결 관리 중 어느 것을 말하는지가 안 보였다.

      <p>「읽기 전용입니다」 참고는 없앴다 — 그 게이트는 2026-08-20 에 걷어내서 <b>참이 아닌 말</b>이었다.
    */
    path: '/docs/nas',
    label: '자료 조회 · NAS',
    what: '현장 Synology NAS 와 본사 부서 공유폴더를 서비스 안에서 그대로 탐색하는 곳입니다.',
    actions: '이 버튼으로 자료실 입구로 돌아갑니다.',
    spots: [
      {
        target: 'nas-scope',
        title: '갈래',
        body: '현장 NAS 는 담당 현장의 것, 본사 NAS 는 소속 부서의 공유폴더입니다. 갈래마다 보이는 연결이 다릅니다.',
        placement: 'bottom',
      },
      {
        /*
          「접속 등록」 걸음은 없앴다(2026-08-26). 그 걸음이 짚던 「⚙ 연결 관리」 버튼을 걷었기
          때문이다 — 등록·수정·삭제·연결 테스트가 전부 옆판 안으로 들어왔다. 없는 버튼을 가리키는
          안내는 화면보다 먼저 늙는다.
        */
        target: 'nas-picker',
        title: '볼 NAS 고르기',
        body: '누르면 왼쪽에서 목록이 나옵니다. 고른 NAS 이름이 이 버튼에 남습니다.',
        placement: 'bottom',
      },
      {
        /*
          <b>말로 설명하지 않고 열어서 보여 준다</b>(사용자 지시 2026-08-26). 이 판은 눌러야 나오는데,
          투어 중에는 클릭이 막히고 <b>판이 열려 있으면 ? 버튼이 가려</b> 그 안에서 안내를 시작할
          수도 없다. 그래서 이 걸음이 대신 연다({@link TourStep.openPanelKeys}).
        */
        target: 'nas-drawer',
        openPanels: ['nas-picker'],
        title: 'NAS 목록입니다',
        body:
          'NAS를 이름/부서 단위로 검색할 수 있고, 고르면 판은 바로 닫히고 자료를 조회합니다. ' +
          '빨간 ● 은 접속 점검이 실패한 연결입니다.',
        placement: 'right',
      },
      {
        /*
          <b>권한 이야기는 권한이 있는 사람에게만</b>(사용자 지시 2026-08-26). 앞 걸음에 「고칠 수
          있는 사람에게는…」 을 끼워 넣으면, 못 고치는 사람이 <b>자기가 못 하는 일</b>을 매번 읽는다.
          걸음을 나눠 두면 그 사람의 안내에는 그 말이 아예 나오지 않는다.
        */
        target: 'nas-gear',
        openPanels: ['nas-picker'],
        requires: { menuKey: 'settings_nas', action: 'manage' },
        title: '카드의 톱니바퀴',
        body: '그 연결의 경로·계정·소속을 그 자리에서 고칩니다.',
        placement: 'right',
      },
      {
        /*
          <b>등록 권한이 있는 사람에게만</b> 이 걸음이 선다(사용자 지시 2026-08-26). 조회만 하는
          사람에게는 그 줄 자체가 화면에 없고, 「여기서 추가하세요」 는 할 수 없는 일을 시키는 말이 된다.

          <p>화면에 없으니 저절로 빠질 것 같지만 <b>아니다</b> — 판을 여는 걸음은 «대상이 아직 없는
          것이 정상» 이라 걸러내기의 예외이고, 그 예외가 이 걸음까지 살린다. 그래서 권한으로 거른다.
        */
        target: 'nas-add',
        openPanels: ['nas-picker'],
        requires: { menuKey: 'settings_nas', action: 'manage' },
        title: '찾는 NAS 가 없으면',
        body: '여기서 새로 연결합니다.',
        placement: 'right',
      },
      {
        /*
          설명을 <b>글로 늘리지 않고 창을 띄운다</b>(사용자 지시 2026-08-26). 어차피 그 사람은 이
          창에 들어올 사람이고, 「이런 칸들이 나옵니다」 를 문장으로 적으면 그 카드만 길어진다.
        */
        target: 'nas-editor',
        openPanels: ['nas-picker', 'nas-editor'],
        requires: { menuKey: 'settings_nas', action: 'manage' },
        title: '연결 창',
        body: '경로·계정을 넣고 <b>연결 시험</b>으로 붙는지 본 뒤 저장합니다. 현장(또는 부서)은 <b>반드시</b> 고르세요 — 비우면 전 직원에게 열립니다.',
        placement: 'left',
      },
      {
        target: 'page-list',
        title: '파일',
        body: '폴더를 눌러 파고들고, 파일은 [보기]·[받기] 로 열고 받습니다. [AI 학습] 에 태운 자료만 AI 가 답의 근거로 씁니다.',
      },
    ],
  },
  {
    path: '/docs/submissions',
    label: '자료 요청',
    what: '자료를 요청하고, 받은 자료를 검토하고, 승인되면 자료실로 옮기는 곳입니다. 요청도 검토 요청도 대상을 골라 보내고, 보내면 그 사람들의 TO DO 할일에 뜹니다. 여기 있는 동안의 파일은 자료실과 AI 검색에 나타나지 않습니다 — 검토를 마쳐야 정식 자료가 됩니다.',
    actions: '이 버튼으로 대상(부서·현장·개인)·기한·낼 것을 적어 요청하거나, 직접 낼 새 제출을 만듭니다.',
    tabs: '이 버튼들로 목록을 좁힙니다 — 전체 · 제출 요청(낼 차례: 요청받음·보완 필요·임시 보관) · 검토 대기(볼 차례).',
    spots: [
      { target: null, title: '파일을 담아 [검토 요청]', body: '받은 사람이 그 건에 파일을 담고 볼 사람을 골라 보냅니다. 보내면 보완요청을 받을 때까지 고칠 수 없습니다.' },
      { target: null, title: '볼 사람이 판단합니다', body: '[승인] · [보완요청] · [반려] 중 하나를 합니다. 뒤의 둘은 사유를 적어야 보내집니다.' },
      { target: null, title: '승인 뒤 [자료실로 편입]', body: '이 버튼까지 눌러야 자료실에 들어갑니다 — 그전까지는 자료 조회·AI 검색에 나타나지 않습니다.' },
    ],
    tip: '보완요청과 반려는 사유를 적어야 보내집니다. 받는 사람은 그 내용으로만 무엇을 해야 할지 알 수 있습니다. 차례가 넘어가면 앞 차례의 할일은 자동으로 거둬집니다. 임시파일은 정한 보관 기간이 지나면 자동으로 지워지고, 제출 기록과 이력은 남습니다.',
  },
  {
    /*
      `/docs` 대본이 접두어로 이 경로를 주워 가고 있었다 — `/docs/audit` 은 「자료 조회」 안내를
      받아, 감사 기록 화면에서 «저장소를 고르세요» 를 읽었다. 뜻이 다른 화면은 자기 대본을 갖는다.
    */
    path: '/docs/audit',
    label: '자료실 감사 기록',
    what:
      '자료실에서 <b>누가 무엇을 가져갔고, 누가 공개 범위를 바꿨는지</b>가 시간순으로 남는 곳입니다. ' +
      '지운 파일의 기록도 남습니다 — 기록은 파일이 아니라 <b>행적</b>이기 때문입니다.',
    spots: [
      {
        target: 'page-filters',
        title: '세 칸으로 좁힙니다',
        body:
          '<b>사람</b> 은 「이 사람이 무엇을 가져갔나」, <b>행위</b> 는 열람·다운로드(반출)와 ' +
          '공유 변경·이동·폴더 생성(공개 범위)을 가릅니다. <b>경로</b> 는 그 폴더 <b>아래 전부</b>를 뜻해서 ' +
          '`docs/site/청주현장/` 이라고 적으면 그 현장 자료의 반출이 한 번에 모입니다.',
      },
      {
        target: 'page-list',
        title: '색이 종류를 가릅니다',
        body:
          '테두리 없는 회색은 <b>반출</b>(열람·다운로드), 주황은 <b>공개 범위가 바뀐</b> 줄, ' +
          '붉은 바탕은 <b>거절된 시도</b>입니다 — 권한 없이 열려다 막힌 기록이라 여기가 가장 먼저 볼 자리입니다. ' +
          '「공개 범위」 칸에 적힌 「이전 → 이후」 는 <b>그때의 판단</b>이라, 규칙이 나중에 바뀌어도 이 줄은 안 바뀝니다.',
      },
    ],
    tip:
      '이 메뉴는 자료실 권한이 아니라 <b>보안 권한</b>(환경설정 &gt; 보안)으로 막혀 있습니다. ' +
      '자료실을 쓰는 모두에게 열면 동료의 열람 이력을 서로 들여다보는 창이 되기 때문입니다. ' +
      '기록은 고치거나 지울 수 없습니다.',
  },
  {
    path: '/docs/lab',
    label: '실험실',
    what: '업무에 정식으로 넣기 전에 새 기술을 먼저 시험해 보는 곳입니다. 여기 결과는 아직 검증 중입니다.',
    tabs: '이 버튼들이 실험 주제입니다 — 드론 토공량·굴착기 시뮬레이션·보할 적정성 검토·한글(hwp) 읽기·검측 서류 판독. 앱 배포와 캐드 어시스턴트는 받아서 내 기기에 까는 파일입니다. 쓸 만하다고 판정된 것은 해당 업무 메뉴로 옮겨 갑니다(공사일보가 그렇게 현장 조회로 갔습니다).',
    tip: '실험 결과를 그대로 업무 문서에 쓰지 마세요. 값이 바뀌거나 화면이 통째로 없어질 수 있습니다.',
  },
  {
    // 도면 전용 화면(/bim)을 자료실이 흡수했다(2026-08-18). 안내도 이 자리로 옮긴다.
    path: '/docs/view',
    label: '자료 뷰어',
    what: '자료실에서 고른 파일을 화면 전체로 보는 곳입니다. PDF·이미지·CAD 도면(dwg·dxf)·3D 모델(ifc·glb·gltf·obj)을 봅니다.',
    spots: [
      { target: null, title: '보고 돌아가기', body: '굴려서 확대하고 끌어서 옮깁니다 — 3D 는 끌면 돌아갑니다. 왼쪽 위 [← 자료실] 을 누르면 보던 폴더 그 자리로 돌아갑니다.' },
    ],
    actions: '[확장자 안내] 를 누르면 이 뷰어가 여는 파일 종류가 전부 나옵니다. [내 컴퓨터 파일] 은 저장소에 올리지 않고 브라우저 안에서만 엽니다.',
    tip: '엑셀·워드·PPT 는 이 뷰어가 아니라 목록의 [문서 편집] 으로 열립니다(새 창 · 여럿이 함께 고칠 수 있습니다). DWG 는 서버에서 변환해 열기 때문에 파일이 크면 시간이 걸립니다.',
  },
  {
    path: '/docs/office',
    label: '문서 편집기',
    what: '자료실의 엑셀·워드·PPT 를 브라우저에서 열고 <b>여러 사람이 동시에</b> 고치는 곳입니다. 새 창으로 열립니다.',
    spots: [
      { target: null, title: '같이 고치고 저장합니다', body: '평소 엑셀처럼 고치면 됩니다 — 같은 파일을 연 사람의 커서가 함께 보입니다. [저장] 을 누르는 순간 자료실의 원본이 갱신됩니다.' },
    ],
    tip: '저장은 [저장] 을 누를 때와 마지막 사람이 창을 닫을 때 자료실에 반영됩니다. 옛 형식(xls·doc·ppt)은 그 형식 그대로 저장할 수 없어 보기 전용으로 열립니다 — 고치려면 xlsx 로 다시 저장해 올리세요.',
  },
  {
    // 대장 입력 시트(V319). 대장 화면의 [엑셀로 열기] 가 막혔을 때 서는 대타이기도 하다.
    path: '/sheet/:specKey',
    label: '입력 시트',
    what: '대장에 자료를 <b>우리 양식으로</b> 넣는 곳입니다. 칸이 미리 정해져 있어 머리글을 맞출 필요가 없습니다.',
    spots: [
      { target: null, title: '치거나 붙여 넣습니다', body: '칸에 바로 치고 Enter 로 아래 칸으로 갑니다. 엑셀에서 긁어 온 블록은 누른 칸에서부터 떨어지고, 줄이 모자라면 저절로 늘어납니다.' },
    ],
    actions: '[빈 양식 받기] 는 이 칸 그대로의 엑셀을 내려줍니다. 채워 와서 위 상자에 올리면 열이 어긋나지 않습니다.',
    tip: '줄 끝의 「신규 / 갱신」 표시가 반영 전에 무엇이 일어날지 말해 줍니다. 치던 것은 창을 닫아도 남습니다.',
  },
  {
    // 대장 엑셀로 가는 중간 역(V319). 링크로만 들어오고 곧바로 편집기로 갈아 끼운다.
    path: '/ledger/:ledgerKey',
    label: '대장 엑셀',
    what: '그 대장의 <b>엑셀 파일</b>을 자료실에서 찾아 문서 편집기로 엽니다. 파일이 아직 없으면 지금 대장 내용으로 만들어 둡니다.',
    spots: [
      { target: null, title: '고치고 저장하면 대장에 들어갑니다', body: '평소 엑셀처럼 고친 뒤 [저장] 을 누르면, 서버가 그 파일을 읽어 대장 표에 반영합니다. 파일은 자료실의 「대장」 폴더에 그대로 남습니다.' },
    ],
    tip: '엑셀에서 줄을 지워도 대장에서는 지워지지 않습니다 — 없애는 일은 화면의 [삭제] 로 합니다.',
  },

  // ── 요청게시판 ──────────────────────────────────────────────────────────
  {
    path: '/requests',
    label: '개선요청',
    what: 'BoMI Lounge 자체에 대한 개선 요청과 그 반영 내용을 공유하는 곳입니다.',
    actions: '이 버튼으로 요청을 등록합니다.',
    spots: [
      { target: 'page-list', title: '요청 목록', body: '목록에는 요청번호·담당자·등록일자만 보이고, 줄을 누르면 상세가 펼쳐집니다. 반영이 끝난 요청은 완료 표시로 구분됩니다.' },
    ],
    tip: '어느 화면에서든 위쪽 빨간 「개선사항 요청」 버튼을 누르면 이 목록으로 옵니다.',
  },

  // ── 전자결재 ────────────────────────────────────────────────────────────
  /*
    화면이 하나다 (2026-09-22). 사이드바의 「전자결재」(`/approval`)와 헤더의 「전자결재」
    (`/approval/my`)가 <b>같은 화면</b>이 됐다 — 결재함과 기안 서식이 한 탭 줄에 함께 선다.
    옛 주소도 그대로 열리므로 두 경로에 <b>같은 대본</b>을 단다(`resolveGuide` 가 접두어로
    물러서 주긴 하지만, `/approval/my` 를 적어 두지 않으면 `/approval` 것이 잡혀도 말이 같아야 한다).
  */
  {
    path: '/approval',
    label: '전자결재',
    what: '기안·결재·합의를 한곳에서 처리하는 곳입니다. 내가 결재할 문서를 처리하고, 내가 올린 문서가 지금 누구에게 가 있는지 확인하고, 새 기안을 시작합니다.',
    tabs: '<b>왼쪽이 결재함</b>(결재 대기·진행·완료·상신함·임시저장·수신참조·전체·협조전), <b>오른쪽이 기안 서식 갈래</b>(인사·총무·구매·회계·자산·일반)입니다. 옆의 숫자는 그 함에 쌓인 건수, 갈래에서는 서식 수입니다. 고른 탭은 주소에 남아 링크로 그대로 건네줄 수 있습니다.',
    actions: '이 버튼들로 서식을 찾고, 알림을 보고, 위임을 걸고, 규정을 확인하고, [기안하기] 로 서식 없이 새 문서를 올립니다.',
    spots: [
      { target: 'page-list', title: '문서 목록', body: '줄을 누르면 문서 전체와 결재선, 지금까지의 처리 이력이 열립니다. 왼쪽 배지가 문서 상태이고, 긴급 문서는 빨간 표시가 함께 붙습니다. 내 차례면 그 창 안에서 승인·반려합니다.' },
      { target: null, title: '서식을 골라 기안합니다', body: '갈래 탭을 누르면 그 갈래의 서식이 설명과 함께 깔립니다. [이 서식으로 기안] 을 누르면 그 서식으로 기안 화면이 열립니다. 「금액」 딱지가 붙은 서식은 적은 금액에 따라 결재선이 갈립니다.' },
    ],
    tip: '서식이 어느 갈래인지 모르겠으면 탭을 돌지 말고 검색창에 단어를 넣으세요 — 띄어쓰기는 걷어 내고 보므로 「차량사용」 으로 「회사차량 사용」 이 나옵니다. 자리를 비울 때는 「대결/위임」 에서 결재권을 넘겨 두세요.',
  },
  {
    path: '/approval/my',
    label: '전자결재',
    what: '기안·결재·합의를 한곳에서 처리하는 곳입니다. 헤더의 「전자결재」 버튼이 여는 화면이고, 사이드바에는 줄이 없습니다.',
    tabs: '<b>왼쪽이 결재함</b>(결재 대기·진행·완료·상신함·임시저장·수신참조·전체·협조전), <b>오른쪽이 기안 서식 갈래</b>(인사·총무·구매·회계·자산·일반)입니다.',
    actions: '이 버튼들로 서식을 찾고, 알림을 보고, 위임을 걸고, 규정을 확인하고, [기안하기] 로 서식 없이 새 문서를 올립니다.',
    spots: [
      { target: 'page-list', title: '문서 목록', body: '줄을 누르면 문서와 결재선이 창으로 열리고, 내 차례면 그 안에서 승인·반려합니다. 빨간 「N」 은 내 차례인데 아직 안 연 줄에만 붙습니다.' },
      { target: null, title: '서식을 골라 기안합니다', body: '갈래 탭을 누르면 그 갈래의 서식이 설명과 함께 깔립니다. [이 서식으로 기안] 을 누르면 그 서식으로 기안 화면이 열립니다.' },
    ],
    tip: '서식이 어느 갈래인지 모르겠으면 검색창에 단어를 넣으세요. 지우면 보던 탭이 그대로 돌아옵니다.',
  },
  {
    path: '/approval/draft',
    label: '기안하기',
    what: '새 결재 문서를 올리는 곳입니다.',
    actions: '맨 왼쪽 [기안서·자료입력] 으로 <b>적는 방식</b>을 바꿉니다 — 「기안서」 는 나갈 문서에 바로 적고, 「자료입력」 은 항목 카드에 값을 넣고 미리보기로 기안서를 봅니다. 고른 방식은 다음에 열 때도 그대로입니다. 제목이 비어 있으면 눌러도 상신되지 않고 이유가 적힙니다.',
    spots: [
      { target: 'draft-form', title: '서식을 고릅니다', body: '고르는 순간 아래 <b>기안서</b>가 그 서식의 양식으로 바뀝니다. 적는 자리가 곧 인쇄되는 자리라, 제목·항목·비고를 기안서 위에서 바로 채웁니다.' },
      { target: 'draft-line', title: '결재선은 규정이 세웁니다', body: '금액과 결재규정(전결)을 보고 자동으로 섭니다 — 누가 서 있는지는 기안서의 <b>결재란</b>에 그대로 보입니다. 고칠 때만 이 단추를 열고, [규정대로 자동] 으로 다시 세울 수도 있습니다.' },
      { target: null, title: '붙임과 관련 문서는 종이 밑', body: '파일을 끌어다 놓으면 붙고, 그 이름이 종이의 「붙임」 줄에 섭니다. 관련 문서는 이 기안이 딛고 선 앞의 기안입니다.' },
    ],
    tip: '임시저장해 두면 나중에 이어 쓸 수 있습니다. 상신 전에는 결재자에게 아무 알림도 가지 않습니다.',
  },
  {
    path: '/approval/rules',
    label: '결재규정 (전결)',
    what: '금액 구간별로 최종 전결 직급을 정하는 곳입니다. 기안할 때 자동 결재선이 이 규정을 보고 전결 지점을 잡습니다.',
    actions: '이 버튼으로 결재함으로 돌아갑니다.',
    spots: [
      {
        target: 'page-form',
        title: '여기서 규정을 더하고 고칩니다',
        body:
          '적용 서식을 고르고(비우면 모든 서식에 적용되는 공통 규정입니다), 금액 구간을 넣고, ' +
          '그 구간의 최종 전결 직급을 고릅니다.',
      },
    ],
    tip: '여기를 바꾸면 앞으로 올라오는 문서의 결재선이 달라집니다. 이미 상신된 문서는 그대로 갑니다.',
  },
  {
    path: '/approval/delegation',
    label: '대결/위임',
    what: '부재 기간 동안 내 결재권을 다른 사람에게 넘기는 곳입니다. 기간 안의 대기 건이 수임자 결재함에도 떠서 대결로 처리됩니다.',
    actions: '이 버튼으로 결재함으로 돌아갑니다.',
    spots: [
      {
        target: 'page-form',
        title: '여기서 위임을 겁니다',
        body:
          '수임자(대결자)를 고르고, 적용 서식을 고르고(비우면 모든 문서가 넘어갑니다), 위임 기간을 정합니다.',
      },
    ],
    tip: '기간이 지나면 저절로 원래대로 돌아옵니다. 휴가·출장 전에 미리 걸어 두세요.',
  },

  // ── 자산관리 ────────────────────────────────────────────────────────────
  {
    path: '/assets/supplies',
    label: '소모품 불출',
    what: '자산 공지를 보고, 소모품을 신청하고 불출하는 곳입니다.',
    actions: '이 버튼으로 새 불출 신청을 올립니다 — 품목과 수량을 넣으면 금액에 따라 결재선을 탑니다.',
    spots: [
      { target: 'page-notices', title: '맨 위는 자산 공지입니다', body: '자산·비품·소모품 관련 공지가 최신순으로 놓입니다. 제목을 누르면 본문이 열립니다. 담당자에게만 「공지 등록」 버튼이 보이고, 나머지는 읽기만 됩니다.' },
      { target: 'page-filters', title: '이 버튼들이 처리 상태입니다', body: '대기·승인·완료처럼 상태별로 목록을 나눠 봅니다.' },
      { target: 'page-list', title: '신청 목록', body: '줄을 누르면 품목·수량·결재 진행 상황이 열립니다. 승인 뒤 불출 처리하면 재고가 자동으로 차감됩니다.' },
    ],
    tip: '결재 진행 상황은 「전자결재」 에서도 함께 보입니다.',
  },

  // ── 협력사 ──────────────────────────────────────────────────────────────
  {
    path: '/vendors',
    label: '협력사 마스터',
    what: '회사가 거래하는 협력사와 감리단이 모이는 곳입니다. 계약·기성·출역이 각자 들고 있던 업체 이름을 <b>한 줄로 모으는</b> 자리입니다.',
    tabs: '목록은 등록된 업체를 보고 고치는 곳, 미등록 업체는 <b>계약·기성에 글자로만 적혀 있는 업체</b>를 정식 등록하는 곳, 등록 신청은 <b>협력사가 파트너 페이지에서 스스로 넣은 신청</b>을 심사하는 곳, 문의는 <b>파트너 페이지 「문의하기」로 온 글</b>을 관리하는 곳입니다. 승인하면 업체 행과 첫 담당자 초대가 함께 서고, 승인·반려 결과는 담당자 이메일로 나갑니다. 탭 옆 숫자는 <b>아직 처리하지 않은 것</b>입니다.',
    spots: [
      { target: 'page-filters', title: '구분이 먼저입니다', body: '기본은 협력사입니다 — 감리를 섞어 세면 「우리 회사 협력사가 몇 곳인가」 의 답이 달라집니다. 상호와 사업자등록번호로 찾고, 「업체 등록」 은 권한이 있을 때만 보입니다.' },
      { target: 'page-list', title: '계약 수가 함께 옵니다', body: '계약·기성이 걸린 업체는 지워지지 않습니다. 그만 쓰는 업체는 지우지 말고 <b>사용 중을 끄세요</b> — 지난 계약서에 적힌 상호는 그때의 상호로 남아야 합니다.' },
    ],
    tip: '미등록 업체 탭이 마스터를 채우는 유일한 문입니다. <b>많이 쓰인 업체부터</b> 확정하면 그만큼 많은 줄이 한 번에 이어 붙습니다. 「(주)대한건설」과 「대한건설산업」이 한 회사인지는 사람만 알 수 있어, 확정 창에서 표기를 골라 뺄 수 있습니다.',
  },

  // ── 환경설정 ────────────────────────────────────────────────────────────
  {
    /*
      <b>오른쪽을 열어 놓고 설명한다</b>(사용자 지시 2026-08-26). 종전에는 왼쪽 추가 줄 하나만
      짚고 「오른쪽에서 정합니다」 로 끝냈는데, 아무 부서도 안 고른 화면의 오른쪽은 «왼쪽에서 부서
      줄을 누르세요» 한 줄뿐이다 — 있지도 않은 것을 글로만 설명한 셈이다.

      <p>그래서 이 대본의 걸음들은 <b>부서 하나를 대신 골라 두고</b>(`openPanels: ['dept-detail']`)
      그 안의 자리를 차례로 짚는다.
    */
    path: '/settings/departments',
    label: '부서 관리',
    what: '부서를 만들고, 그 부서 사람들에게 어떤 메뉴를 열어 줄지 정하는 곳입니다. 왼쪽에서 부서를 고르면 오른쪽이 그 부서가 됩니다.',
    spots: [
      { target: 'page-form', title: '부서 만들기', body: '이름을 넣고 [추가] 를 누르면 아래 목록 맨 끝에 생깁니다.' },
      {
        target: 'dept-list',
        title: '부서 목록',
        body:
          '줄을 누르면 오른쪽에 그 부서가 열립니다. 순서는 손잡이를 끌거나 ↑↓ 로 바꾸고, ' +
          '<b>여기 순서대로</b> 주간업무·조직도에 나옵니다. ' +
          '이름에 <b>줄이 그어진 부서</b>는 지금 안 쓰는 부서, <b>「업무 제외」</b>가 붙은 부서는 업무 일정에만 안 나오는 부서, ' +
          '오른쪽 숫자는 그 부서 사람 수입니다.',
      },
      {
        target: 'dept-detail',
        openPanels: ['dept-detail'],
        title: '부서 이름과 사용 여부',
        body:
          '맨 위에서 이름을 고치고, 스위치 둘로 <b>지금 쓰는 부서인지</b>와 <b>업무 일정에 낼지</b>를 정합니다. ' +
          '이름을 바꾸면 그 이름을 쓰고 있던 곳(소속·권한 규칙)도 함께 바뀌고, 무엇이 바뀌었는지 바로 알려 줍니다.',
        placement: 'left',
      },
      {
        target: 'dept-members',
        openPanels: ['dept-detail'],
        title: '이 부서에 누가 있나',
        body:
          '이름으로 찾아 넣고 뺍니다. <b>저장 버튼이 없습니다</b> — 누르는 즉시 반영됩니다. ' +
          '여기 넣은 사람에게는 아래 <b>부서 권한</b>이 그대로 붙습니다.',
        placement: 'left',
      },
      {
        target: 'dept-permissions',
        openPanels: ['dept-detail'],
        title: '이 부서 사람들에게 열리는 메뉴',
        body:
          '체크한 메뉴가 이 부서 <b>모든 사람</b>에게 열립니다. [추천 불러오기] 는 표를 <b>채워 주기만</b> 하니, ' +
          '보고 고친 뒤 <b>[권한 저장] 을 눌러야</b> 반영됩니다. ' +
          '회색으로 잠긴 「보기」 칸은 부서에 들어오면 저절로 열리는 메뉴라 끌 수 없습니다.',
        placement: 'left',
      },
      /*
        저장이 <b>둘</b>이라는 것은 참고 한 줄로 적어 두면 아무도 안 읽는다(사용자 지시 2026-08-26).
        버튼 두 개를 <b>각각 밝혀</b> 말한다 — 오른쪽 판은 그대로 열어 둔 채로.
      */
      {
        target: 'dept-save-info',
        openPanels: ['dept-detail'],
        title: '저장은 여기, 이름·사용 여부만',
        body: '이 버튼은 <b>위쪽 칸만</b> 저장합니다. 아래 권한 표는 함께 저장되지 않습니다.',
        placement: 'left',
      },
      {
        target: 'dept-save-permissions',
        openPanels: ['dept-detail'],
        title: '권한 표는 따로 저장합니다',
        body:
          '권한은 <b>이 버튼으로 따로</b> 저장합니다. 두 저장이 갈려 있는 것은 <b>필요한 권한이 서로 다르기</b> 때문이라, ' +
          '한쪽만 눌리는 사람도 있습니다. 부서 전체가 아니라 <b>한 사람에게만</b> 주는 권한은 「역할·권한 관리」 에서 정합니다.',
        placement: 'left',
      },
    ],
  },
  {
    path: '/settings/sites',
    label: '현장 관리',
    what: '현장 정보와 계약·규모, 표시 순서를 관리하는 곳입니다. 여기 등록된 현장이 현장 현황·주간업무에 나타납니다.',
    actions: '이 버튼으로 현장을 추가합니다.',
    spots: [
      { target: 'page-filters', title: '이 버튼들로 분류를 고릅니다', body: '건축·토목 같은 현장 분류입니다. 고른 분류의 현장만 아래에 남습니다.' },
    ],
    tip: '준공 처리는 현장을 지우는 것이 아닙니다 — 목록에서 준공현장 쪽으로 옮겨 갈 뿐, 지난 기록은 남습니다.',
  },
  {
    /*
      로그인 화면 설정 (V274 · 2026-09-15). 화면에 <b>data-tour 자리가 없어</b> 짚을 대상도 없다 —
      「이 버튼들로 …」 를 적으면 대본이 없는 곳을 가리키게 되므로 spots 는 전부 대상 없는 카드다.
    */
    path: '/settings/login-design',
    label: '로그인 화면',
    what: '로그인 화면의 바탕 효과와 거기 쓰이는 사진을 정하는 곳입니다. 아무것도 켜지 않으면 단색 바탕으로 나옵니다.',
    spots: [
      {
        target: null,
        title: '오른쪽은 흉내가 아닙니다',
        body: '미리보기는 로그인 화면이 실제로 쓰는 그 조각을 줄여 놓은 것입니다. 스위치를 만지면 그 자리에서 바로 움직입니다 — 저장하고 로그아웃해서 확인할 필요가 없습니다.',
      },
      {
        target: null,
        title: '바탕 효과 둘은 자리를 다툽니다',
        body: '무작위 격자와 수평 시차는 같은 바탕을 씁니다. 둘 다 켜면 화면이 열릴 때마다 번갈아 나옵니다. 커서 효과는 그 위에 겹치고, 모양은 물결과 꼬리 중 하나만 고릅니다.',
      },
      {
        target: null,
        title: '사진은 올리는 것과 등록하는 것이 다릅니다',
        body: '「사진 올리기」 는 저장소에 넣고 목록에 줄을 하나 얹을 뿐입니다. 저장을 눌러야 로그인 화면이 그것을 봅니다. 목록에서 줄을 지우고 저장하면 그 사진도 저장소에서 사라집니다.',
      },
    ],
    tip: '여기서 바꾼 것은 로그인하지 않은 사람이 보는 화면입니다 — 이미 들어와 있는 사람의 화면은 달라지지 않습니다.',
  },
  {
    /*
      홈페이지 관리 (DOC-114 · 2026-10-08). 탭 줄은 data-tour 자리가 없어 짚지 않는다 — 머리 줄
      버튼(page-actions)만 PageHeader 가 달아 준다.
    */
    path: '/settings/homepage',
    label: '홈페이지 관리',
    what: '회사 홈페이지의 디자인·페이지·게시글·시공실적·문의를 고치고 게시하는 곳입니다. 오른쪽 미리보기는 실제 홈페이지 시안에 지금 적은 글을 먹인 것입니다.',
    actions: '이 버튼들로 초안을 저장하고, 게시하고, 이전 게시본을 보거나 되돌립니다. 저장만으로는 바깥에 나가지 않습니다 — [게시] 를 눌러야 홈페이지가 바뀝니다.',
    spots: [
      {
        target: null,
        title: '한국어가 원본입니다',
        body: '한국어를 저장하면 바뀐 칸만 다른 언어로 다시 번역됩니다. 사람이 고친 번역 칸은 덮이지 않고 「원문이 바뀜」 표시만 붙습니다.',
      },
      {
        target: null,
        title: '언어는 한 번 「준비됨」 을 눌러야 나갑니다',
        body: '언어를 켜면 번역이 채워지지만 게시에는 실리지 않습니다. 디자인 탭에서 그 언어의 「준비됨」 을 한 번 누르면 그 뒤로는 게시 때마다 함께 나갑니다.',
      },
    ],
    tip: '시공실적의 「현장에서 가져오기」 는 한 번 복사입니다. 현장 자료를 나중에 고쳐도 홈페이지 글은 저절로 바뀌지 않습니다.',
  },
  {
    path: '/settings/menus',
    label: '메뉴 관리',
    what: '왼쪽 사이드바에 보이는 메뉴의 이름·순서·소속을 관리하는 곳입니다.',
    spots: [
      { target: 'page-form', title: '여기서 폴더 메뉴를 더합니다', body: '하위 화면을 담는 묶음입니다. 화면 자체를 새로 만드는 것은 아닙니다.' },
      { target: 'page-list', title: '아래가 메뉴 트리', body: '줄마다 이름을 고치고, 위/아래 이동과 상위 폴더 변경으로 자리를 옮깁니다. 「구조 고정」 이 붙은 메뉴는 이름만 바꿀 수 있습니다 — 소속이 바뀌면 화면 경로와 어긋납니다.' },
    ],
    tip: '이름만 바꾸는 것이면 안전합니다. 소속을 옮기면 사람들이 쓰던 자리가 바뀌니 공지와 함께 하세요.',
  },
  {
    path: '/settings/users',
    label: '사용자·권한 관리',
    what: '계정과 소속, 메뉴별 권한을 관리하는 곳입니다. "메뉴가 안 보인다" 는 문의는 대부분 여기서 풉니다.',
    actions: '이 버튼으로 사용자를 추가합니다.',
    spots: [
      { target: 'permission-explainer', title: '권한이 처음이라면 여기부터', body: '권한이 어떻게 정해지는지, 역할·규칙·소속이 무슨 말인지 이 카드에 적혀 있습니다. 접어 두면 다음에도 접힌 채로 열립니다.' },
      { target: 'page-filters', title: '사람을 찾습니다', body: '이름·이메일·직급·소속이 한 번에 걸립니다.' },
      { target: null, title: '줄을 눌러 고칩니다', body: '소속과 메뉴 권한을 그 자리에서 고칩니다. 저장하면 그 사람이 다음에 열 때부터 반영됩니다.' },
    ],
    tip: '부서에 넣는 것이 곧 쓸 수 있게 만드는 일입니다 — 소속이 생기면 환경설정을 뺀 모든 메뉴가 조회로 열립니다. 고치는 권한은 「부서 관리」(부서 전원) 나 「역할·권한 관리」(사람 지목) 에서 줍니다. 본인이 지금 무엇을 가졌는지는 마이페이지 › 내 권한에서 봅니다.',
  },
  {
    path: '/settings/roles',
    label: '역할·권한 관리',
    what: '기성·검측·급여처럼 정해진 사람만 해야 하는 일을 「담당 역할」 로 만드는 곳입니다. 조회는 여기서 주지 않습니다 — 부서에 넣으면 열립니다.',
    actions: '이 버튼으로 역할을 추가하고, 등록·수정·삭제 권한을 고릅니다.',
    spots: [
      { target: 'permission-explainer', title: '역할이 처음이라면 여기부터', body: '조회는 부서가 열고 등록·수정·삭제는 역할이 연다는 것, 부서에 넣어도 안 열리는 셋이 무엇인지 적혀 있습니다.' },
      { target: 'page-list', title: '역할 목록', body: '줄마다 담긴 메뉴 수와 지목된 담당자가 적혀 있습니다. 「담당자 없음」 은 만들어만 두고 아무에게도 안 준 역할입니다.' },
      {
        // 「저쪽에서」 라고 말하면서 <b>그 저쪽을 안 밝히면</b> 사람은 사이드바를 눈으로 뒤진다
        // (사용자 지적 2026-08-26). 메뉴 한 줄에 붙은 이름표를 짚고, 접혀 있으면 대신 펼친다.
        target: 'sidebar-settings_users',
        expandMenuKeys: ['settings'],
        title: '담당자는 이곳에서 지목합니다',
        body: '「사용자·권한 관리」 에서 그 역할의 정담당·부담당을 지목하고, 지목된 사람이 다시 로그인하면 열립니다.',
        placement: 'right',
      },
    ],
    tip: '매트릭스의 「보기」 칸이 잠겨 있는 줄은 부서에 넣는 것만으로 이미 열린 메뉴입니다 — 여기서 끌 수 없습니다. 잠기지 않은 보기 칸(환경설정·인사자료·기성·공무)이 곧 따로 줘야 하는 것입니다. 부서 전원에게 줄 것이면 이 화면이 아니라 「부서 관리」 입니다.',
  },
  {
    path: '/settings/permission-diagnosis',
    label: '권한 진단',
    what: '한 사람을 골라 「무엇이 열리고 무엇이 막히는지, 그리고 왜 그런지」 를 한 장으로 봅니다. 여기서는 아무것도 바뀌지 않습니다 — 고치는 자리는 따로입니다.',
    actions: '위에서 계정을 고르면 아래 표가 채워집니다.',
    spots: [
      { target: 'page-filters', title: '먼저 사람을 고릅니다', body: '이름이나 이메일로 찾아 고릅니다. 「막힌 메뉴만」 을 켜 두면 열리는 줄은 감추고 막힌 줄만 남습니다 — 이 화면을 여는 이유가 대개 그쪽입니다.' },
      { target: null, title: '계정 카드가 답의 절반입니다', body: '부서 소속이 한 줄이라도 있으면 자료 범위가 「전 현장」 이 됩니다. 「자료 범위」 가 현장 소속보다 넓으면 인력배치표로 걸린 현장이 더 있다는 뜻입니다. 관리자 계정이면 맨 위에 그렇게 적힙니다 — 관리자로 보면서 「왜 다 보이지」 를 묻는 일이 가장 흔합니다.' },
      { target: null, title: '오른쪽 「이유」 를 읽습니다', body: '무엇이 그 줄을 열었는지, 또는 무엇이 막았는지가 문장으로 적힙니다. 「오픈 관리에서 아직 닫혀 있습니다」 면 오픈 관리로, 「역할 «…» — 조회」 면 역할·권한 관리로 가면 됩니다.' },
    ],
    tip: '「―」 는 막힌 것이 아니라 그 메뉴에 없는 동작입니다(조회만 있는 화면의 수정 칸). 권한을 고친 뒤에는 그 사람이 다시 로그인해야 역할이 새로 붙습니다 — 소속은 즉시, 역할은 다음 로그인부터입니다.',
  },
  {
    path: '/settings/backup',
    label: '데이터 백업',
    what: '운영 DB 를 통째로(.sql) 추출하고, 사내망 서버로 보내 쌓아 두는 곳입니다.',
    actions: '「지금 추출」 은 이 자리에서 파일로 받고, 「지금 백업해서 보내기」 는 사내 서버로 올립니다.',
    spots: [
      { target: 'page-form', title: '여기서 추출합니다', body: '서버가 그 자리에서 덤프를 떠서 내려보냅니다. 데이터 양에 따라 몇 분이 걸릴 수 있으니 버튼이 도는 동안 창을 닫지 마세요.' },
      // 「마지막 성공」 줄을 짚는 이유: 백업은 안 도는 것을 아무도 모르는 기능이라, 사람이 볼 곳을
      // 하나 정해 주지 않으면 실패가 몇 주 동안 쌓인다.
      { target: 'page-form', title: '맨 위의 「마지막 백업」 을 보세요', body: '자동 백업은 실패해도 그날 다시 시도하지 않습니다. 이 줄의 날짜가 며칠씩 늙어 있으면 전송 설정이나 사내 서버를 확인해야 한다는 뜻입니다.' },
    ],
    tip: '추출한 .sql 에는 계정과 인사·급여 정보가 모두 들어 있습니다. 개인 PC나 메신저에 두지 마세요. 사내 서버로 보내는 계정은 셸이 없는 SFTP 전용 계정을 권합니다 — 보관 개수를 넘긴 옛 파일은 우리가 올린 것만 골라 지우고, 폴더의 다른 파일은 건드리지 않습니다.',
  },
  {
    path: '/settings/external-api',
    label: '외부 API 설정',
    what: '날씨·지역명·환율·공휴일·이동시간처럼 바깥 서비스를 부를 때 쓰는 API 키를 넣는 곳입니다. 전산 담당만 들어옵니다.',
    actions: '키마다 「발급받기」 로 발급처를 열고, 받은 키를 붙여넣어 저장한 뒤 「연결 확인」 을 눌러 실제로 되는지 봅니다.',
    // 「값 보기」 는 이 화면에서 유일하게 원문이 나오는 자리라, 도움말에서 그 조건을 분명히 말한다.
    spots: [
      { target: 'page-list', title: '이 화면 값이 환경변수를 이깁니다', body: '서버에 넣어 둔 환경변수는 그대로 두셔도 됩니다 — 여기가 비어 있는 동안만 그 값이 쓰이고, 넣는 순간부터 이쪽이 우선입니다. 되돌리려면 「화면 값 지우기」 를 누르면 됩니다.' },
      { target: 'page-list', title: '만료일을 적어 두세요', body: '유효기간이 있는 키라면 만료일을 적어 두시면 한 달 전부터 전산 담당의 TO DO 에 「갱신해 주세요」 가 뜹니다. 적지 않으면 알림이 없습니다 — 우리가 만료일을 짐작하지 않기 때문입니다. 환경변수(k8s Secret)로 넣은 키에도 만료일은 적을 수 있습니다.' },
      { target: 'page-list', title: '연동 스위치와 자동 꺼짐', body: '「연동」 스위치를 끄면 그 API 를 아예 호출하지 않습니다. 만료일이 지나면 켜 두셨더라도 자동으로 꺼집니다 — 만료된 키는 제공자가 거절하므로 부르나 마나이기 때문입니다. 다시 켜려면 스위치가 아니라 키를 갱신하고 새 만료일을 넣으시면 됩니다.' },
    ],
    tip: '목록에는 뒤 네 자리만 보입니다. 원문을 보려면 「값 보기」 에서 본인 비밀번호를 한 번 더 넣어야 하고, 그 열람은 활동 로그에 남습니다(값은 남지 않습니다). 저장만으로는 맞는지 알 수 없으니 「연결 확인」 을 꼭 눌러 보세요 — 공공데이터포털 키는 발급 직후 승인 대기(1~2시간) 동안 0건으로 나옵니다.',
  },
  {
    path: '/settings/mail',
    label: '메일 설정',
    what: '시스템이 보내는 알림 메일의 수신자와 표시 방식을 정하는 곳입니다.',
    actions: '이 버튼으로 바꾼 내용을 저장합니다.',
    spots: [
      { target: 'page-form', title: '여기서 수신자를 정합니다', body: '관리자 알림을 받을 주소를 한 줄에 하나씩 적습니다. 비워 두면 배포 기본값으로 돌아가거나 아예 보내지 않습니다.' },
    ],
    tip: '맨 위에 "메일 서버가 설정되지 않았습니다" 가 떠 있으면, 저장은 되지만 실제로 발송되지는 않습니다.',
  },
  {
    path: '/settings/push',
    label: '푸시 알림',
    what: '앱으로 나가는 푸시 알림이 지금 실제로 나가는지, 누가 앱을 깔았는지, 그 폰에 정말 뜨는지를 보는 곳입니다.',
    actions: '결재·공지 스위치를 바꾼 뒤 이 버튼으로 저장합니다. 테스트 발송은 저장과 무관하게 바로 나갑니다.',
    spots: [
      { target: null, title: '넣는 칸이 없습니다', body: 'FCM 키는 배포 환경변수로만 들어갑니다. 여기에는 붙어 있는 프로젝트와 서비스 계정 이름만 보입니다 — 맨 위에 「발송되지 않는 상태」 가 떠 있으면 스위치를 켜 두어도 대기함에 쌓이기만 합니다.' },
      { target: null, title: '결재 알림과 공지 알림', body: '결재 알림은 메일 주소가 없는 현장 인원이 자기 차례를 아는 유일한 길입니다. 공지 알림은 공지를 쓴 사람이 켰을 때만 나가고, 여기는 그 위의 회사 차원 잠금입니다.' },
      { target: null, title: '테스트는 사람이 아니라 기기를 고릅니다', body: '한 사람이 폰과 태블릿을 등록하면 두 줄입니다. 「그 사람의 그 폰만 안 온다」 를 잡으려면 기기를 지목해야 합니다. 전원 발송 버튼은 일부러 없습니다.' },
    ],
    tip: '스위치를 바꿨는데 알림이 안 오면 먼저 맨 위 상태 카드를 보세요. 자격증명이 없으면 어떤 스위치도 소용이 없습니다.',
  },
  {
    path: '/settings/exit-survey',
    label: '퇴사자 설문 양식',
    what:
      '사직서 결재가 끝나면 퇴사자의 개인 메일로 나가는 설문 안내와, 그 링크로 열리는 설문지의 ' +
      '내용을 정하는 곳입니다. 사내 계정은 퇴사와 함께 닫히므로 사내 메일이 아니라 사직서에 적은 ' +
      '개인 메일로 나갑니다.',
    actions: '「미리보기」 는 저장하지 않고 그려만 봅니다. 「저장」 을 눌러야 다음 퇴사자부터 적용됩니다.',
    spots: [
      { target: 'page-form', title: '메일 문구의 {{ }} 자리', body: '{{name}}·{{link}}·{{days}} 는 보낼 때 실제 값으로 바뀝니다. 목록에 없는 이름을 적으면 저장할 때 막습니다 — 오타 난 자리는 치환되지 않고 {{ }} 그대로 나가기 때문입니다.' },
      { target: null, title: '답의 모양은 만든 뒤 바뀌지 않습니다', body: '물음 문구·순서·사용 여부는 언제든 고칠 수 있지만, 답의 모양(5점 척도·자유 서술 …)은 문항을 만들 때 한 번만 고릅니다. 5점 척도로 받던 칸을 자유 서술로 바꾸면 지난 평균이 표본만 줄어든 채 계속 그려지기 때문입니다. 뜻이 달라지는 물음은 새 문항으로 만드세요.' },
      { target: null, title: '지우지 말고 「사용」 을 끄세요', body: '쓰지 않게 된 문항은 사용을 끄면 설문지에서 사라지고 집계에는 그대로 남습니다. 뜻이 달라지는 수정은 문구를 고치지 말고 새 문항으로 만드세요 — 문구만 고치면 지난 답과 새 답이 한 칸에 섞입니다.' },
      { target: null, title: '사유 선택지는 사직서 서식과 맞추세요', body: '전자결재 사직서의 선택지와 같은 글자여야 퇴사사유 분석의 「공식 사유 vs 본심」 표를 나란히 놓고 볼 수 있습니다. 한쪽만 바꾸면 두 축이 서로 다른 낱말 사전이 됩니다.' },
      { target: null, title: '「기타」 는 직접 적을 수 있습니다', body: '「기타」 로 시작하는 선택지를 고르면 퇴사자에게 입력칸이 열리고, 적은 글은 보미소식 › 입/퇴사자 소식 › 퇴사사유 분석의 「기타에 적은 사유」 에 이름 없이 모입니다. 「기타 12명」 으로만 남던 칸이 그제야 읽을 수 있는 자료가 됩니다. 응답이 5명 미만인 기간은 통째로 감춥니다 — 두 명이 답한 달의 자유 서술은 이름이 없어도 누가 썼는지 알기 때문입니다.' },
      { target: null, title: '미리보기와 시험 발송은 다릅니다', body: '미리보기는 고치는 중인 문구를 그려 보고, 시험 발송은 저장된 문구로 실제 한 통을 보냅니다. 편지함에서 줄바꿈이 어떻게 보이는지·스팸함으로 가는지는 실제로 받아 봐야 압니다. 시험 메일은 제목 앞에 [시험 발송] 이 붙고 링크는 예시라 열리지 않습니다 — 시험 응답이 집계에 섞이지 않게 하기 위해서입니다.' },
    ],
    tip: '설문은 사직서 결재가 완료된 순간 대기함에 들어가고 30초 안에 나갑니다. 결과는 보미소식 「입/퇴사자 소식」 의 퇴사사유 분석 탭에 집계로만 쌓입니다 — 개별 응답을 여는 화면은 없고, 응답이 5명 미만인 칸은 평균도 감춥니다. 안내문에 「익명」 이라고 쓰지 마세요: 응답은 인사 자료와 이어져 저장되고, 감추면 나중에 알게 됐을 때 남는 것은 자료가 아니라 불신입니다.',
  },
  {
    path: '/settings/error-log',
    label: '오류 기록',
    what: '서버가 남긴 오류·경고를 같은 것끼리 묶어, 누가 언제 어느 주소에서 겪었는지 봅니다. 최고 관리자만 들어옵니다.',
    actions: '줄을 누르면 아래에 낱건과 스택이 펼쳐집니다.',
    spots: [
      { target: 'page-filters', title: '기간·수준·상태로 거릅니다', body: '「4xx」 는 요청이 잘못 들어온 것(권한·입력), 「5xx」 는 서버가 실패한 것, 「요청 밖」 은 정기 작업·발송처럼 사람이 누르지 않은 일에서 난 것입니다.' },
      { target: 'page-list', title: '횟수는 묶음의 누적입니다', body: '같은 자리에서 같은 이유로 난 것은 한 줄로 셉니다. 낱건은 30일이 지나면 지워지고, 30일 동안 다시 나지 않은 묶음도 함께 사라집니다.' },
    ],
    tip: '요약의 이메일과 비밀번호 값은 가려서 저장합니다.',
  },
  {
    path: '/settings/release-notes',
    label: '변경이력',
    what: '버전 줄을 누르면 열리는 릴리즈 노트의 줄마다, 어느 판(Job 번호)에 실려 나갔는지와 노트 창에 보일지를 고칩니다. 최고 관리자와 역할로 변경이력 권한을 받은 사람이 들어옵니다.',
    actions: '판을 고르거나 숨김을 누르면 바로 저장됩니다.',
    spots: [
      { target: 'page-filters', title: '판으로 거릅니다', body: '「판 미지정」 은 아직 어느 판에도 실리지 않았거나 판을 비운 줄입니다.' },
      { target: 'page-list', title: '판은 저절로 붙습니다', body: '새 판이 처음 뜰 때 그 판에 처음 들어간 줄이 자동으로 그 판에 저장됩니다. 잘못 붙은 줄만 여기서 옮깁니다.' },
    ],
    tip: '문장을 고치면 다음 판에 새 줄로 붙습니다 — 그때 옛 판으로 옮겨 잡으세요.',
  },
  {
    path: '/settings/shortcuts',
    label: '바로가기 관리',
    what:
      '화면 위쪽 「바로가기」 목록에 들어갈 링크를 정하는 곳입니다. 회사 홈페이지·블로그·그룹웨어처럼 ' +
      '이 시스템 바깥으로 나가는 주소를 모아 둡니다.',
    actions: '이 버튼으로 목록을 통째로 저장합니다 — 누르면 전 직원에게 그대로 보입니다. 저장 전에는 새로고침이 곧 취소입니다.',
    spots: [
      { target: null, title: '이름과 주소를 적습니다', body: '줄마다 이름과 주소를 넣고, 화살표로 순서를 맞춥니다. 여기 순서가 그대로 헤더 드롭다운의 순서가 됩니다.' },
    ],
    tip: '여기 등록한 순서가 그대로 헤더 드롭다운의 순서가 됩니다. 등록 권한이 없으면 읽기만 됩니다.',
  },
  {
    path: '/settings/safety-admins',
    label: '안전관리 관리자',
    what: '안전관리 페이지(safe)에 관리자로 들어갈 직원을 정하는 곳입니다. 관리자는 그 안에서 직원·협력업체 계정을 만들고 역할을 줍니다.',
    actions: '이 버튼으로 목록을 통째로 저장합니다. 안전관리 페이지에는 10분 안에 반영됩니다.',
    spots: [
      { target: null, title: '이름으로 찾아 넣습니다', body: '직원을 이름으로 찾아 넣고, 뺄 사람은 줄 끝의 [빼기] 를 누릅니다. 메일 주소가 없는 사람은 안전관리 페이지에 들어갈 수 없습니다.' },
    ],
    tip: '목록에서 빼면 관리자 권한만 사라지고, 그 사람의 안전관리 계정은 남습니다.',
  },

  // ── 메뉴 밖 화면 ────────────────────────────────────────────────────────
  {
    path: '/admin/dashboard',
    label: '관리자 대시보드',
    what: '서버 상태·트래픽·오류·페이지 사용 통계를 실시간으로 보는 운영 화면입니다. 최고 관리자만 들어옵니다.',
    actions: '따로 새로고침할 것 없이 30초마다 저절로 갱신됩니다. 지금 갱신 중인지 여기서 보입니다.',
    spots: [
      { target: 'page-summary', title: '맨 위는 서버 상태', body: '가동 시간과 구성요소별 상태(초록·빨강)입니다. 빨간 것이 있으면 아래 통계보다 이것이 먼저입니다.' },
    ],
    tip: '오류 수가 갑자기 튀면 어느 화면에서 났는지부터 봅니다 — 사용 통계와 함께 보면 범위가 좁혀집니다.',
  },
  {
    path: '/mypage',
    label: '마이페이지',
    what: '나에게만 보이는 자료를 모아 둔 곳입니다.',
    tabs: '이 버튼들이 자료의 종류입니다 — 개인정보 설정·교육·연차관리·복지·인사 다섯입니다. 개인 자료는 「개인정보 설정」 아래쪽 버튼으로 열고, 경력·법정선임·자격증·근속기간·인사고과·명함신청·증명서 발급은 「인사」 안의 하위 탭입니다.',
    tip: '인사고과는 비밀번호를 한 번 더 넣어야 열립니다. 탭을 벗어나면 다시 잠기므로 공용 PC 에서도 안심하고 쓰세요. 출퇴근이 잘못 찍혔으면 「연차관리 › 출퇴근」 에서 정정을 요청하세요 — 인사총무팀이 확인하면 그 자리에서 기록이 맞춰집니다.',
  },
  {
    path: '/aiagent',
    label: 'AI Agent',
    what: '사내 자료를 찾아 답해 주는 대화 화면입니다.',
    spots: [
      { target: 'page-form', title: '여기에 묻습니다', body: '"이번 달 회의 일정 알려줘", "○○현장 지난주 업무 요약해줘" 처럼 쓰면 됩니다. Enter 로 보내고, Shift+Enter 로 줄을 바꿉니다.' },
      { target: null, title: '답에는 근거가 붙습니다', body: '답과 함께 근거 자료가 카드로 붙습니다. 지난 대화는 왼쪽 목록에 저장되어 다시 열립니다.' },
    ],
    tip: '내 권한 안의 자료만 답에 들어갑니다. 답에 붙은 근거 카드를 눌러 원문을 확인하세요.',
  },
];

/** 대본이 없는 화면에서 ? 를 눌렀을 때. 길을 잃지 않도록 공통 도구만 짚어 준다. */
const FALLBACK_STEPS: TourStep[] = [
  {
    key: 'fallback-what',
    target: null,
    title: '이 화면의 안내는 아직 준비 중입니다',
    body:
      '대신 어디서든 쓸 수 있는 것들을 짚어 드릴게요. 이 화면에 대한 안내가 필요하면 위쪽 빨간 「개선사항 요청」 으로 알려 주세요 — ' +
      '누르면 요청 목록으로 가고, 거기서 등록하면 담당자가 답합니다.',
  },
  {
    key: 'fallback-menu',
    target: 'sidebar-search',
    title: '메뉴가 많으면 검색하세요',
    body: '메뉴 이름 일부만 쳐도 목적지가 바로 나옵니다.',
    placement: 'right',
  },
  {
    key: 'fallback-agent',
    target: 'fab-ai-agent',
    title: '찾기 어려우면 AI Agent 에게 물어보세요',
    body: '오른쪽 아래 동그란 버튼입니다. 말로 물으면 사내 자료를 찾아 답합니다. 권한이 없는 자료는 답에 들어가지 않습니다.',
    placement: 'left',
  },
];

const HOME_GUIDE: PageGuide = { key: HOME_GUIDE_KEY, label: '홈', steps: TOUR_STEPS };

/**
 * 경로에 맞는 안내 대본을 고른다.
 *
 * 정확히 일치하는 것을 먼저 보고, 없으면 <b>가장 긴 접두사</b>로 물러선다 — `/docs/s3/폴더/하위`
 * 처럼 뒤에 무언가 더 붙는 화면이 생겨도 `/docs/s3` 안내가 그대로 나온다. `/docs` 가 `/docs/upload`
 * 를 가로채지 않도록 길이 순으로 고른다.
 */
export function resolveGuide(pathname: string): PageGuide {
  const path = normalize(pathname);
  if (path === HOME_PATH) return HOME_GUIDE;

  const exact = PAGE_GUIDES.find((guide) => guide.path === path);
  if (exact) return toGuide(exact);

  const prefix = PAGE_GUIDES.filter((guide) => path.startsWith(`${guide.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];

  return prefix ? toGuide(prefix) : { key: 'fallback', label: '안내', steps: FALLBACK_STEPS };
}

/** 대본이 준비된 경로 목록. 문서·테스트가 빠진 화면을 찾는 데 쓴다. */
export function guidedPaths(): string[] {
  return [HOME_PATH, ...PAGE_GUIDES.map((guide) => guide.path)];
}

/** 문서 생성·검수용. 화면 이름과 각 칸의 문구를 그대로 돌려준다. */
export function guideSpecs(): readonly PageGuideSpec[] {
  return PAGE_GUIDES;
}

function normalize(pathname: string): string {
  // 끝의 슬래시만 떼어 낸다. `/` 자체는 홈으로 리다이렉트되므로 여기까지 오지 않는다.
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * 대본 한 줄을 걸음들로 편다.
 *
 * 순서는 화면을 읽는 순서다 — 무엇인지(가운데) → 상단 탭 → 오른쪽 버튼 → 본문 자리들 → 참고.
 * 이 순서를 화면마다 다르게 두면, 여러 화면에서 ? 를 눌러 본 사람이 매번 다시 적응해야 한다.
 */
function toGuide(spec: PageGuideSpec): PageGuide {
  const steps: TourStep[] = [
    { key: 'what', target: null, title: `${spec.label} 화면입니다`, body: spec.what },
  ];

  if (spec.tabs) {
    steps.push({
      key: 'tabs',
      target: 'page-tabs',
      title: spec.tabsTitle ?? '상단 탭',
      body: spec.tabs,
      // 왼쪽 나무는 <b>오른쪽</b>에 카드를 세운다 — 아래에 세우면 카드가 목차를 절반쯤 덮는다.
      placement: spec.tabsTitle ? 'right' : 'bottom',
    });
  }
  if (spec.actions) {
    /*
      제목은 <b>어느 화면에서나 맞는 말</b>이어야 한다. 한때 여기 「새 서식 만들기」가 박혀 있었다 —
      문서서식 화면(`DocTemplatesPage`)의 버튼 이름이 공용 조립부로 새어 들어온 것이라, 대시보드에서
      ? 를 눌러도 「새 서식 만들기」라는 제목 아래 대시보드 버튼 설명이 붙었다(2026-08-28 발견).
      화면마다 다른 말은 {@code spec.actions} 가 한다.
    */
    steps.push({ key: 'actions', target: 'page-actions', title: '오른쪽 위 버튼', body: spec.actions, placement: 'bottom' });
  }
  // 이름에 <b>차례를 함께</b> 넣는다. 대상 없는 걸음(`target: null`)이 여럿일 수 있어 대상만으로는
  // 이름이 겹치고, 겹치면 목록을 그릴 때 React 가 같은 것으로 보고 카드 내용이 섞인다.
  (spec.spots ?? []).forEach((spot, index) => {
    steps.push({
      key: `spot-${index}-${spot.target ?? 'card'}`,
      target: spot.target,
      subTargets: spot.subTargets,
      openPanelKeys: spot.openPanels,
      openTabKey: spot.openTab,
      expandMenuKeys: spot.expandMenuKeys,
      requires: spot.requires,
      interactive: spot.interactive,
      advanceWhen: spot.advanceWhen,
      act: spot.act,
      title: spot.title,
      body: spot.body,
      placement: spot.placement,
    });
  });
  if (spec.tip) {
    steps.push({ key: 'tip', target: null, title: '참고', body: spec.tip });
  }

  return { key: spec.path, label: spec.label, steps };
}
